import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { useApp } from "../context/AppContext";
import { Role as UserRole } from "../constants/roles";
import HorizontalWorkflowStepper from "../components/HorizontalWorkflowStepper";
import { Modal } from "../components/Modal";
import { MsaContractBody } from "../components/MsaContractBody";
import { ContractGoogleDocsShell } from "../components/ContractGoogleDocsShell";
import { normalizeSections as normalizeContractAppendSections } from "../components/ContractAppendSections";
import { ContractAnnotationsSidebar } from "../components/ContractAnnotationsSidebar";
import {
  normalizeContractAnnotations,
  createContractAnnotation,
  addReplyToAnnotation,
  resolveAnnotation,
  stableAnchorKey,
} from "../utils/contractAnnotations";
import { pickScopingPersistPayload } from "../utils/pickScopingPersistPayload";
import { buildContractPreviewFromProfile } from "../utils/buildContractPreviewFromProfile";
import {
  buildContractParties,
  buildScopeOfWork,
  buildLocationSummary,
  buildDurationLabel,
  missingContractParties,
  stripLegacyFabricatedContractValues,
  fillBlankParties,
} from "../utils/contractParties";
import { useAdvisoryFirmRecord } from "../hooks/useAdvisoryFirmRecord";
import {
  computeLineTotal,
  computePriceEstimateGrandTotalOrNull,
  formatPriceLine,
  formatTrancheLine,
  isBlankMoney,
  listUnpricedLines,
  recomputeTranchePayables,
} from "../utils/pricingCalculations";
import {
  RACI_AI_FAILED_LABEL,
  applyEffortAssignments,
  applyRaciAssignments,
  describeControlAnalysis,
  generationNoticeMessages,
  isRowAiFailed,
  mapPricingResponse,
} from "../utils/scopingAiResults";
import { AiGenerationNotice } from "../components/AiGenerationNotice";
import {
  mergeProjectOnboardingWizard,
  getStoredOnboardingReviewStatusFromProfile,
  isOnboardingWorkflowGateSatisfied,
  getOnboardingReviewStatusForUi,
} from "../utils/projectOnboardingWizard";
import {
  getStepNavigationGate,
  clampStepToAllowed,
  getWorkspaceStepForProject,
} from "../utils/workspaceStepRestore";
import { MASTER_REGISTRY } from "../components/onboardingRegistry";
import { insertRowInPhaseGroup, removeRowAt } from "../utils/groupedRowOps";
import { normalizeUploadFileList, formatOnboardingUploadFailureMessage } from "../utils/onboardingAssetUpload";
import { raciCsv } from "../components/csvMocks";
import { milestoneCsv } from "../components/csvMocks";
import { resourceCsv } from "../components/csvMocks";
import { message } from "antd";
import { userMessageForError } from "../utils/apiError";
import { COUNTRIES, getCitiesForCountry } from "../constants/countries";
import { getBulkUploadTemplateHref, BULK_UPLOAD_TEMPLATE_FILE_NAME } from "../constants/bulkUploadTemplate";
import { ProjectOnboarding, renderOnboardingStatusBar } from "../components/ProjectOnboarding";
import { GapAnalysis } from "../components/GapAnalysis";
import { RiskAnalysis } from "../components/RiskAnalysis";
import { ControlImplementation } from "../components/ControlImplementation";
import { isControlImplementationFinalized } from "../utils/controlImplementation";
import { EvidencingMonitoring } from "../components/EvidencingMonitoring";
import { InternalAudit } from "../components/InternalAudit";
import { OrganizationalSetup } from "../components/OrganizationalSetup";
import { getOrgChartSpocEffectiveStatus } from "../utils/organizationalSetupWizard";
import { ActivityLogDrawer } from "../components/ActivityLogDrawer";
import { CompanyProfileDocument } from "../components/CompanyProfileDocument";
import ContractSigningPanel from "../components/ContractSigningPanel";
import CompanyEnrichmentPanel from "../components/CompanyEnrichmentPanel";

// Enrichment suggestion field -> profile attribute label (keys are onboardingRegistry attribute keys).
const ENRICHMENT_FIELD_LABELS = { website: "Website", linkedIn: "LinkedIn", hqCountry: "HQ Country", hqCity: "HQ City", aboutCompany: "About Company", yearOfInception: "Year of Inception", companyHeadcount: "Company Headcount" };
import SubStepNav from "../components/page/SubStepNav";
import StageHeader from "../components/page/StageHeader";
import { resolveReadableBlobUrl } from "../utils/blobPreview";
import { persistableBlobUrlFromCompanyUpload } from "../utils/persistableBlobUrl";
import { formatDdMmYyyy, formatDdMmYyyyTime, formatLongDateIst } from "../utils/formatDdMmYyyy";

/** Preset swatches for Company Color Palette (stored as #rrggbb). */
const COMPANY_COLOR_PRESETS = [
  "var(--color-text-hero)", "#1e293b", "var(--color-text-muted-dark)", "#475569", "var(--color-text-muted)",
  "#dc2626", "#ea580c", "#d97706", "#ca8a04", "#16a34a", "#059669",
  "#0d9488", "#2563eb", "#4f46e5", "#7c3aed", "#db2777",
  "#fce7f3", "#fef3c7", "#ecfdf5", "#e0f2fe", "var(--color-bg-base)",
];

function normalizeCompanyColorHex(v) {
  const s = String(v ?? "").trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/i.test(s)) {
    const r = s[1], g = s[2], b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return "";
}

/** Preset fonts for Company Font (`value` is a valid CSS font-family stack). Loaded families match `public/index.html` Google Fonts link. */
const COMPANY_FONT_OPTIONS = [
  { value: "inherit", label: "Default (inherit)" },
  { value: '"Inter", system-ui, sans-serif', label: "Inter" },
  { value: '"DM Sans", system-ui, sans-serif', label: "DM Sans" },
  { value: '"Open Sans", system-ui, sans-serif', label: "Open Sans" },
  { value: '"Lato", system-ui, sans-serif', label: "Lato" },
  { value: '"Montserrat", system-ui, sans-serif', label: "Montserrat" },
  { value: '"Outfit", system-ui, sans-serif', label: "Outfit" },
  { value: '"Syne", sans-serif', label: "Syne" },
  { value: '"Merriweather", Georgia, serif', label: "Merriweather" },
  { value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', label: "System UI" },
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  { value: "Georgia, Times, serif", label: "Georgia" },
  { value: '"Times New Roman", Times, serif', label: "Times New Roman" },
  { value: '"Courier New", Courier, monospace', label: "Courier New" },
];
function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (ch === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      row.push(cell);
      cell = "";
    } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && next === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}
function toKey(label) {
  return label.replace(/[^a-zA-Z0-9 ]/g, "").split(" ").map((w, i) => i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)).join("");
}
function mapFormat(format) {
  const f = (format || "").toLowerCase();
  if (f.includes("table")) return "table";
  if (f.includes("attachment")) return "attachment";
  if (f.includes("number")) return "number";
  if (f.includes("email")) return "email";
  if (f.includes("url")) return "url";
  if (f.includes("dropdown")) return "dropdown";
  return "text";
}
function mapCategory(cat) {
  const c = (cat || "").toLowerCase();
  if (c.includes("basic")) return "Basic";
  if (c.includes("contract")) return "Contractual";
  if (c.includes("it")) return "IT";
  if (c.includes("legal")) return "Legal";
  if (c.includes("brand")) return "Branding";
  if (c.includes("location")) return "Location";
  if (c.includes("optional")) return "Optional";
  return "Optional";
}
const SCOPING_WIZARD_ORDER = ["summary", "raci", "milestones", "resources", "pricing"];
const SCOPING_SUBTAB_IDS = ["summary", "raci", "milestones", "resources", "pricing", "contract"];
const SCOPING_SUBSTEP_NAV_SPECS = [
  { key: "summary", label: "Summary", num: 1 },
  { key: "raci", label: "RACI", num: 2 },
  { key: "milestones", label: "Milestones", num: 3 },
  { key: "resources", label: "Resources", num: 4 },
  { key: "pricing", label: "Pricing", num: 5 },
  { key: "contract", label: "Contract", num: 6 },
];
// R013 — which scoping subtabs a persona should even SEE in the nav strip, before any
// per-tab lock check runs. LA/Admin/SPOC see all 6; Advisor and Delegate never get an
// unlocked-looking "Contract" tab (commercial terms are LA/SPOC-only — mirrors
// canViewContract / the server-side contractAccessFilter). Advisor already gets a fully
// bespoke single read-only pill instead of this strip (see isAdvisorOnly branch below);
// this filter keeps the same rule true for Delegate, and keeps Advisor correct too if that
// bespoke branch is ever removed.
function visibleSubtabsForRole({ isLeadOrAdmin, isSPOC, isAdvisorOnly, isDelegate }) {
  if (isLeadOrAdmin || isSPOC) return SCOPING_SUBTAB_IDS;
  if (isAdvisorOnly || isDelegate) return SCOPING_SUBTAB_IDS.filter((id) => id !== "contract");
  return SCOPING_SUBTAB_IDS;
}
const defaultScopingWizard = () => ({ summary: false, raci: false, milestones: false, resources: false, pricing: false });

function parseMoneyToNumber(s) {
  return Number(String(s ?? "").replace(/,/g, "")) || 0;
}
function formatInrForStorage(n) {
  const r = Math.round(Number(n) || 0);
  return r.toLocaleString("en-IN");
}
function displayMoneyFromInr(inrStr, currency, inrPerUsd) {
  // A blank amount stays blank: "0" would read as a real zero price.
  if (isBlankMoney(inrStr)) return "";
  const n = parseMoneyToNumber(inrStr);
  if (currency === "INR") return n.toLocaleString("en-IN");
  // No FX rate => keep INR (the platform default); never guess a rate.
  if (!inrPerUsd) return n.toLocaleString("en-IN");
  const usd = n / inrPerUsd;
  return usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function inputToInrStorage(raw, currency, inrPerUsd) {
  if (isBlankMoney(raw)) return "";
  const n = parseFloat(String(raw).replace(/,/g, "")) || 0;
  const inr = currency === "INR" || !inrPerUsd ? n : n * inrPerUsd;
  return formatInrForStorage(inr);
}

/** Sum of max role-days per task row (parallel roles on the same task are not double-counted). */
function totalEffortParallelAdjusted(rRows, effortCols) {
  if (!rRows.length || !effortCols.length) return 0;
  return rRows.reduce((sum, row) => {
    const vals = effortCols.map(({ ci }) => Number(row[ci]) || 0);
    return sum + (vals.length ? Math.max(0, ...vals) : 0);
  }, 0);
}

function totalEffortRawRoleDays(rRows, effortCols) {
  if (!rRows.length || !effortCols.length) return 0;
  return rRows.reduce((sum, row) => sum + effortCols.reduce((s, { ci }) => s + (Number(row[ci]) || 0), 0), 0);
}

/** True for role effort columns — excludes phase/task ids and summary columns like Est. Effort (man days). */
function isResourceEffortRoleColumn(h) {
  const hLow = (h || "").toLowerCase();
  if (hLow.includes("phase") || hLow.includes("task")) return false;
  if (hLow.includes("est.") || hLow.includes("est ") || /^est\b/.test(hLow)) return false;
  if (hLow.includes("total") && (hLow.includes("effort") || hLow.includes("man"))) return false;
  return true;
}

/** Summarize locationDetails JSON for onboarding doc (avoids raw JSON in the table). */
function formatLocationDetailsSummary(raw) {
  if (!raw || typeof raw !== "string") return "-";
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return "-";
    return arr.map((loc) => {
      const city = loc.city || loc.Location || "";
      const country = loc.country || "";
      const place = [city, country].filter(Boolean).join(", ") || "Site";
      const hc = loc.headcount ?? loc.HC;
      const it = loc.itAssets ?? loc.it_assets;
      const cloud = loc.cloudServices ?? loc.cloud;
      const apps = loc.applications ?? loc.apps;
      const parts = [];
      if (hc != null && hc !== "") parts.push(`HC ${hc}`);
      if (it != null && it !== "") parts.push(`IT: ${typeof it === "object" ? JSON.stringify(it) : String(it)}`);
      if (cloud != null && cloud !== "") parts.push(`Cloud: ${typeof cloud === "object" ? JSON.stringify(cloud) : String(cloud)}`);
      if (apps != null && apps !== "") {
        const s = String(apps);
        parts.push(`Apps: ${s.length > 180 ? `${s.slice(0, 180)}…` : s}`);
      }
      return parts.length ? `${place} (${parts.join(" · ")})` : place;
    }).join(" | ");
  } catch {
    return raw.length > 400 ? `${raw.slice(0, 400)}…` : raw;
  }
}

/** Flatten JSON-ish IT profile fields to readable text for the doc table. */
function formatItProfileField(raw) {
  if (!raw || typeof raw !== "string") return "-";
  const t = raw.trim();
  if (!t) return "-";
  try {
    const v = JSON.parse(t);
    if (Array.isArray(v)) {
      return v.map((item) => {
        if (item != null && typeof item === "object") {
          return Object.entries(item).map(([k, val]) => `${k}: ${val}`).join(", ");
        }
        return String(item);
      }).join(" · ");
    }
    if (v != null && typeof v === "object") {
      return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join(", ");
    }
  } catch { /* use raw */ }
  return t.length > 500 ? `${t.slice(0, 500)}…` : t;
}

/**
 * API projects do not persist currentStep; undefined used to land on "Phase Locked".
 * Prefer profile.workspaceActiveStep (persisted workbench tab) over legacy currentStep (mocks),
 * then derive from profiling/scoping when neither is set.
 */

/** Stored onboarding review status only. For unlock rules use {@link isOnboardingWorkflowGateSatisfied}. */
function getOnboardingReviewStatusForGate(project) {
  return getStoredOnboardingReviewStatusFromProfile(project?.profile || {});
}

// getStepNavigationGate, clampStepToAllowed, getWorkspaceStepForProject moved to
// utils/workspaceStepRestore.js (mechanical, behavior-preserving extraction — see that file's
// header comment) so the stage-restore logic implicated in backlog #5 is unit-testable without
// mounting this page. Imported above alongside this file's other utils imports.

export const ProjectWorkspace = () => {
  const { activeProject: project, user, updateProjectProfile: onUpdateProfile, setActiveWorkspaceStep, saveProjectProfile, saveScopingProfile, updateProfilingStatusAPI, updateScopingStatusAPI, fetchMilestones, saveMilestones, generateMilestones, fetchProjectProfile, workspaceStepOverrideRef, profilingSubTabOverrideRef, openBlobUrl } = useApp();
  const persistScopingUiTab = useCallback(async (tab) => {
    if (!project?.id || !SCOPING_SUBTAB_IDS.includes(tab)) return;
    const profile2 = project.profile || {};
    const newProfile = { ...profile2, scopingActiveSubTab: tab };
    onUpdateProfile(project.id, newProfile);
    if (saveScopingProfile) {
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
    }
  }, [project?.id, onUpdateProfile, saveScopingProfile, fetchProjectProfile]);
  const persistWorkspaceActiveStep = useCallback(
    async (stepId) => {
      if (!project?.id || !saveScopingProfile) return;
      // Race-safe: updates only the workbench step against the latest profile in store.
      if (setActiveWorkspaceStep) setActiveWorkspaceStep(project.id, stepId);
      const ok = await saveScopingProfile(project.id, { workspaceActiveStep: stepId });
      if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
    },
    [project?.id, setActiveWorkspaceStep, saveScopingProfile, fetchProjectProfile],
  );
  const handleAfterGapDashboardPush = useCallback(() => {
    void (async () => {
      await persistWorkspaceActiveStep(5);
      setActiveStepId(5);
    })();
  }, [persistWorkspaceActiveStep]);
  const handleComplianceRegisterSentToSpoc = useCallback(
    ({ profile: mergedProfile, complianceRegisterPackage: pkgFromResponse }) => {
      let prof =
        mergedProfile && typeof mergedProfile === "object" ? mergedProfile : null;
      if (!prof || typeof prof !== "object") {
        prof = pkgFromResponse
          ? { ...(project.profile || {}), complianceRegisterPackage: pkgFromResponse }
          : project.profile || {};
      }
      const synthetic = { ...project, profile: prof };
      const g = getStepNavigationGate(6, synthetic, user?.role);
      if (g.allowed) {
        setActiveStepId(6);
        void persistWorkspaceActiveStep(6);
      }
    },
    [project, user?.role, persistWorkspaceActiveStep],
  );
  const [activeStepId, setActiveStepId] = useState(() =>
    clampStepToAllowed(getWorkspaceStepForProject(project, user), project, user?.role),
  );
  const [isAddAttrOpen, setIsAddAttrOpen] = useState(false);
  const [isColorPaletteExpanded, setIsColorPaletteExpanded] = useState(false);
  const [csvTableVisible, setCsvTableVisible] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [attrSearch, setAttrSearch] = useState("");
  const [expandedLocations, setExpandedLocations] = useState({});
  const [uploadingField, setUploadingField] = useState(null);
  const [onboardingExtraUploading, setOnboardingExtraUploading] = useState(false);
  const { instance: msalInstance } = useMsal();
  const getApiToken = useCallback(async () => {
    const resp = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account: msalInstance.getActiveAccount() });
    return resp.accessToken;
  }, [msalInstance]);
  const msalAccount = msalInstance.getActiveAccount();
  /** Stable dep for effects — AccountInfo object identity can change across renders. */
  const msalActiveAccountId = msalAccount?.homeAccountId ?? "";
  const profileHydrated = !!(project?.profile && Array.isArray(project.profile.attributes));
  const workspaceProjectIdRef = useRef(project?.id ?? null);

  useEffect(() => {
    const override = workspaceStepOverrideRef?.current;
    if (override != null) {
      const clamped = clampStepToAllowed(override, project, user?.role);
      setActiveStepId(clamped);
      if (clamped === override) {
        workspaceStepOverrideRef.current = null;
      }
      return;
    }
    if (!project) return;
    const pid = project.id ?? null;
    const idChanged = pid !== workspaceProjectIdRef.current;
    if (idChanged) {
      workspaceProjectIdRef.current = pid;
      setActiveStepId(clampStepToAllowed(getWorkspaceStepForProject(project, user), project, user?.role));
      return;
    }
    if (!profileHydrated) return;
    setActiveStepId(clampStepToAllowed(getWorkspaceStepForProject(project, user), project, user?.role));
    // Intentionally omit full `project` / nested wizard blobs — avoids resetting the workbench tab on every
    // Risk Analysis / Org Setup JSON churn; gate clamp effect still enforces invalid steps.
  }, [
    project?.id,
    profileHydrated,
    project?.profile?.workspaceActiveStep,
    project?.profilingStatus,
    project?.scopingStatus,
    project?.profile?.projectOnboardingWizard?.onboardingReviewStatus,
    project?.profile?.gapAnalysisWizard?.gapAnalysisStatus,
    project?.profile?.gapAnalysisWizard?.pushedToDashboard,
    project?.profile?.gapAnalysisWizard?.delegationFinalized,
    project?.profile?.complianceRegisterPackage?.workflow,
    user?.role,
    workspaceStepOverrideRef,
  ]);

  const handleFileUpload = async (fieldKey, file) => {
    if (!file || !project.companyId) return;
    // R003 / R041 fix: surface a read-only state instead of silently uploading + never persisting.
    if (!canEdit) {
      alert('You do not have permission to change this field on this project. Ask a Lead Advisor or platform admin.');
      return;
    }
    // R003 fix: pre-flight image validation so the user sees the reason before waiting on a network round-trip.
    if (fieldKey === 'companyLogo') {
      const okName = /\.(png|jpe?g)$/i.test(file.name || '');
      const okMime = file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg';
      if (!okName && !okMime) {
        alert('Logo must be a PNG or JPEG file. SVG, HEIC, WebP, and GIF are not supported.');
        return;
      }
      const MAX = 2 * 1024 * 1024;
      if (file.size > MAX) {
        alert(`Logo is ${(file.size / 1024 / 1024).toFixed(1)} MB - max ${(MAX / 1024 / 1024).toFixed(0)} MB. Please compress before uploading.`);
        return;
      }
    }
    setUploadingField(fieldKey);
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: msalAccount,
      });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldKey', fieldKey);
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/companies/${project.companyId}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
        body: formData,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        const persistUrl = persistableBlobUrlFromCompanyUpload(json.data);
        if (persistUrl) handleUpdateAttr(fieldKey, persistUrl);
      } else {
        console.error('Upload failed:', json.error?.message);
        alert('File upload failed: ' + (json.error?.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('File upload failed. Please try again.');
    } finally {
      setUploadingField(null);
    }
  };

  const filteredMasterRegistry = useMemo(() => {
    return MASTER_REGISTRY.filter(
      (attr) => attr.label.toLowerCase().includes(attrSearch.toLowerCase()) || attr.category.toLowerCase().includes(attrSearch.toLowerCase())
    );
  }, [attrSearch]);
  const profilingStatus = project.profilingStatus || "Draft";
  const stepGateMap = useMemo(() => {
    const m = {};
    for (let id = 1; id <= 9; id++) {
      m[id] = getStepNavigationGate(id, project, user?.role);
    }
    return m;
  }, [project, project?.profilingStatus, project?.scopingStatus, project?.profile?.projectOnboardingWizard?.onboardingReviewStatus, project?.profile?.gapAnalysisWizard?.gapAnalysisStatus, project?.profile?.gapAnalysisWizard?.pushedToDashboard, project?.profile?.complianceRegisterPackage?.workflow, project?.profile?.organizationalSetupWizard, user?.role]);

  const handleSelectStep = useCallback(
    (stepId) => {
      if (user?.role === UserRole.COMPANY_DELEGATE) {
        if (stepId === 4) {
          /* delegate gap lane — same as before */
        } else if (stepId === 6 && getStepNavigationGate(6, project, user?.role).allowed) {
          /* risk register when advisor sent register to delegate */
        } else if (stepId === 7 && getStepNavigationGate(7, project, user?.role).allowed) {
          /* control implementation when docs are in delegate_review */
        } else if (stepId === 8 && getStepNavigationGate(8, project, user?.role).allowed) {
          /* evidencing & monitoring when incidents are in delegate_review */
        } else {
          return;
        }
      }
      const g = getStepNavigationGate(stepId, project, user?.role);
      if (!g.allowed) {
        message.info(g.hint, 6);
        return;
      }
      setActiveStepId(stepId);
      void persistWorkspaceActiveStep(stepId);
    },
    [project, persistWorkspaceActiveStep, user?.role],
  );

  /** If project data updates (e.g. status refresh), keep the user on an allowed step */
  useEffect(() => {
    if (!project?.profile || !Array.isArray(project.profile.attributes)) return;
    const g = getStepNavigationGate(activeStepId, project, user?.role);
    if (!g.allowed) {
      setActiveStepId(clampStepToAllowed(activeStepId, project, user?.role));
    }
  }, [project?.profilingStatus, project?.scopingStatus, project?.profile?.projectOnboardingWizard?.onboardingReviewStatus, project?.profile?.gapAnalysisWizard?.gapAnalysisStatus, project?.profile?.gapAnalysisWizard?.pushedToDashboard, project?.profile?.complianceRegisterPackage?.workflow, project?.profile?.organizationalSetupWizard, project?.profile?.riskRegisterPackage?.workflow, activeStepId, project, user?.role]);

  const currentRole = user?.role;
  const isAdvisorOnly = currentRole === UserRole.ADVISOR;
  const isLeadOrAdmin =
    currentRole === UserRole.LEAD_ADVISOR || currentRole === UserRole.ADMIN;
  const isLeadAdvisor = isLeadOrAdmin || isAdvisorOnly;
  // Mirror backend statusPatchRoles.ts: scoping Finalized = Lead Advisor only; onboarding Finalized =
  // Advisor or Lead Advisor; platform_admin is refused on every approval/finalize (PRD s2.2). UX hint only.
  const canFinalizeScoping = currentRole === UserRole.LEAD_ADVISOR;
  const canFinalizeProfiling = currentRole === UserRole.LEAD_ADVISOR || currentRole === UserRole.ADVISOR;
  const isSPOC = currentRole === UserRole.COMPANY_SPOC;
  const isDelegate = currentRole === UserRole.COMPANY_DELEGATE;
  // R013 (Phase 3.3) — Contract + pricing tables are visible to LA / SPOC / Admin only.
  // The server also filters the profile (see backend contractAccessFilter.ts) — this is UX hint,
  // not the trust boundary. The `_redacted` marker below arrives from the server when denied.
  const canViewContract = isLeadOrAdmin || isSPOC;
  const contractDraftRedactedByServer =
    project?.profile?.scopingContractDraft &&
    typeof project.profile.scopingContractDraft === "object" &&
    project.profile.scopingContractDraft._redacted === true;
  const contractAccessDenied = !canViewContract || contractDraftRedactedByServer;
  const profile = useMemo(() => {
    const p = project.profile || { attributes: [] };
    const dbAttrs = p.attributes || [];
    const dbValueMap = new Map(dbAttrs.map((a) => [a.key, a.value ?? '']));

    // Build full attribute list from MASTER_REGISTRY, merging DB values
    const merged = MASTER_REGISTRY.map((def) => {
      const existing = dbAttrs.find((a) => a.key === def.key);
      const rawValue = dbValueMap.get(def.key) ?? '';

      // For table-format fields, parse the JSON string value into tableData
      let tableData = existing?.tableData || undefined;
      if (def.format === 'table' && rawValue && !tableData) {
        try {
          let parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
          if (typeof parsed === 'string') parsed = JSON.parse(parsed); // double-encoded
          if (Array.isArray(parsed)) {
            const headers = def.tableHeaders || ['Title', 'Location'];
            // Map backend keys (e.g. clientName) to display headers (e.g. "Client Name")
            tableData = parsed.map((item, idx) => {
              const row = { id: `db-${idx}-${Date.now()}` };
              headers.forEach((h, hi) => {
                const camelKey = h.replace(/\s+(.)/g, (_, c) => c.toUpperCase()).replace(/\s/g, '').replace(/^(.)/, (_, c) => c.toLowerCase());
                const direct = item[h];
                const camel = item[camelKey];
                if (direct !== undefined && direct !== null) {
                  row[h] = direct;
                } else if (camel !== undefined && camel !== null) {
                  row[h] = camel;
                } else if (headers.length === 1) {
                  const vals = Object.values(item);
                  row[h] = vals[hi] ?? vals[0] ?? '';
                } else {
                  row[h] = '';
                }
              });
              return row;
            });
          }
        } catch { /* not valid JSON, leave tableData undefined */ }
      }

      return {
        ...def,
        value: rawValue,
        tableData,
        isPendingReview: existing?.isPendingReview || false,
        lastModifiedBy: existing?.lastModifiedBy || null,
      };
    });

    // Also keep any extra attributes from dbAttrs not in MASTER_REGISTRY
    const registryKeys = new Set(MASTER_REGISTRY.map((d) => d.key));
    for (const attr of dbAttrs) {
      if (!registryKeys.has(attr.key)) {
        merged.push({ ...attr, isPendingReview: attr.isPendingReview || false });
      }
    }

    return { ...p, attributes: merged };
  }, [project.profile]);

  const companyLogoAttrValue = useMemo(() => {
    const a = profile.attributes?.find((x) => x.key === "companyLogo");
    const v = a?.value;
    return typeof v === "string" && v.startsWith("http") ? v : "";
  }, [profile.attributes]);

  const [companyLogoDisplayUrl, setCompanyLogoDisplayUrl] = useState("");
  const [companyLogoDisplayLoading, setCompanyLogoDisplayLoading] = useState(false);
  /** Avoids refetch + loading flicker when profile re-renders but logo URL is unchanged. */
  const companyLogoResolveCacheRef = useRef({ raw: "", display: "" });
  useEffect(() => {
    let cancelled = false;
    if (!companyLogoAttrValue) {
      companyLogoResolveCacheRef.current = { raw: "", display: "" };
      setCompanyLogoDisplayUrl("");
      setCompanyLogoDisplayLoading(false);
      return;
    }
    const cache = companyLogoResolveCacheRef.current;
    if (cache.raw === companyLogoAttrValue && cache.display) {
      setCompanyLogoDisplayLoading(false);
      setCompanyLogoDisplayUrl((prev) => (prev === cache.display ? prev : cache.display));
      return;
    }
    setCompanyLogoDisplayLoading(true);
    (async () => {
      try {
        const account = msalInstance.getActiveAccount();
        if (!account) {
          if (!cancelled) {
            const display = companyLogoAttrValue;
            companyLogoResolveCacheRef.current = { raw: companyLogoAttrValue, display };
            setCompanyLogoDisplayUrl(display);
            setCompanyLogoDisplayLoading(false);
          }
          return;
        }
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...apiTokenRequest,
          account,
        });
        const fresh = await resolveReadableBlobUrl(
          apiConfig.baseUrl,
          companyLogoAttrValue,
          async () => tokenResponse.accessToken,
        );
        if (!cancelled) {
          const display = fresh || companyLogoAttrValue;
          companyLogoResolveCacheRef.current = { raw: companyLogoAttrValue, display };
          setCompanyLogoDisplayUrl(display);
          setCompanyLogoDisplayLoading(false);
        }
      } catch {
        if (!cancelled) {
          const display = companyLogoAttrValue;
          companyLogoResolveCacheRef.current = { raw: companyLogoAttrValue, display };
          setCompanyLogoDisplayUrl(display);
          setCompanyLogoDisplayLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyLogoAttrValue, msalInstance, msalActiveAccountId]);

  const saveTimerRef = useRef(null);
  const companyColorPickerRef = useRef(null);
  const companySecondaryColorPickerRef = useRef(null);
  const companyFontDetailsRef = useRef(null);
  const updateAttributes = (newAttrs) => {
    onUpdateProfile(project.id, { ...profile, attributes: newAttrs });

    // Debounced save to backend (500ms)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (saveProjectProfile && project?.id) {
        const toSave = newAttrs.map((a) => {
          // For table-format fields, serialize tableData back to JSON value
          if (a.format === 'table' && a.tableData) {
            return { key: a.key, value: JSON.stringify(a.tableData.map(row => {
              const clean = { ...row };
              delete clean.id; // strip client-side ID
              return clean;
            })) };
          }
          return { key: a.key, value: a.value };
        });
        saveProjectProfile(project.id, toSave);
      }
    }, 500);
  };
  const markAttributeModified = (key, newAttrs) => {
    const updated = newAttrs.map((a) => a.key === key ? {
      ...a,
      isPendingReview: true,
      // store role only (do not expose user name)
      lastModifiedBy: user?.role || "UNKNOWN"
    } : a);
    updateAttributes(updated);
  };
  const handleUpdateAttr = (key, value) => {
    if (!canEdit) return;
    const nextAttrs = (profile.attributes || []).map((a) => a.key === key ? { ...a, value } : a);
    markAttributeModified(key, nextAttrs);
  };
  const handleDeleteAttr = (key) => {
    if (!canEdit) return;
    const newAttrs = (profile.attributes || []).filter((a) => a.key !== key);
    updateAttributes(newAttrs);
  };
  const handleAddAttr = (attr) => {
    if (!canEdit) return;
    if ((profile.attributes || []).find((a) => a.key === attr.key)) return;
    const headers = attr.tableHeaders || ["Title", "Location"];
    const defaultRow = headers.reduce((acc, h) => ({ ...acc, [h]: "" }), { id: Date.now().toString() });
    const newAttr = {
      ...attr,
      value: "",
      tableData: attr.format === "table" ? [defaultRow] : void 0,
      isPendingReview: true,
      lastModifiedBy: user?.name || "Authorized Member"
    };
    updateAttributes([...profile.attributes || [], newAttr]);
    setIsAddAttrOpen(false);
  };
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewTargetStatus, setReviewTargetStatus] = useState(null);
  const [docSections, setDocSections] = useState({
    execSummary: "",
    orgStructure: "",
    highLevel: "",
    gics: "",
    applicable: "",
    appendSections: [],
  });
  const [profilingSubTab, setProfilingSubTab] = useState(() => {
    const t = project?.profile?.profilingSubTab;
    return t === "profile" || t === "onboarding" ? t : "onboarding";
  });
  const persistProfilingSubTab = useCallback(
    async (tab) => {
      if (tab !== "profile" && tab !== "onboarding") return;
      setProfilingSubTab(tab);
      if (!project?.id || !saveScopingProfile || !onUpdateProfile) return;
      const base = project.profile || {};
      const newProfile = { ...base, profilingSubTab: tab };
      onUpdateProfile(project.id, newProfile);
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
    },
    [project?.id, project?.profile, onUpdateProfile, saveScopingProfile, fetchProjectProfile],
  );
  useEffect(() => {
    const t = project?.profile?.profilingSubTab;
    if (t === "profile" || t === "onboarding") setProfilingSubTab(t);
  }, [project?.id, project?.profile?.profilingSubTab]);
  useEffect(() => {
    const tab = profilingSubTabOverrideRef?.current;
    if (tab !== "onboarding" && tab !== "profile") return;
    profilingSubTabOverrideRef.current = null;
    void persistProfilingSubTab(tab);
  }, [project?.id, persistProfilingSubTab, profilingSubTabOverrideRef]);
  const [isDocEditMode, setIsDocEditMode] = useState(false);
  const [isDocGenerating, setIsDocGenerating] = useState(false);
  const hasProfilingDocDraft =
    project.profile?.profilingDocumentDraft &&
    typeof project.profile.profilingDocumentDraft === "object";
  const profilingDocHasBody =
    !!(docSections.execSummary || "").trim() ||
    !!(docSections.orgStructure || "").trim() ||
    hasProfilingDocDraft;
  const canEdit = (() => {
    if (profilingStatus === "Finalized" || hasProfilingDocDraft || profilingDocHasBody || isDocGenerating) return false;
    if (profilingStatus === "Under Review") return isSPOC;
    if (profilingStatus === "Draft" || profilingStatus === "Changes Requested" || profilingStatus === "Approved by SPOC") return isLeadOrAdmin;
    return false;
  })();
  // Task backlog #2 (UAT Section 5, Company Onboarding): the advisor may select
  // several supporting files in one file-picker interaction. The backend endpoint
  // (`companies_id_onboarding_assetsPost`, routes.ts: `docUpload.single('file')`)
  // is intentionally single-file-per-request (it mints one assetId/blob/audit row
  // per call), so multi-select is handled here by uploading each file in turn
  // against that same endpoint rather than widening the backend contract.
  const handleOnboardingExtraUpload = useCallback(
    async (fileOrFiles) => {
      const validFiles = normalizeUploadFileList(fileOrFiles);
      if (!validFiles.length || !project?.companyId) return;
      setOnboardingExtraUploading(true);
      const failed = [];
      try {
        for (const file of validFiles) {
          try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              ...apiTokenRequest,
              account: msalAccount,
            });
            const formData = new FormData();
            formData.append("file", file);
            formData.append("category", "branding");
            const res = await fetch(
              `${apiConfig.baseUrl}/api/v1/companies/${project.companyId}/onboarding-assets`,
              {
                method: "POST",
                headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                body: formData,
              }
            );
            const json = await res.json();
            if (!(res.ok && json.success)) {
              failed.push({ name: file.name, reason: json.error?.message || "Upload failed" });
            }
          } catch (e) {
            console.error(e);
            failed.push({ name: file.name, reason: "Upload failed" });
          }
        }
        if (fetchProjectProfile && failed.length < validFiles.length) {
          await fetchProjectProfile(project.id);
        }
        const failureMessage = formatOnboardingUploadFailureMessage(validFiles.length, failed);
        if (failureMessage) {
          message.error(failureMessage);
        }
      } finally {
        setOnboardingExtraUploading(false);
      }
    },
    [project?.companyId, project?.id, msalInstance, msalAccount, fetchProjectProfile]
  );
  const handleOnboardingAssetDelete = useCallback(
    async (assetId) => {
      if (!project?.companyId || !assetId) return;
      if (!window.confirm("Remove this file from onboarding?")) return;
      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...apiTokenRequest,
          account: msalAccount,
        });
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/companies/${project.companyId}/onboarding-assets/${encodeURIComponent(assetId)}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
          }
        );
        const json = await res.json();
        if (res.ok && json.success && fetchProjectProfile) {
          await fetchProjectProfile(project.id);
        } else {
          message.error(json.error?.message || "Delete failed");
        }
      } catch (e) {
        console.error(e);
        message.error("Delete failed");
      }
    },
    [project?.companyId, project?.id, msalInstance, msalAccount, fetchProjectProfile]
  );
  const [isViewOnlyDoc, setIsViewOnlyDoc] = useState(false);
  const [isContractEditMode, setIsContractEditMode] = useState(false);
  const [savedFlash, setSavedFlash] = useState(null);
  const [contractDraftSaving, setContractDraftSaving] = useState(false);
  const [contractSections, setContractSections] = useState({
    effectiveDate: "",
    companyName: "",
    companyPAN: "",
    companyGST: "",
    advisorName: "",
    advisorPAN: "",
    advisorGST: "",
    scopeOfWork: "",
    projectPlan: "",
    raciSummary: "",
    effortEstimate: "",
    priceEstimateSummary: "",
    paymentTerms: "",
    totalPayment: "",
    duration: "",
    advisorLogoUrl: "",
    creditPeriodDays: "",
    invoiceObjectionDays: "",
    terminationNoticeDays: "",
    jurisdiction: ""
  });
  const { firm: advisoryFirm } = useAdvisoryFirmRecord(project?.id);
  // Stage components that build client documents (gap questionnaire, context document) read the
  // real advisory firm from project.advisoryFirm; blank when the firm is unknown.
  const projectWithFirm = useMemo(() => (advisoryFirm ? { ...project, advisoryFirm } : project), [project, advisoryFirm]);
  const [scopingStatus, setScopingStatus] = useState(project.scopingStatus || "Draft");
  const [contractAnnotations, setContractAnnotations] = useState(() =>
    normalizeContractAnnotations(project.profile?.scopingContractAnnotations)
  );
  const [contractAnnotationFocusKey, setContractAnnotationFocusKey] = useState("");
  const [contractAppendSections, setContractAppendSections] = useState(() =>
    normalizeContractAppendSections(project.profile?.scopingContractAppendSections)
  );
  useEffect(() => {
    setContractAnnotations(normalizeContractAnnotations(project.profile?.scopingContractAnnotations));
  }, [project.id, project.profile?.scopingContractAnnotations]);
  useEffect(() => {
    setContractAppendSections(normalizeContractAppendSections(project.profile?.scopingContractAppendSections));
  }, [project.id, project.profile?.scopingContractAppendSections]);
  useEffect(() => {
    const d = project.profile?.profilingDocumentDraft;
    if (!d || typeof d !== "object") return;
    setDocSections((prev) => ({
      execSummary: d.execSummary ?? prev.execSummary ?? "",
      orgStructure: d.orgStructure ?? prev.orgStructure ?? "",
      highLevel: d.highLevel ?? prev.highLevel ?? "",
      gics: d.gics ?? prev.gics ?? "",
      applicable: d.applicable ?? prev.applicable ?? "",
      appendSections: Array.isArray(d.appendSections) ? d.appendSections : prev.appendSections || [],
    }));
  }, [project.id, project.profile?.profilingDocumentDraft]);
  useEffect(() => {
    if (profilingSubTab !== "profile") return;
    if (profilingStatus !== "Finalized") return;
    const saved = (profile.evidence || []).find((e) => e.name && e.name.includes("Onboarding"));
    if (!saved?.versions?.length) return;
    try {
      const parsed = JSON.parse(saved.versions[saved.versions.length - 1].content);
      if (parsed.execSummary != null) {
        setDocSections((prev) => ({
          ...prev,
          ...parsed,
          appendSections: Array.isArray(parsed.appendSections) ? parsed.appendSections : prev.appendSections || [],
        }));
      }
    } catch {
      /* ignore */
    }
  }, [profilingSubTab, profilingStatus, project.id, profile.evidence]);
  useEffect(() => {
    if (project.scopingStatus) setScopingStatus(project.scopingStatus);
  }, [project.scopingStatus]);
  const [isScopingReviewOpen, setIsScopingReviewOpen] = useState(false);
  const [scopingReviewComment, setScopingReviewComment] = useState("");
  const [scopingReviewTarget, setScopingReviewTarget] = useState(null);
  const canEditScoping = (() => {
    if (isAdvisorOnly) return false;
    if (scopingStatus === "Finalized") return false;
    if (scopingStatus === "Under Review") return false;
    if (scopingStatus === "Approved") return false;
    if (scopingStatus === "Draft") return isLeadOrAdmin;
    return isLeadOrAdmin;
  })();
  const [scopingTab, setScopingTab] = useState("summary");
  useEffect(() => {
    const raw = project?.profile?.scopingActiveSubTab;
    if (raw && SCOPING_SUBTAB_IDS.includes(raw)) {
      setScopingTab(raw);
    }
  }, [project?.id, project?.profile?.scopingActiveSubTab]);
  useEffect(() => {
    if (!isAdvisorOnly) return;
    setIsContractEditMode(false);
    setIsDocEditMode(false);
  }, [isAdvisorOnly, project?.id]);
  useEffect(() => {
    if (!isAdvisorOnly) return;
    if (activeStepId === 1) setProfilingSubTab("profile");
    if (activeStepId === 2) {
      setScopingTab("contract");
      void persistScopingUiTab("contract");
    }
  }, [isAdvisorOnly, activeStepId, project?.id, persistScopingUiTab]);
  const [controlAnalysis, setControlAnalysis] = useState(null);
  const [isAnalyzingControls, setIsAnalyzingControls] = useState(false);
  const [isGeneratingRaci, setIsGeneratingRaci] = useState(false);
  const [isGeneratingResources, setIsGeneratingResources] = useState(false);
  const [pricingDisplayCurrency, setPricingDisplayCurrency] = useState("INR");
  const [inrPerUsd, setInrPerUsd] = useState(null);
  const [isGeneratingPricing, setIsGeneratingPricing] = useState(false);
  // Warnings returned by the AI generators (failed rows, unpriced lines, ...) — shown, never swallowed.
  const [raciNotice, setRaciNotice] = useState([]);
  const [resourcesNotice, setResourcesNotice] = useState([]);
  const [pricingNotice, setPricingNotice] = useState([]);
  // generate-document returned no applicable requirements: say so instead of leaving stale/canned rows.
  const [docApplicableMissing, setDocApplicableMissing] = useState(false);
  const [aiDisabled, setAiDisabled] = useState({ raci: false, milestones: false, resources: false, pricing: false });
  const selectedFramework = project.standards && project.standards.length > 0 ? project.standards[0] : project.certification || "";
  // Counts are blank until real data exists: totals from the live registry / control analysis,
  // "in scope" only after control analysis has run. Nothing is seeded.
  const [scopingRows, setScopingRows] = useState(() => [
    { id: `r-${Date.now()}`, framework: selectedFramework, totalRequirements: "", totalControls: "", controlsInScope: "", applicableRegions: "" }
  ]);

  // Live ISO 27001 control counts fetched from the backend on mount
  const [isoControlStats, setIsoControlStats] = useState({ totalControls: null });
  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...apiTokenRequest,
          account: msalAccount,
        });
        const headers = { Authorization: `Bearer ${tokenResponse.accessToken}` };
        const ctrlRes = await fetch(`${apiConfig.baseUrl}/api/v1/iso27001/controls`, { headers });
        if (cancelled) return;
        if (ctrlRes.ok) {
          const json = await ctrlRes.json();
          const total = json?.meta?.total ?? json?.data?.controls?.length;
          if (typeof total === "number" && total > 0) setIsoControlStats({ totalControls: total });
        }
      } catch {
        // silently keep defaults on any error
      }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, []);

  const addScopingRow = () => {
    const newRow = { id: `r-${Date.now()}`, framework: selectedFramework, totalRequirements: "", totalControls: "", controlsInScope: "", applicableRegions: "", isEditing: true };
    setScopingRows((s) => [...s, newRow]);
  };
  const deleteScopingRow = (id) => {
    setScopingRows((s) => s.filter((r) => r.id !== id));
  };
  const toggleEditRow = (id, on) => {
    setScopingRows((s) => s.map((r) => r.id === id ? { ...r, isEditing: typeof on === "boolean" ? on : !r.isEditing } : r));
  };
  const updateScopingField = (id, field, value) => {
    setScopingRows((s) => s.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };
  const [milestoneItems, setMilestoneItems] = useState([]);
  const [milestoneLoading, setMilestoneLoading] = useState(false);
  const [milestoneGenerating, setMilestoneGenerating] = useState(false);

  // Fetch milestones from API on mount
  useEffect(() => {
    let cancelled = false;
    const loadMilestones = async () => {
      if (!project?.id) return;
      setMilestoneLoading(true);
      try {
        const data = await fetchMilestones(project.id);
        if (!cancelled && data && data.length) {
          setMilestoneItems(data.map((m, i) => ({
            id: m.id,
            wbs: String(m.sortOrder || i + 1),
            title: m.taskTitle,
            start: m.startDate || '',
            due: m.dueDate || '',
            duration: m.duration || 0,
            dependencies: m.dependencies || '',
            keyDeliverable: m.keyDeliverables || '',
            isAiGenerated: m.isAiGenerated || false,
          })));
        }
      } catch (e) {
        console.error('Failed to load milestones:', e);
      } finally {
        if (!cancelled) setMilestoneLoading(false);
      }
    };
    loadMilestones();
    return () => { cancelled = true; };
  }, [project?.id]);

  // Restore RACI / Summary / Resources / Pricing from DB when profile loads (fetchProjectProfile merges scopingProfile)
  const scopingHydratedSigRef = useRef(null);
  useEffect(() => {
    scopingHydratedSigRef.current = null;
  }, [project.id]);
  useEffect(() => {
    const p = project.profile;
    if (!p) return;
    const hasWizard = p.scopingWizard && typeof p.scopingWizard === "object" && Object.values(p.scopingWizard).some(Boolean);
    const hasDraft = p.scopingContractDraft && typeof p.scopingContractDraft === "object";
    // R007 fix: `raci: []` is a legitimate saved state (user cleared the table). Any array counts.
    const hasSavedTables = Array.isArray(p.raci) || (p.resources && Array.isArray(p.resources.rows)) || Array.isArray(p.scoping);
    if (!hasWizard && !hasDraft && !hasSavedTables) return;
    // R007 fix: a length-only sig missed same-count edits and treated them as "already hydrated".
    // Cheap content-aware fingerprint — O(n) row-length sum, no JSON.stringify.
    const fp = (rows) => Array.isArray(rows)
      ? `${rows.length}:${rows.reduce((a, r) => a + (Array.isArray(r) ? r.join("|").length : 0), 0)}`
      : "0:0";
    const sig = `${project.id}:${p.scopingContractDraft?.scopingDocumentUpdatedAt || ""}:${JSON.stringify(p.scopingWizard || {})}:raci=${fp(p.raci)}:res=${fp(p.resources?.rows)}:sco=${fp(p.scoping)}`;
    if (scopingHydratedSigRef.current === sig) return;
    scopingHydratedSigRef.current = sig;
    // Accept `[]` — an intentional empty state is data, not "nothing to hydrate".
    if (Array.isArray(p.raci)) setRaciRows(p.raci);
    if (Array.isArray(p.scoping)) setScopingRows(p.scoping);
    if (p.resources && p.resources.header && Array.isArray(p.resources.rows)) setResourceTable(p.resources);
    if (Array.isArray(p.priceEstimate) && p.priceEstimate.length) setPriceEstimate(p.priceEstimate);
    if (Array.isArray(p.paymentTranches) && p.paymentTranches.length) setPaymentTranches(p.paymentTranches);
    if (p.pricingDisplayCurrency === "INR" || p.pricingDisplayCurrency === "USD") setPricingDisplayCurrency(p.pricingDisplayCurrency);
    if (typeof p.inrPerUsd === "number" && p.inrPerUsd > 1) setInrPerUsd(p.inrPerUsd);
  }, [project.id, project.profile]);

  useEffect(() => {
    setControlAnalysis(null);
  }, [project.id]);

  useEffect(() => {
    const ca = project?.profile?.scopingControlAnalysis;
    if (ca != null && typeof ca === "object") setControlAnalysis(ca);
  }, [project?.profile?.scopingControlAnalysis]);

  useEffect(() => {
    if (scopingTab !== "pricing") return;
    let cancelled = false;
    fetch(`${apiConfig.baseUrl}/api/v1/public/fx/usd-inr`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j.success || typeof j.data?.inrPerUsd !== "number") return;
        setInrPerUsd(j.data.inrPerUsd);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [scopingTab]);

  // Generate milestones via AI
  const handleGenerateMilestones = async () => {
    if (!project?.id) return;
    setMilestoneGenerating(true);
    try {
      const data = await generateMilestones(project.id);
      if (data && data.length) {
        setMilestoneItems(data.map((m, i) => ({
          id: m.id,
          wbs: String(m.sortOrder || i + 1),
          title: m.taskTitle,
          start: m.startDate || '',
          due: m.dueDate || '',
          duration: m.duration || 0,
          dependencies: m.dependencies || '',
          keyDeliverable: m.keyDeliverables || '',
          isAiGenerated: m.isAiGenerated || false,
        })));
      }
    } catch (e) {
      console.error('Failed to generate milestones:', e);
    } finally {
      setMilestoneGenerating(false);
    }
  };

  // Save milestones to backend
  const handleSaveMilestones = async () => {
    setAiDisabled(prev => ({ ...prev, milestones: true }));
    if (!project?.id) return;
    const payload = milestoneItems.map(m => ({
      id: m.id,
      startDate: m.start || null,
      dueDate: m.due || null,
      duration: m.duration || null,
      dependencies: m.dependencies || null,
      keyDeliverables: m.keyDeliverable || null,
    }));
    const ok = await saveMilestones(project.id, payload);
    if (ok) {
      setSavedFlash('milestones');
      setTimeout(() => setSavedFlash(null), 2000);
    }
  };
  const addMilestone = () => {
    setMilestoneItems((s) => [...s, { id: `m-${Date.now()}`, wbs: "", title: "New task", start: "", due: "", duration: 7, isEditing: true }]);
  };
  const updateMilestoneField = (id, field, value) => {
    setMilestoneItems((s) => {
      const update = (item) => {
        const updated = { ...item, [field]: value };
        if (field === "start" || field === "due") {
          const startStr = field === "start" ? value : updated.start;
          const dueStr = field === "due" ? value : updated.due;
          if (startStr && dueStr) {
            const diffMs = new Date(dueStr).getTime() - new Date(startStr).getTime();
            if (!isNaN(diffMs) && diffMs >= 0) {
              updated.duration = Math.round(diffMs / (24 * 60 * 60 * 1e3));
            }
          }
        }
        if (field === "duration" && updated.start) {
          const startMs = new Date(updated.start).getTime();
          if (!isNaN(startMs)) {
            const dueMs = startMs + Number(value) * 24 * 60 * 60 * 1e3;
            updated.due = new Date(dueMs).toISOString().slice(0, 10);
          }
        }
        return updated;
      };
      const exists = s.find((it) => it.id === id);
      if (exists) {
        return s.map((it) => it.id === id ? update(it) : it);
      }
      return [...s, update({ id, wbs: "", title: "", start: "", due: "", duration: 7 })];
    });
  };
  const deleteMilestone = (id) => {
    setMilestoneItems((s) => s.filter((it) => it.id !== id));
  };
  const ganttContainerRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const startGanttDrag = (e, m, leftPct, widthPct, totalWeeks, gridStartDate) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const container = ganttContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    dragRef.current = {
      id: m.id,
      pointerId: e.pointerId,
      type: "move",
      startX: e.clientX,
      startLeftPct: leftPct,
      startWidthPct: widthPct,
      containerWidth: rect.width,
      totalWeeks,
      gridStartDate
    };
    window.addEventListener("pointermove", onGanttPointerMove);
    window.addEventListener("pointerup", onGanttPointerUp);
  };
  const startGanttResize = (e, m, leftPct, widthPct, totalWeeks, gridStartDate) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const container = ganttContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    dragRef.current = {
      id: m.id,
      pointerId: e.pointerId,
      type: "resize",
      startX: e.clientX,
      startLeftPct: leftPct,
      startWidthPct: widthPct,
      containerWidth: rect.width,
      totalWeeks,
      gridStartDate
    };
    window.addEventListener("pointermove", onGanttPointerMove);
    window.addEventListener("pointerup", onGanttPointerUp);
  };
  const onGanttPointerMove = (ev) => {
    const d = dragRef.current;
    if (!d) return;
    const deltaPx = ev.clientX - d.startX;
    const deltaPct = deltaPx / d.containerWidth * 100;
    if (d.type === "move") {
      const newLeft = Math.max(0, Math.min(100 - d.startWidthPct, d.startLeftPct + deltaPct));
      const newWeekIndex = Math.round(newLeft / 100 * d.totalWeeks);
      const newStart = new Date(d.gridStartDate.getTime() + newWeekIndex * 7 * 24 * 3600 * 1e3);
      updateMilestoneField(d.id, "start", newStart.toISOString().slice(0, 10));
    } else if (d.type === "resize") {
      const newWidthPct = Math.max(1 / d.totalWeeks * 100, Math.min(100 - d.startLeftPct, d.startWidthPct + deltaPct));
      const newSpanWeeks = Math.max(1, Math.round(newWidthPct / 100 * d.totalWeeks));
      updateMilestoneField(d.id, "duration", newSpanWeeks * 7);
    }
  };
  const onGanttPointerUp = (ev) => {
    const d = dragRef.current;
    if (!d) return;
    window.removeEventListener("pointermove", onGanttPointerMove);
    window.removeEventListener("pointerup", onGanttPointerUp);
    dragRef.current = null;
  };
  const [resourceTable, setResourceTable] = useState(() => {
    const fromProfile = project.profile?.resources;
    if (fromProfile && fromProfile.header && fromProfile.rows && fromProfile.rows.length > 3) return fromProfile;
    try {
      const allRows = parseCsv(resourceCsv);
      const header2 = allRows[1] || [];
      const data = allRows.slice(2).filter((r) => r.some((c) => (c || "").toString().trim()));
      if (header2.length && data.length) {
        // Template rows only: effort cells stay blank until a human (or an explicitly marked AI draft) fills them.
        // Phase ID / title / task come from the template; every role-effort cell (col >= 3) starts blank.
        const filled = data.map((row) => header2.map((h, ci) => (ci < 3 ? (row[ci] || "").toString().trim() : "")));
        return { header: header2, rows: filled };
      }
    } catch (e) {
    }
    const header = ["Phase ID", "Phase title", "Task title", "Lead Advisor", "Advisor", "Associate Advisor", "Technical Advisor"];
    const rows = [
      ["1", "Project onboarding", "Create project", "", "", "", ""],
      ["2", "Gap analysis", "Complete technical assessment", "", "", "", ""],
      ["3", "Organizational setup", "Set up collaboration channels", "", "", "", ""]
    ];
    return { header, rows };
  });
  // No seeded commercials: the Lead Advisor builds the estimate; empty until then.
  const [priceEstimate, setPriceEstimate] = useState([]);
  const [paymentTranches, setPaymentTranches] = useState([]);
  // Bug #8 fix: a row's Total is derived from Unit Price x Total Units, never independently
  // set — this is what guarantees `totalPrice === unitPrice * totalUnits` for every row.
  // Bug #9 fix: whenever a price row edit can move the Price Estimate grand total, the
  // Payment Tranches are re-derived from that same grand total (see `applyPriceEstimateUpdate`)
  // so the two tables' footer totals never drift apart. See utils/pricingCalculations.js.
  const withDerivedTotal = (row, field, value) => {
    const updated = { ...row, [field]: value };
    if (field === "totalUnits" || field === "unitPrice") {
      updated.totalPrice = computeLineTotal(updated.totalUnits, updated.unitPrice);
    }
    return updated;
  };
  const applyPriceEstimateUpdate = (updater) => {
    setPriceEstimate((prev) => {
      const next = updater(prev);
      setPaymentTranches((pt) => recomputeTranchePayables(pt, computePriceEstimateGrandTotalOrNull(next)));
      return next;
    });
  };
  const updatePriceRow = (id, field, value) => {
    applyPriceEstimateUpdate((prev) => prev.map((p) => {
      if (p.id === id) return withDerivedTotal(p, field, value);
      if (p.children) {
        return { ...p, children: p.children.map((c) => c.id === id ? withDerivedTotal(c, field, value) : c) };
      }
      return p;
    }));
  };
  const updateTrancheRow = (id, field, value) => {
    setPaymentTranches((prev) => {
      const next = prev.map((t) => t.id === id ? { ...t, [field]: value } : t);
      // Bug #9: a changed percentage share must redistribute so the tranches total still
      // matches the Price Estimate grand total exactly.
      return field === "pctOfTotal" ? recomputeTranchePayables(next, computePriceEstimateGrandTotalOrNull(priceEstimate)) : next;
    });
  };
  const addPriceChildRow = (parentId) => {
    applyPriceEstimateUpdate((prev) => prev.map((p) => {
      if (p.id === parentId) {
        const newChild = { id: `pe-${Date.now()}`, srNo: "", expenseHead: "New item", unitOfMeasurement: "", totalUnits: "", unitPrice: "", totalPrice: "" };
        return { ...p, children: [...p.children || [], newChild] };
      }
      return p;
    }));
  };
  const addPriceCategory = () => {
    const nextSr = String(priceEstimate.length + 1);
    applyPriceEstimateUpdate((prev) => [...prev, { id: `pe-${Date.now()}`, srNo: nextSr, expenseHead: "New category", unitOfMeasurement: "", totalUnits: "", unitPrice: "", totalPrice: "", isHeader: true, children: [] }]);
  };
  const addTrancheRow = () => {
    setPaymentTranches((prev) => {
      const next = [...prev, { id: `pt-${Date.now()}`, srNo: "", particulars: `Tranche ${prev.length + 1}`, description: "", pctOfTotal: "", payableAmount: "" }];
      return recomputeTranchePayables(next, computePriceEstimateGrandTotalOrNull(priceEstimate));
    });
  };
  useEffect(() => {
    const now = /* @__PURE__ */ new Date();
    const weekStart = (d, w = 0) => {
      const dd = new Date(d.getTime() + w * 7 * 24 * 60 * 60 * 1e3);
      return dd.toISOString().slice(0, 10);
    };
    try {
      const raciParsed = parseCsv(raciCsv);
      const raciHeader = raciParsed[0] && raciParsed[0].length ? raciParsed[0] : ["Phase ID", "Phase title", "Task title", "Responsible", "Accountable", "Consulted", "Informed"];
      setRaciRows((prev) => {
        const base = prev && prev.length ? prev : [
          ["1", "Project onboarding", "Create the project", "", "", "", ""],
          ["2", "Gap analysis", "Complete technical assessment", "", "", "", ""]
        ];
        return base.map((row, ri) => raciHeader.map((h, ci) => {
          return (row[ci] || "").toString().trim();
        }));
      });
      setResourceTable((rt) => {
        const header = rt && rt.header && rt.header.length ? rt.header : ["Phase ID", "Phase title", "Task title", "Lead Advisor", "Advisor", "Associate Advisor", "Technical Advisor", "Est. Effort (man days)"];
        const rows = rt && rt.rows && rt.rows.length ? rt.rows : [
          ["1", "Project onboarding", "Create project", "", "", "", "", ""]
        ];
        const sanitized = rows.map((r, ri) => header.map((h, ci) => {
          // Never invent cell values: blank stays blank for the Lead Advisor to fill.
          return (r[ci] || "").toString().trim();
        }));
        return { header, rows: sanitized };
      });
      // Milestones are now loaded from the API, no local seeding needed
    } catch (e) {
    }
  }, []);
  const updateResourceCell = (rowIdx, colIdx, val) => {
    setResourceTable((t) => {
      const next = { header: t.header, rows: t.rows.map((r, i) => i === rowIdx ? r.map((c, j) => j === colIdx ? val : c) : r) };
      return next;
    });
  };
  const addResourceRow = () => {
    setResourceTable((t) => {
      const newRow = t.header.map(() => "");
      return { ...t, rows: [...t.rows, newRow] };
    });
  };
  // R009 — Resources grouped-table parity with RACI's addTaskInPhase: insert the new row
  // immediately after the last existing row of the same phase (matched on Phase TITLE, the
  // actual grouping key the render below groups by), and carry the group's Phase ID onto the
  // new row so it doesn't render with a blank ID badge. Shared, unit-tested logic lives in
  // utils/groupedRowOps.js (see groupedRowOps.test.js).
  const addResourceRowInPhase = (phaseTitle) => {
    setResourceTable((t) => ({ ...t, rows: insertRowInPhaseGroup(t.rows, t.header, phaseTitle, "New Task") }));
  };
  const deleteResourceRow = (rowIdx) => {
    setResourceTable((t) => ({ ...t, rows: removeRowAt(t.rows, rowIdx) }));
  };
  const [raciRows, setRaciRows] = useState(() => {
    const fromProfile = project.profile?.raci;
    if (fromProfile && Array.isArray(fromProfile) && fromProfile.length) return fromProfile;
    try {
      const rows = parseCsv(raciCsv);
      const header = rows[0] || [];
      const data = rows.slice(1).filter((r) => r.length > 1);
      if (data.length) return data;
    } catch (e) {
    }
    return [
      ["1", "Project onboarding", "Create the project", "", "", "", ""],
      ["2", "Gap analysis", "Complete technical assessment", "", "", "", ""],
      ["3", "Control implementation", "Implement organizational controls", "", "", "", ""]
    ];
  });
  // R008 — caller MUST pass the phase's TITLE (not its Phase ID) — see the call site
  // fix in renderRaciFromTest's "+ Add Task" button and utils/groupedRowOps.js's doc
  // comment for the exact ID-vs-title regression this shape was extracted to fix.
  const addTaskInPhase = (phaseTitle) => {
    const header = (parseCsv(raciCsv)[0]) || [];
    setRaciRows((r) => insertRowInPhaseGroup(r, header, phaseTitle, "New Task"));
  };
  const renderRaciFromTest = () => {
    const rows = parseCsv(raciCsv);
    const headerFromCsv = rows[0] && rows[0].length ? rows[0] : ["Phase ID", "Phase title", "Task title", "Responsible", "Accountable", "Consulted", "Informed"];
    const header = headerFromCsv;
    const phaseTitleIdx = header.findIndex((h) => (h || "").toLowerCase().includes("phase title"));
    const phaseIdIdx = header.findIndex((h) => (h || "").toLowerCase().includes("phase id"));
    const phaseMap = {};
    raciRows.forEach((r, idx) => {
      const phaseLabel = phaseTitleIdx >= 0 ? (r[phaseTitleIdx] || "").toString().trim() : phaseIdIdx >= 0 ? (r[phaseIdIdx] || "").toString().trim() : "Unspecified";
      const phaseId = phaseIdIdx >= 0 ? (r[phaseIdIdx] || "").toString().trim() : phaseLabel || `p-${idx}`;
      if (!phaseMap[phaseLabel]) phaseMap[phaseLabel] = { id: phaseId, phase: phaseLabel, tasks: [] };
      const taskCol = header.findIndex((h) => (h || "").toLowerCase().includes("task"));
      const respCol = header.findIndex((h) => (h || "").toLowerCase().includes("responsible"));
      const accCol = header.findIndex((h) => (h || "").toLowerCase().includes("accountable"));
      const conCol = header.findIndex((h) => (h || "").toLowerCase().includes("consulted"));
      const infCol = header.findIndex((h) => (h || "").toLowerCase().includes("informed"));
      phaseMap[phaseLabel].tasks.push({
        id: idx,
        title: (r[taskCol] || "").toString().trim(),
        r: (r[respCol] || "").toString().trim(),
        a: (r[accCol] || "").toString().trim(),
        c: (r[conCol] || "").toString().trim(),
        i: (r[infCol] || "").toString().trim(),
        aiFailed: isRowAiFailed(r)
      });
    });
    const phases = Object.keys(phaseMap).map((k) => ({ id: phaseMap[k].id, phase: phaseMap[k].phase || k, tasks: phaseMap[k].tasks }));
    const ROLES = ["Lead Advisor", "Advisor", "Company SPOC", "Delegate"];
    // Takes the phase TITLE (addTaskInPhase's match key), not a Phase ID — see the
    // "+ Add Task" button below and utils/groupedRowOps.js's doc comment.
    const handleAddTask = (phaseTitle) => {
      addTaskInPhase(phaseTitle);
    };
    const handleUpdateTask = (phaseId, taskIdx, updates) => {
      if (updates.title !== void 0) updateRaciCell(taskIdx, header.findIndex((h) => (h || "").toLowerCase().includes("task")), updates.title);
      if (updates.r !== void 0) updateRaciCell(taskIdx, header.findIndex((h) => (h || "").toLowerCase().includes("responsible")), updates.r);
      if (updates.a !== void 0) updateRaciCell(taskIdx, header.findIndex((h) => (h || "").toLowerCase().includes("accountable")), updates.a);
      if (updates.c !== void 0) updateRaciCell(taskIdx, header.findIndex((h) => (h || "").toLowerCase().includes("consulted")), updates.c);
      if (updates.i !== void 0) updateRaciCell(taskIdx, header.findIndex((h) => (h || "").toLowerCase().includes("informed")), updates.i);
    };
    const handleDeleteTask = (phaseId, taskIdx) => {
      deleteRaciRow(taskIdx);
    };
    return /* @__PURE__ */ jsx("div", {
      className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-700", children: /* @__PURE__ */ jsx("div", {
        className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
          className: "w-full text-left", children: [
      /* @__PURE__ */ jsx("thead", {
            children: /* @__PURE__ */ jsxs("tr", {
              className: "bg-[color:var(--color-surface-sunken)] text-[10px] font-black uppercase tracking-[0.2em] text-[color:var(--color-accent)]", children: [
        /* @__PURE__ */ jsx("th", { className: "px-8 py-6", children: "Engagement Workflow Item" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-6 text-center", children: "Responsible" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-6 text-center", children: "Accountable" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-6 text-center", children: "Consulted" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-6 text-center", children: "Informed" }),
                canEditScoping && /* @__PURE__ */ jsx("th", { className: "px-6 py-6 text-right", children: "Actions" })
              ]
            })
          }),
      /* @__PURE__ */ jsx("tbody", {
            className: "divide-y divide-[color:var(--color-border)]", children: phases.map((phase) => /* @__PURE__ */ jsxs(React.Fragment, {
              children: [
        /* @__PURE__ */ jsx("tr", {
                className: "bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_50%,transparent)]", children: /* @__PURE__ */ jsx("td", {
                  colSpan: canEditScoping ? 6 : 5, className: "px-8 py-4", children: /* @__PURE__ */ jsxs("div", {
                    className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs("div", {
                      className: "flex items-center gap-3", children: [
            /* @__PURE__ */ jsx("span", { className: "w-6 h-6 rounded-lg bg-[color:var(--color-accent-fill)] text-white flex items-center justify-center text-[10px] font-black", children: phase.id }),
            /* @__PURE__ */ jsx("span", { className: "text-[11px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: phase.phase })
                      ]
                    }),
                      canEditScoping && /* @__PURE__ */ jsx(
                        "button",
                        {
                          // Bug fix: addTaskInPhase matches rows by Phase TITLE (the actual grouping
                          // key), but this handler was passing phase.id (the separate Phase ID badge
                          // value) — those never match raciCsv's data ("1" vs "Project onboarding"),
                          // so the match loop always fell through to its `r.length` default and every
                          // "+ Add Task" click silently appended the new row as its OWN new group at
                          // the bottom instead of inside this phase. Pass phase.phase (the title) so
                          // the row lands, and stays grouped, under the phase the button was clicked in.
                          onClick: () => handleAddTask(phase.phase),
                          className: "px-3 py-1 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-accent)] rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm",
                          children: "+ Add Task"
                        }
                      )
                    ]
                  })
                })
              }),
                phase.tasks.length === 0 && /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: canEditScoping ? 6 : 5, className: "px-12 py-4 text-[10px] text-[color:var(--color-text-muted)] italic", children: "No tasks provisioned for this phase." }) }),
                phase.tasks.map((task) => /* @__PURE__ */ jsxs("tr", {
                  className: "group hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_5%,transparent)] transition-all", children: [
          /* @__PURE__ */ jsx("td", {
                    className: "px-12 py-4", children: [/* @__PURE__ */ jsx(
                      "input",
                      {
                        type: "text",
                        value: task.title,
                        onChange: (e) => handleUpdateTask(phase.id, task.id, { title: e.target.value }),
                        readOnly: !canEditScoping,
                        className: `w-full bg-transparent text-sm font-bold text-[color:var(--color-text-hero)] outline-none transition-colors ${canEditScoping ? "focus:text-[color:var(--color-accent)]" : "cursor-default"}`
                      },
                      "title"
                    ),
                    task.aiFailed && /* @__PURE__ */ jsx("div", { "data-testid": "raci-row-ai-failed", className: "mt-1 text-[10px] font-bold text-[color:var(--color-warn-fg)]", children: RACI_AI_FAILED_LABEL }, "aifailed")]
                  }),
                    ["r", "a", "c", "i"].map((roleKey) => {
                      const colIdx = roleKey === "r" ? header.findIndex((h) => (h || "").toLowerCase().includes("responsible")) : roleKey === "a" ? header.findIndex((h) => (h || "").toLowerCase().includes("accountable")) : roleKey === "c" ? header.findIndex((h) => (h || "").toLowerCase().includes("consulted")) : header.findIndex((h) => (h || "").toLowerCase().includes("informed"));
                      return /* @__PURE__ */ jsx("td", {
                        className: "px-2 py-4 text-center", children: /* @__PURE__ */ jsx(
                          "select",
                          {
                            value: (() => {
                              const cellVal = (raciRows[task.id] || [])[colIdx];
                              return (cellVal && cellVal.trim() && ROLES.includes(cellVal.trim())) ? cellVal.trim() : "";
                            })(),
                            onChange: (e) => handleUpdateTask(phase.id, task.id, { [roleKey]: e.target.value }),
                            disabled: !canEditScoping,
                            className: `bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1 text-[9px] font-black uppercase tracking-tighter text-[color:var(--color-text-muted)] outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] w-full max-w-[120px] ${canEditScoping ? "cursor-pointer" : "cursor-default opacity-80"}`,
                            children: [/* @__PURE__ */ jsx("option", { value: "", children: "- Select -" }, "_empty"), ...ROLES.map((r) => /* @__PURE__ */ jsx("option", { value: r, children: r }, r))]
                          }
                        )
                      }, roleKey);
                    }),
                    canEditScoping && /* @__PURE__ */ jsx("td", {
                      className: "px-6 py-4 text-right", children: /* @__PURE__ */ jsx(
                        "button",
                        {
                          onClick: () => handleDeleteTask(phase.id, task.id),
                          className: "p-2 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-colors opacity-0 group-hover:opacity-100",
                          children: /* @__PURE__ */ jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" }) })
                        }
                      )
                    })
                  ]
                }, task.id))
              ]
            }, phase.id))
          })
          ]
        })
      })
    });
  };
  const addRaciRow = () => {
    setRaciRows((r) => [...r, ["", "New Phase", "New task", "", "", "", ""]]);
  };
  const updateRaciCell = (rowIdx, colIdx, val) => {
    setRaciRows((r) => r.map((row, i) => i === rowIdx ? row.map((c, j) => j === colIdx ? val : c) : row));
  };
  const deleteRaciRow = (rowIdx) => {
    setRaciRows((r) => removeRowAt(r, rowIdx));
  };
  const generateRaciWithAI = async () => {
    if (isGeneratingRaci) return;
    setIsGeneratingRaci(true);
    try {
      const rows = parseCsv(raciCsv);
      const header = rows[0] || [];
      const phaseTitleIdx = header.findIndex((h) => (h || "").toLowerCase().includes("phase title"));
      const taskColIdx = header.findIndex((h) => (h || "").toLowerCase().includes("task"));
      // Must keep 1:1 order with raciRows — do not filter rows out, or assignments[i] no longer
      // matches row i and the last rows get no AI data (see empty-task row in raciCsv).
      const tasks = raciRows.map((r) => ({
        phase: (r[phaseTitleIdx] || "").toString().trim() || "Unspecified",
        task: (r[taskColIdx] || "").toString().trim() || "Untitled task",
      }));

      if (!tasks.length) {
        alert("No tasks found in the RACI table. Add task titles or reload the default RACI rows.");
        return;
      }

      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: msalAccount,
      });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/raci/generate`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${tokenResponse.accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tasks }),
      });
      const json = await res.json();
      if (res.ok && json.success && json.data?.assignments) {
        setRaciRows((prev) => applyRaciAssignments(prev, json.data.assignments, header).rows);
        setRaciNotice(generationNoticeMessages(json.data, "RACI"));
      } else {
        alert("Failed to generate RACI: " + (json.error?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("RACI AI generation error:", err);
      alert("Failed to generate RACI assignments. Please try again.");
    } finally {
      setIsGeneratingRaci(false);
    }
  };
  const generateResourcesWithAI = async () => {
    if (isGeneratingResources) return;
    setIsGeneratingResources(true);
    try {
      const rHeader = resourceTable?.header?.length ? resourceTable.header : [];
      const rRows = resourceTable?.rows || [];
      const phaseTitleColIdx = rHeader.findIndex((h) => (h || "").toLowerCase().includes("phase title"));
      const taskColIdx = rHeader.findIndex((h) => (h || "").toLowerCase().includes("task"));
      const effortCols = rHeader.map((h, ci) => ({ h, ci })).filter(({ h }) => isResourceEffortRoleColumn(h));
      const roleLabels = effortCols.map(({ h }) => h);
      const tasks = rRows.map((r) => ({
        phase: (phaseTitleColIdx >= 0 ? r[phaseTitleColIdx] : "").toString().trim() || "Unspecified",
        task: (taskColIdx >= 0 ? r[taskColIdx] : "").toString().trim() || "Untitled task",
      }));
      if (!tasks.length) {
        alert("No rows in the effort registry.");
        return;
      }
      if (!roleLabels.length) {
        alert("Could not detect role columns (effort columns).");
        return;
      }
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: msalAccount,
      });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/resources/generate`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${tokenResponse.accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tasks, roleLabels }),
      });
      const json = await res.json();
      if (res.ok && json.success && json.data?.assignments) {
        setResourceTable((prev) => ({
          header: prev.header,
          rows: applyEffortAssignments(prev.rows, json.data.assignments, effortCols),
        }));
        setResourcesNotice(generationNoticeMessages(json.data, "effort"));
      } else {
        alert("Failed to generate effort: " + (json.error?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Resources AI generation error:", err);
      alert("Failed to generate resource effort. Please try again.");
    } finally {
      setIsGeneratingResources(false);
    }
  };
  const generatePricingWithAI = async () => {
    if (isGeneratingPricing) return;
    setIsGeneratingPricing(true);
    try {
      const rHeader = resourceTable?.header?.length ? resourceTable.header : [];
      const effortCols = rHeader.map((h, ci) => ({ h, ci })).filter(({ h }) => isResourceEffortRoleColumn(h));
      const parallel = totalEffortParallelAdjusted(resourceTable.rows || [], effortCols);
      const raw = totalEffortRawRoleDays(resourceTable.rows || [], effortCols);
      const hasEffort = (resourceTable.rows || []).some((row) => effortCols.some(({ ci }) => String(row[ci] ?? "").trim() !== ""));
      const resourceSummary = JSON.stringify({
        // No effort entered yet => unknown (null), not "0 man-days".
        totalParallelManDays: hasEffort ? parallel : null,
        totalRawRoleDays: hasEffort ? raw : null,
        rowCount: (resourceTable.rows || []).length,
        roleColumns: effortCols.map(({ h }) => h),
      }, null, 2);
      const milestonesSummary = JSON.stringify(
        milestoneItems.map((m) => ({
          title: m.title,
          durationDays: m.duration,
          start: m.start,
          due: m.due,
        })),
        null,
        2
      );
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: msalAccount,
      });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/pricing/generate`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${tokenResponse.accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ resourceSummary, milestonesSummary }),
      });
      const json = await res.json();
      if (!res.ok || !json.success || !json.data) {
        alert("Failed to generate pricing: " + (json.error?.message || "Unknown error"));
        return;
      }
      const ts = Date.now();
      const fmt = (v) => formatInrForStorage(parseMoneyToNumber(String(v)));
      const mapped = mapPricingResponse(json.data, ts, fmt);
      if (mapped.priceEstimate.length) setPriceEstimate(mapped.priceEstimate);
      if (mapped.paymentTranches.length) {
        setPaymentTranches(mapped.paymentTranches);
      } else {
        // No tranches proposed: keep the user's, but their amounts must follow the new estimate.
        setPaymentTranches((pt) => recomputeTranchePayables(pt, computePriceEstimateGrandTotalOrNull(mapped.priceEstimate.length ? mapped.priceEstimate : priceEstimate)));
      }
      setPricingNotice(mapped.warnings);
      setPricingDisplayCurrency("INR");
    } catch (err) {
      console.error("Pricing AI generation error:", err);
      alert("Failed to generate pricing. Please try again.");
    } finally {
      setIsGeneratingPricing(false);
    }
  };
  const persistScopingMilestoneResource = async (tab) => {
    setAiDisabled(prev => ({ ...prev, [tab]: true }));
    const profile2 = project.profile || {};
    const newProfile = {
      ...profile2,
      scoping: scopingRows,
      milestones: milestoneItems,
      resources: resourceTable,
      raci: raciRows,
      priceEstimate,
      paymentTranches,
      pricingDisplayCurrency,
      inrPerUsd,
      scopingActiveSubTab: scopingTab,
      scopingContractAnnotations: contractAnnotations,
      scopingContractAppendSections: contractAppendSections,
    };
    onUpdateProfile(project.id, newProfile);
    const ok = saveScopingProfile && (await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile)));
    if (!ok) console.warn("Scoping data may not have persisted to the server; check network or permissions.");
    setSavedFlash(tab);
    setTimeout(() => setSavedFlash(null), 2e3);
  };
  const persistScopingWizardStep = async (stepKey, nextTabKey) => {
    const baseDraft = computeContractSections();
    setContractSections(baseDraft);
    const profile2 = project.profile || {};
    const wizard = { ...(profile2.scopingWizard || defaultScopingWizard()), [stepKey]: true };
    const scopingContractDraft = {
      ...baseDraft,
      raciSnapshot: raciRows,
      resourcesSnapshot: resourceTable,
      milestonesSnapshot: milestoneItems,
      scopingDocumentUpdatedAt: Date.now(),
    };
    const newProfile = {
      ...profile2,
      scoping: scopingRows,
      milestones: milestoneItems,
      resources: resourceTable,
      raci: raciRows,
      priceEstimate,
      paymentTranches,
      pricingDisplayCurrency,
      inrPerUsd,
      scopingWizard: wizard,
      scopingControlAnalysis: controlAnalysis ?? profile2.scopingControlAnalysis,
      scopingActiveSubTab: nextTabKey != null && nextTabKey !== undefined ? nextTabKey : scopingTab,
      scopingContractDraft,
      scopingContractAnnotations: contractAnnotations,
      scopingContractAppendSections: contractAppendSections,
    };
    onUpdateProfile(project.id, newProfile);
    const ok = saveScopingProfile && (await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile)));
    if (!ok) console.warn("Scoping wizard state may not have persisted to the server; check network or permissions.");
    setSavedFlash(stepKey);
    setTimeout(() => setSavedFlash(null), 2e3);
    if (nextTabKey) {
      setScopingTab(nextTabKey);
    }
  };
  const computeContractSections = () => {
    const attrs = project.profile?.attributes || [];
    const getAttr = (k) => (attrs.find((a) => a.key === k) || { value: "" }).value || "";
    let locationDetails = [];
    try {
      locationDetails = JSON.parse(getAttr("locationDetails") || "[]");
    } catch {
      locationDetails = [];
    }
    if (!Array.isArray(locationDetails)) locationDetails = [];
    const locSummary = buildLocationSummary(locationDetails);
    const milestoneSummary = milestoneItems.map(
      (m) => `${m.title}: ${m.start} - ${m.due} (${m.duration} days)`
    ).join("\n") || "See milestone map";
    const raciSummaryText = raciRows.slice(0, 5).map(
      (r) => r.slice(1, 4).join(" | ")
    ).join("\n") || "See RACI matrix";
    const effortLines = (resourceTable.rows || []).slice(0, 8).map(
      (r) => `${r[1] || ""} - ${r[2] || ""}: ${r.slice(3).filter(Boolean).join(", ")} man-days`
    ).join("\n") || "See resource requirements";
    const totalPrice = computePriceEstimateGrandTotalOrNull(priceEstimate);
    const priceSummaryLines = priceEstimate.map((pe) => {
      if (pe.children && pe.children.length) {
        return `${pe.expenseHead}:
` + pe.children.map((c) => formatPriceLine(c, "  ")).join("\n");
      }
      return formatPriceLine(pe);
    }).join("\n");
    const paymentLines = paymentTranches.map(
      (t) => formatTrancheLine(t)
    ).join("\n");
    return {
      effectiveDate: formatLongDateIst(Date.now()),
      ...buildContractParties({ getAttr, project, firm: advisoryFirm }),
      scopeOfWork: buildScopeOfWork({ project, getAttr, locationDetails, locSummary }),
      projectPlan: milestoneSummary,
      raciSummary: raciSummaryText,
      effortEstimate: effortLines,
      priceEstimateSummary: priceSummaryLines,
      paymentTerms: paymentLines,
      totalPayment: totalPrice === null ? "" : totalPrice.toLocaleString(),
      duration: buildDurationLabel(milestoneItems)
    };
  };
  const populateContractData = () => {
    setContractSections((prev) => ({ ...prev, ...computeContractSections() }));
  };
  useEffect(() => {
    populateContractData();
  }, []);
  useEffect(() => {
    const d = project.profile?.scopingContractDraft;
    if (d && typeof d === "object") {
      const { raciSnapshot, resourcesSnapshot, milestonesSnapshot, scopingDocumentUpdatedAt, ...rest } = d;
      setContractSections((prev) => ({ ...prev, ...stripLegacyFabricatedContractValues(rest, { dropBlankParties: true }) }));
    }
  }, [project.id, project.profile?.scopingContractDraft]);
  // Party fields come from real records (Company attributes + the tenant's AdvisoryFirm); fill
  // only blanks so a Lead Advisor's edits are kept and nothing is ever invented.
  const liveContractParties = useMemo(() => {
    const attrs = project.profile?.attributes || [];
    const getAttr = (k) => (attrs.find((a) => a.key === k) || { value: "" }).value || "";
    return buildContractParties({ getAttr, project, firm: advisoryFirm });
  }, [project.profile?.attributes, project.companyName, advisoryFirm]);
  useEffect(() => {
    setContractSections((prev) => fillBlankParties(prev, liveContractParties));
  }, [liveContractParties]);
  const contractPartyGaps = missingContractParties(contractSections);
  // Advisory firm name for client documents: the tenant's AdvisoryFirm record, else what the saved
  // contract draft carries; blank (header omitted) when neither exists.
  const profilingAdvisorName = (advisoryFirm?.firmName || contractSections.advisorName || "").trim();
  const persistScopingContractDraft = async (sectionsOverride) => {
    setContractDraftSaving(true);
    try {
      const profile2 = project.profile || {};
      // advisorLogoUrl is an expiring SAS URL; it is re-derived on load, never persisted.
      const { advisorLogoUrl: _expiringLogo, ...sectionSource } = sectionsOverride ?? contractSections;
      const scopingContractDraft = {
        ...sectionSource,
        raciSnapshot: raciRows,
        resourcesSnapshot: resourceTable,
        milestonesSnapshot: milestoneItems,
        scopingDocumentUpdatedAt: Date.now(),
      };
      const newProfile = {
        ...profile2,
        scoping: scopingRows,
        milestones: milestoneItems,
        resources: resourceTable,
        raci: raciRows,
        priceEstimate,
        paymentTranches,
        pricingDisplayCurrency,
        inrPerUsd,
        scopingActiveSubTab: "contract",
        scopingContractDraft,
        scopingContractAnnotations: contractAnnotations,
        scopingContractAppendSections: contractAppendSections,
      };
      onUpdateProfile(project.id, newProfile);
      if (saveScopingProfile) {
        const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
        if (!ok) console.warn("Contract draft may not have persisted to the server; check network or permissions.");
        return ok;
      }
      return true;
    } finally {
      setContractDraftSaving(false);
    }
  };
  const persistContractAnnotations = async (nextList) => {
    const profile2 = project.profile || {};
    const newProfile = {
      ...profile2,
      scopingContractAnnotations: nextList,
      scopingContractAppendSections: contractAppendSections,
    };
    onUpdateProfile(project.id, newProfile);
    if (saveScopingProfile) {
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) console.warn("Contract comments may not have persisted to the server.");
      return ok;
    }
    return true;
  };
  const handleContractAnnotationPost = async ({ anchor, body, kind }) => {
    const authorRole = user?.role || "UNKNOWN";
    const ann = createContractAnnotation({ authorRole, body, anchor, kind: kind || "comment" });
    const next = [...contractAnnotations, ann];
    setContractAnnotations(next);
    setContractAnnotationFocusKey(stableAnchorKey(anchor));
    await persistContractAnnotations(next);
  };
  const handleContractAnnotationReply = async (annotationId, text) => {
    const authorRole = user?.role || "UNKNOWN";
    const next = addReplyToAnnotation(contractAnnotations, annotationId, { authorRole, body: text });
    setContractAnnotations(next);
    await persistContractAnnotations(next);
  };
  const handleContractAnnotationResolve = async (annotationId) => {
    const role = user?.role || "UNKNOWN";
    const next = resolveAnnotation(contractAnnotations, annotationId, role);
    setContractAnnotations(next);
    await persistContractAnnotations(next);
  };
  const analyzeControls = async () => {
    if (!project.companyId || isAnalyzingControls) return;
    setIsAnalyzingControls(true);
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: msalAccount,
      });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/companies/${project.companyId}/analyze-controls`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId: project.id }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        const data = json.data;
        setControlAnalysis(data);
        const analysedTotalControls = typeof data?.totalControls === 'number' && data.totalControls > 0 ? data.totalControls : null;
        const prevRows = scopingRows;
        let nextScoping = prevRows;
        if (prevRows && prevRows.length > 0) {
          nextScoping = [...prevRows];
          nextScoping[0] = {
            ...nextScoping[0],
            ...(analysedTotalControls != null ? { totalControls: analysedTotalControls } : {}),
          };
          setScopingRows(nextScoping);
        }
        const profile2 = project.profile || {};
        const newProfile = {
          ...profile2,
          scoping: nextScoping,
          scopingControlAnalysis: data,
        };
        onUpdateProfile(project.id, newProfile);
        if (saveScopingProfile) {
          const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
          if (!ok) console.warn("Control analysis may not have persisted to the server.");
        }
      } else {
        console.error('Control analysis failed:', json.error?.message);
      }
    } catch (err) {
      console.error('Control analysis error:', err);
    } finally {
      setIsAnalyzingControls(false);
    }
  };
  const openScopingReview = (target) => {
    setScopingReviewTarget(target);
    setScopingReviewComment("");
    setIsScopingReviewOpen(true);
  };
  const submitScopingReview = async () => {
    if (!scopingReviewTarget) return;
    if (!project?.id) {
      alert("Missing project. Please reopen the project and try again.");
      return;
    }
    if (!updateScopingStatusAPI) {
      alert("Unable to update scoping status. Please refresh and sign in again.");
      return;
    }
    if (scopingReviewTarget === "Under Review" && contractPartyGaps.length) {
      message.error(`Cannot send for review. Missing contract details: ${contractPartyGaps.join(", ")}.`);
      return;
    }
    const comments = ((project.profile || {}).comments || []).concat([{
      actorRole: user?.role || "UNKNOWN",
      text: scopingReviewComment || "",
      timestamp: Date.now()
    }]);
    try {
      await updateScopingStatusAPI(project.id, scopingReviewTarget, scopingReviewComment);
    } catch (err) {
      message.error(userMessageForError(err, "Could not update scoping status. Please try again."));
      return;
    }
    onUpdateProfile(project.id, { ...project.profile || {}, comments });
    setScopingStatus(scopingReviewTarget);
    setIsScopingReviewOpen(false);
  };
  const openReviewModal = (status) => {
    setReviewTargetStatus(status);
    setReviewComment("");
    setIsReviewModalOpen(true);
  };
  const openDocModal = async ({ viewOnly = false } = {}) => {
    const attrs = profile.attributes || [];
    const get = (key) => (attrs.find((a) => a.key === key) || { value: "" }).value || "";
    const locCount = parseInt(get("additionalLocations")) || 0;
    const hqCity = get("hqCity") || "";
    const locationStr = hqCity ? `${hqCity}${locCount > 0 ? ` + ${locCount} locations` : ""}` : `${locCount || 1} locations`;

    const locSummary = formatLocationDetailsSummary(get("locationDetails"));
    const appsRaw = get("apps") || get("productsAndServices") || "-";
    const appsDisplay = String(appsRaw).length > 600 ? `${String(appsRaw).slice(0, 600)}…` : String(appsRaw);
    const highLevelRows = [
      { param: "Locations", value: locSummary !== "-" ? `${locationStr} - ${locSummary}` : locationStr },
      { param: "Headcount (range)", value: get("companyHeadcount") || "-" },
      { param: "IT assets", value: formatItProfileField(get("servers")) },
      { param: "Cloud", value: formatItProfileField(get("cloudServices")) },
      { param: "Applications", value: appsDisplay },
    ];
    const cert = project.certification || (project.standards && project.standards[0]) || "";
    const baseSections = {
      execSummary: "",
      orgStructure: "",
      highLevel: JSON.stringify(highLevelRows),
      gics: get("gicsIndustry"),
      applicable: JSON.stringify((project.standards || (cert ? [cert] : [])).map((s, i) => ({
        sr: i + 1, title: s, reqCount: "--", controlCount: "--"
      }))),
      appendSections: [],
    };
    const savedDraft = (project.profile || {}).profilingDocumentDraft;
    if (savedDraft && typeof savedDraft === "object" && !viewOnly) {
      setDocSections({
        ...baseSections,
        execSummary: savedDraft.execSummary ?? baseSections.execSummary,
        orgStructure: savedDraft.orgStructure ?? baseSections.orgStructure,
        highLevel: savedDraft.highLevel ?? baseSections.highLevel,
        gics: savedDraft.gics ?? baseSections.gics,
        applicable: savedDraft.applicable ?? baseSections.applicable,
        appendSections: Array.isArray(savedDraft.appendSections) ? savedDraft.appendSections : [],
      });
    } else {
      setDocSections(baseSections);
    }
    setIsViewOnlyDoc(viewOnly);
    setIsDocEditMode(false);

    if (viewOnly) {
      const saved = (profile.evidence || []).find(e => e.name && e.name.includes("Onboarding"));
      if (saved && saved.versions && saved.versions.length > 0) {
        try {
          const parsed = JSON.parse(saved.versions[saved.versions.length - 1].content);
          if (parsed.execSummary != null) {
            setDocSections((prev) => ({
              ...prev,
              ...parsed,
              appendSections: Array.isArray(parsed.appendSections) ? parsed.appendSections : prev.appendSections || [],
            }));
            return;
          }
        } catch { /* not JSON, fall through */ }
      }
      return;
    }

    setIsDocGenerating(true);
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: msalAccount,
      });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/companies/${project.companyId}/generate-document`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          certificationTarget: project.certification || (project.standards && project.standards[0]) || '',
        }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        const rows = Array.isArray(json.data.applicableRequirements) ? json.data.applicableRequirements : null;
        // applicableRequirementsMissing (or an empty list): the AI identified nothing. Say so; never keep a canned table.
        setDocApplicableMissing(json.data.applicableRequirementsMissing === true || !rows || rows.length === 0);
        setDocSections(prev => ({
          ...prev,
          execSummary: json.data.executiveSummary || prev.execSummary,
          orgStructure: json.data.orgStructure || prev.orgStructure,
          ...(rows && rows.length ? { applicable: JSON.stringify(rows.map((r, i) => ({
            sr: r.sr != null ? Number(r.sr) : i + 1,
            title: r.title || "",
            reqCount: r.reqCount != null ? String(r.reqCount) : "",
            controlCount: r.controlCount != null ? String(r.controlCount) : "",
          }))) } : {}),
        }));
      } else {
        alert(json.error?.message || 'Could not generate document with AI. Check GROQ_API_KEY and try again.');
      }
    } catch (err) {
      console.error('Doc generation error:', err);
    } finally {
      setIsDocGenerating(false);
    }
  };
  const persistProfilingDocumentDraft = async () => {
    const profile2 = project.profile || {};
    const newProfile = { ...profile2, profilingDocumentDraft: docSections };
    onUpdateProfile(project.id, newProfile);
    if (saveScopingProfile) {
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) console.warn("Company profile draft may not have persisted to the server.");
      return ok;
    }
    return true;
  };
  const saveDocAndFinalize = async () => {
    const content = JSON.stringify(docSections);
    const existingEvidence = (profile.evidence || []);
    const existingIdx = existingEvidence.findIndex(e => e.name && e.name.includes("Onboarding"));
    let updatedEvidence;
    if (existingIdx >= 0) {
      updatedEvidence = [...existingEvidence];
      updatedEvidence[existingIdx] = {
        ...updatedEvidence[existingIdx],
        versions: [...(updatedEvidence[existingIdx].versions || []), {
          content,
          createdBy: user?.role || "UNKNOWN",
          timestamp: Date.now()
        }]
      };
    } else {
      updatedEvidence = [...existingEvidence, {
        id: `e-${Date.now()}`,
        name: `Onboarding - ${project.companyName}`,
        versions: [{
          content,
          createdBy: user?.role || "UNKNOWN",
          timestamp: Date.now()
        }]
      }];
    }
    const updatedProfile = {
      ...profile || {},
      evidence: updatedEvidence
    };
    // Only a server-accepted Finalized may touch local state or advance the step.
    try {
      if (!updateProfilingStatusAPI || !project?.id) throw new Error("missing status API");
      await updateProfilingStatusAPI(project.id, "Finalized", "Profile finalized");
    } catch (err) {
      message.error(userMessageForError(err, "Could not finalize the profile. Please try again."));
      return;
    }
    onUpdateProfile(project.id, updatedProfile);
    setIsDocEditMode(false);
    setIsViewOnlyDoc(false);
    setActiveStepId(2);
    // Bug #5 fix (RACI refresh-to-Profiling). `getWorkspaceStepForProject` trusts a
    // persisted `profile.workspaceActiveStep` over the live profilingStatus fallback on
    // every future page load — a step transition that only sets local React state (as
    // this one did) leaves the server-side restore cursor stale or unset, so any later
    // refresh from Scoping/RACI drops the user back to whichever step was last actually
    // persisted (Profiling, step 1, if nothing ever was). Every intentional forward step
    // change must persist, mirroring handleSelectStep's setActiveStepId+persist pairing.
    void persistWorkspaceActiveStep(2);
  };
  const submitReviewWithComment = async (status) => {
    const comments = (profile.comments || []).concat([{ actorRole: user?.role || "UNKNOWN", text: reviewComment || "", timestamp: Date.now() }]);
    const updatedAttrs = (profile.attributes || []).map((a) => {
      if (a.lastModifiedBy && a.lastModifiedBy !== user?.role) {
        const copy = { ...a };
        copy.isPendingReview = false;
        delete copy.lastModifiedBy;
        return copy;
      }
      return a;
    });
    // Persist status transition to DB with snapshot audit; local state changes only if the server accepted.
    try {
      if (!updateProfilingStatusAPI || !project?.id) throw new Error("missing status API");
      await updateProfilingStatusAPI(project.id, status, reviewComment || '');
    } catch (err) {
      message.error(userMessageForError(err, "Could not update the profile status. Please try again."));
      return;
    }
    onUpdateProfile(project.id, { ...profile || {}, comments, attributes: updatedAttrs });

    setIsReviewModalOpen(false);
    setReviewComment("");
  };
  const handleAddTableRow = (attrKey) => {
    if (!canEdit) return;
    const nextAttrs = (profile.attributes || []).map((a) => {
      if (a.key === attrKey && a.format === "table") {
        const headers = a.tableHeaders || ["Title", "Location"];
        const newRow = headers.reduce((acc, h) => ({ ...acc, [h]: "" }), { id: Date.now().toString() });
        return { ...a, tableData: [...a.tableData || [], newRow] };
      }
      return a;
    });
    markAttributeModified(attrKey, nextAttrs);
  };
  const handleUpdateTableRow = (attrKey, rowId, field, val) => {
    if (!canEdit) return;
    const nextAttrs = (profile.attributes || []).map((a) => {
      if (a.key === attrKey && a.format === "table") {
        return {
          ...a,
          tableData: (a.tableData || []).map((row) => row.id === rowId ? { ...row, [field]: val } : row)
        };
      }
      return a;
    });
    markAttributeModified(attrKey, nextAttrs);
  };
  const handleDeleteTableRow = (attrKey, rowId) => {
    if (!canEdit) return;
    const nextAttrs = (profile.attributes || []).map((a) => {
      if (a.key === attrKey && a.format === "table") {
        return {
          ...a,
          tableData: (a.tableData || []).filter((row) => row.id !== rowId)
        };
      }
      return a;
    });
    markAttributeModified(attrKey, nextAttrs);
  };
  const renderTableField = (attr) => {
    const headers = attr.tableHeaders || ["Title", "Location"];
    const isModified = attr.isPendingReview;
    const isChangedByOther = !!attr.lastModifiedBy && attr.lastModifiedBy !== user?.role;
    return /* @__PURE__ */ jsxs("div", {
      className: `col-span-full space-y-4 bg-[color:var(--color-bg-white)] p-6 md:p-8 rounded-[2rem] border shadow-xl relative overflow-hidden group/table animate-in fade-in zoom-in-95 duration-500 transition-all ${isModified ? "border-[color:var(--color-warn-line)] ring-4 ring-[color:color-mix(in_srgb,var(--color-warn-line)_50%,transparent)]" : "border-[color:var(--color-border)]"} ${isChangedByOther ? "ring-2 ring-[color:var(--color-info-line)] bg-[color:color-mix(in_srgb,var(--color-accent-tint)_5%,transparent)]" : ""}`, children: [
      /* @__PURE__ */ jsx("div", { className: "absolute top-0 right-0 w-[300px] h-[300px] bg-[color:color-mix(in_srgb,var(--color-accent-fill)_5%,transparent)] blur-[100px] rounded-full pointer-events-none" }),
      /* @__PURE__ */ jsxs("div", {
        className: "flex justify-between items-center relative z-10", children: [
        /* @__PURE__ */ jsxs("div", {
          className: "space-y-1", children: [
          /* @__PURE__ */ jsxs("div", {
            className: "flex items-center gap-3", children: [
            /* @__PURE__ */ jsx("h4", { className: "text-[11px] font-black uppercase tracking-[0.4em] text-[color:var(--color-accent)]", children: attr.label }),
              isModified && /* @__PURE__ */ jsx("span", { className: "px-2 py-0.5 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border border-[color:var(--color-warn-line)] text-[7px] font-black uppercase tracking-[0.3em] rounded-md shadow-sm", children: "CHANGED" })
            ]
          }),
          /* @__PURE__ */ jsx("div", { className: "h-1 w-12 bg-[color:color-mix(in_srgb,var(--color-accent-fill)_20%,transparent)] rounded-full" })
          ]
        }),
        /* @__PURE__ */ jsx("div", {
          className: "flex gap-2", children: canEdit && /* @__PURE__ */ jsxs(Fragment, {
            children: [
          /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => handleAddTableRow(attr.key),
                className: "px-3 py-1 bg-[color:var(--color-bg-alt)] hover:brightness-110 hover:text-white text-[8px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] border border-[color:var(--color-border)] rounded-lg transition-all active:scale-95",
                children: "+ Row"
              }
            ),
          /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => handleDeleteAttr(attr.key),
                className: "p-1.5 bg-[color:var(--color-bg-alt)] hover:brightness-95 hover:text-white text-[color:var(--color-danger-fg)] border border-[color:var(--color-border)] rounded-lg transition-all shadow-sm",
                children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" }) })
              }
            )
            ]
          })
        })
        ]
      }),
      /* @__PURE__ */ jsx("div", {
        className: "relative z-10 overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)]", children: /* @__PURE__ */ jsxs("table", {
          className: "w-full text-left", children: [
        /* @__PURE__ */ jsx("thead", {
            children: /* @__PURE__ */ jsxs("tr", {
              className: "bg-[color:color-mix(in_srgb,var(--color-bg-alt)_50%,transparent)] text-[9px] font-black uppercase tracking-[0.3em] text-[color:var(--color-text-muted)]", children: [
                headers.map((h) => /* @__PURE__ */ jsx("th", { className: "px-6 py-4", children: h }, h)),
                canEdit && /* @__PURE__ */ jsx("th", { className: "px-6 py-4 text-right w-16", children: "Action" })
              ]
            })
          }),
        /* @__PURE__ */ jsx("tbody", {
            className: "divide-y divide-[color:var(--color-border)]", children: (attr.tableData || []).map((row) => /* @__PURE__ */ jsxs("tr", {
              className: "hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)] transition-all group/row", children: [
                headers.map((h) => /* @__PURE__ */ jsx("td", {
                  className: "px-6 py-4", children: (h === "Country" || h === "Location") ? /* @__PURE__ */ jsxs(
                    "select",
                    {
                      value: row[h] || "",
                      disabled: !canEdit,
                      onChange: (e) => handleUpdateTableRow(attr.key, row.id, h, e.target.value),
                      className: "bg-transparent border-b border-[color:var(--color-border)] w-full outline-none text-[color:var(--color-text-hero)] font-medium py-1.5 focus:border-[color:var(--color-accent)] transition-all text-sm disabled:border-transparent disabled:text-[color:var(--color-text-muted)]",
                      children: [
                        /* @__PURE__ */ jsx("option", { value: "", disabled: true, className: "text-[color:var(--color-text-muted)]", children: "Select..." }),
                        ...COUNTRIES.map((country) => /* @__PURE__ */ jsx("option", { value: country, children: country }, country))
                      ]
                    }
                  ) : /* @__PURE__ */ jsx(
                    "input",
                    {
                      type: /^count$/i.test(h.trim()) ? "number" : "text",
                      value: row[h] || "",
                      disabled: !canEdit,
                      onChange: (e) => handleUpdateTableRow(attr.key, row.id, h, e.target.value),
                      placeholder: "Define...",
                      className: "bg-transparent border-b border-[color:var(--color-border)] w-full outline-none text-[color:var(--color-text-hero)] font-medium py-1.5 focus:border-[color:var(--color-accent)] transition-all text-sm placeholder:text-[color:var(--color-text-muted)] disabled:border-transparent disabled:text-[color:var(--color-text-muted)]"
                    }
                  )
                }, h)),
                canEdit && /* @__PURE__ */ jsx("td", {
                  className: "px-6 py-4 text-right", children: /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => handleDeleteTableRow(attr.key, row.id),
                      className: "p-1 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-all opacity-0 group-hover/row:opacity-100",
                      children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M6 18L18 6M6 6l12 12" }) })
                    }
                  )
                })
              ]
            }, row.id))
          })
          ]
        })
      })
      ]
    }, attr.key);
  };
  const renderColorPaletteField = (attr) => {
    const raw = attr.value ?? "";
    let primaryRaw = raw;
    let secondaryRaw = "";
    
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        primaryRaw = parsed.primary || "";
        secondaryRaw = parsed.secondary || "";
      }
    } catch {
      primaryRaw = raw;
    }

    const primaryNormalized = normalizeCompanyColorHex(String(primaryRaw));
    const secondaryNormalized = normalizeCompanyColorHex(String(secondaryRaw));
    
    const primaryPickerValue = primaryNormalized || "#94a3b8";
    const secondaryPickerValue = secondaryNormalized || "#94a3b8";

    const updateColor = (type, hex) => {
      const newVal = JSON.stringify({
        primary: type === 'primary' ? hex : primaryRaw,
        secondary: type === 'secondary' ? hex : secondaryRaw
      });
      handleUpdateAttr(attr.key, newVal);
    };

    return /* @__PURE__ */ jsxs("div", {
      className: "space-y-4",
      children: [
        /* @__PURE__ */ jsx("button", {
          type: "button",
          onClick: () => setIsColorPaletteExpanded(!isColorPaletteExpanded),
          className: "w-full flex items-center justify-between px-4 py-3 bg-[color:var(--color-surface-sunken)] hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_50%,transparent)] border border-[color:var(--color-border)] rounded-xl transition-all",
          children: /* @__PURE__ */ jsxs("div", {
            className: "flex items-center justify-between w-full",
            children: [
              /* @__PURE__ */ jsxs("div", {
                className: "flex items-center gap-3",
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "flex -space-x-2",
                    children: [
                      /* @__PURE__ */ jsx("div", { className: "w-6 h-6 rounded-full border-2 border-[color:var(--color-bg-white)] shadow-sm", style: { backgroundColor: primaryNormalized || "#94a3b8" } }),
                      /* @__PURE__ */ jsx("div", { className: "w-6 h-6 rounded-full border-2 border-[color:var(--color-bg-white)] shadow-sm", style: { backgroundColor: secondaryNormalized || "#94a3b8" } }),
                    ]
                  }),
                  /* @__PURE__ */ jsx("span", {
                    className: "text-[11px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]",
                    children: isColorPaletteExpanded ? "Hide Palette" : "Configure Palette"
                  })
                ]
              }),
              /* @__PURE__ */ jsx("svg", {
                className: `w-4 h-4 text-[color:var(--color-text-muted)] transition-transform duration-300 ${isColorPaletteExpanded ? 'rotate-180' : ''}`,
                fill: "none", stroke: "currentColor", viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 9l-7 7-7-7" })
              })
            ]
          })
        }),
        isColorPaletteExpanded && /* @__PURE__ */ jsxs("div", {
          className: "space-y-6 pt-2 animate-in fade-in slide-in-from-top-2",
          children: [
            /* @__PURE__ */ jsxs("div", {
              className: "space-y-3",
              children: [
            /* @__PURE__ */ jsx("label", {
              className: "text-[10px] font-black uppercase tracking-[0.2em] text-[color:var(--color-text-muted)] block",
              children: "Primary Brand Color",
            }),
            /* @__PURE__ */ jsx("input", {
              ref: companyColorPickerRef,
              type: "color",
              value: primaryPickerValue,
              disabled: !canEdit,
              className: "sr-only",
              tabIndex: -1,
              onChange: (e) => updateColor('primary', e.target.value),
            }),
            /* @__PURE__ */ jsxs("button", {
              type: "button",
              disabled: !canEdit,
              onClick: () => {
                if (!canEdit) return;
                companyColorPickerRef.current?.click();
              },
              className: `w-full rounded-xl border-2 overflow-hidden text-left transition-all ${canEdit ? "border-[color:var(--color-info-line)] hover:ring-4 hover:ring-[color:color-mix(in_srgb,var(--color-accent)_15%,transparent)] focus:outline-none focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] cursor-pointer" : "border-[color:var(--color-border)] opacity-70 cursor-not-allowed"}`,
              children: [
                /* @__PURE__ */ jsx("div", {
                  className: "h-14 w-full",
                  style: { backgroundColor: primaryNormalized || "var(--color-bg-alt)" },
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center justify-between gap-2 px-3 py-2.5 bg-[color:var(--color-surface-sunken)] border-t border-[color:var(--color-border)]",
                  children: [
                    /* @__PURE__ */ jsx("span", {
                      className: "text-[13px] font-semibold text-[color:var(--color-text-hero)] truncate",
                      children: primaryNormalized || "Click to choose primary color",
                    }),
                    canEdit && /* @__PURE__ */ jsx("span", {
                      className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-accent)] shrink-0",
                      children: "Color picker",
                    }),
                  ],
                }),
              ],
            }),
            /* @__PURE__ */ jsxs("div", {
              children: [
                /* @__PURE__ */ jsx("p", {
                  className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2",
                  children: "Quick presets",
                }),
                /* @__PURE__ */ jsx("div", {
                  className: "flex flex-wrap gap-2",
                  children: COMPANY_COLOR_PRESETS.map((hex) =>
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        type: "button",
                        disabled: !canEdit,
                        onClick: () => updateColor('primary', hex),
                        className: `h-9 w-9 shrink-0 rounded-lg border-2 transition-all ${primaryNormalized === hex ? "border-[color:var(--color-accent)] ring-2 ring-[color:var(--color-info-line)] scale-105" : "border-[color:var(--color-border)] shadow-sm hover:scale-110"}`,
                        style: { backgroundColor: hex },
                        title: hex,
                      },
                      hex
                    )
                  ),
                }),
              ],
            }),
            /* @__PURE__ */ jsxs("div", {
              className: "space-y-1",
              children: [
                /* @__PURE__ */ jsx("label", {
                  className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]",
                  children: "Hex value",
                }),
                /* @__PURE__ */ jsx("input", {
                  type: "text",
                  value: primaryRaw,
                  disabled: !canEdit,
                  onChange: (e) => updateColor('primary', e.target.value),
                  placeholder: "#2563eb",
                  spellCheck: false,
                  className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold font-mono outline-none transition-all placeholder:text-[color:var(--color-text-muted)] ${canEdit ? "bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)] cursor-text" : "bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed"}`,
                }),
              ],
            }),
          ],
        }),
        /* @__PURE__ */ jsxs("div", {
          className: "space-y-3 pt-4 border-t border-[color:var(--color-border)]",
          children: [
            /* @__PURE__ */ jsx("label", {
              className: "text-[10px] font-black uppercase tracking-[0.2em] text-[color:var(--color-text-muted)] block",
              children: "Secondary Brand Color",
            }),
            /* @__PURE__ */ jsx("input", {
              ref: companySecondaryColorPickerRef,
              type: "color",
              value: secondaryPickerValue,
              disabled: !canEdit,
              className: "sr-only",
              tabIndex: -1,
              onChange: (e) => updateColor('secondary', e.target.value),
            }),
            /* @__PURE__ */ jsxs("button", {
              type: "button",
              disabled: !canEdit,
              onClick: () => {
                if (!canEdit) return;
                companySecondaryColorPickerRef.current?.click();
              },
              className: `w-full rounded-xl border-2 overflow-hidden text-left transition-all ${canEdit ? "border-[color:var(--color-info-line)] hover:ring-4 hover:ring-[color:color-mix(in_srgb,var(--color-accent)_15%,transparent)] focus:outline-none focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] cursor-pointer" : "border-[color:var(--color-border)] opacity-70 cursor-not-allowed"}`,
              children: [
                /* @__PURE__ */ jsx("div", {
                  className: "h-14 w-full",
                  style: { backgroundColor: secondaryNormalized || "var(--color-bg-alt)" },
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center justify-between gap-2 px-3 py-2.5 bg-[color:var(--color-surface-sunken)] border-t border-[color:var(--color-border)]",
                  children: [
                    /* @__PURE__ */ jsx("span", {
                      className: "text-[13px] font-semibold text-[color:var(--color-text-hero)] truncate",
                      children: secondaryNormalized || "Click to choose secondary color",
                    }),
                    canEdit && /* @__PURE__ */ jsx("span", {
                      className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-accent)] shrink-0",
                      children: "Color picker",
                    }),
                  ],
                }),
              ],
            }),
            /* @__PURE__ */ jsxs("div", {
              children: [
                /* @__PURE__ */ jsx("p", {
                  className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2",
                  children: "Quick presets",
                }),
                /* @__PURE__ */ jsx("div", {
                  className: "flex flex-wrap gap-2",
                  children: COMPANY_COLOR_PRESETS.map((hex) =>
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        type: "button",
                        disabled: !canEdit,
                        onClick: () => updateColor('secondary', hex),
                        className: `h-9 w-9 shrink-0 rounded-lg border-2 transition-all ${secondaryNormalized === hex ? "border-[color:var(--color-accent)] ring-2 ring-[color:var(--color-info-line)] scale-105" : "border-[color:var(--color-border)] shadow-sm hover:scale-110"}`,
                        style: { backgroundColor: hex },
                        title: hex,
                      },
                      hex
                    )
                  ),
                }),
              ],
            }),
            /* @__PURE__ */ jsxs("div", {
              className: "space-y-1",
              children: [
                /* @__PURE__ */ jsx("label", {
                  className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]",
                  children: "Hex value",
                }),
                /* @__PURE__ */ jsx("input", {
                  type: "text",
                  value: secondaryRaw,
                  disabled: !canEdit,
                  onChange: (e) => updateColor('secondary', e.target.value),
                  placeholder: "#2563eb",
                  spellCheck: false,
                  className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold font-mono outline-none transition-all placeholder:text-[color:var(--color-text-muted)] ${canEdit ? "bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)] cursor-text" : "bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed"}`,
                }),
              ],
            }),
          ],
        }),
          ],
        }),
      ],
    });
  };
  const renderFontPickerField = (attr) => {
    const raw = attr.value ?? "";
    const trimmed = String(raw).trim();
    const match = COMPANY_FONT_OPTIONS.find((o) => o.value === trimmed);
    const previewFamily = trimmed === "" || trimmed === "inherit" ? "inherit" : trimmed;
    const summaryLabel = match ? match.label : trimmed ? "Custom font" : "Choose a font style";
    return /* @__PURE__ */ jsxs("div", {
      className: "space-y-3",
      children: [
        /* @__PURE__ */ jsxs("details", {
          ref: companyFontDetailsRef,
          className: `rounded-xl border-2 border-[color:var(--color-info-line)] bg-[color:var(--color-bg-white)] overflow-hidden transition-all open:ring-4 open:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] ${!canEdit ? "pointer-events-none opacity-80" : ""}`,
          children: [
            /* @__PURE__ */ jsxs("summary", {
              className: `list-none cursor-pointer px-3 py-2.5 flex items-center justify-between gap-3 [&::-webkit-details-marker]:hidden ${canEdit ? "hover:bg-[color:color-mix(in_srgb,var(--color-bg-alt)_80%,transparent)]" : ""}`,
              onClick: (e) => {
                if (!canEdit) e.preventDefault();
              },
              children: [
                /* @__PURE__ */ jsxs("div", {
                  className: "min-w-0 flex-1",
                  children: [
                    /* @__PURE__ */ jsx("span", {
                      className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] block mb-0.5",
                      children: "Preview",
                    }),
                    /* @__PURE__ */ jsx("span", {
                      className: "text-[15px] font-semibold text-[color:var(--color-text-hero)] truncate block",
                      style: { fontFamily: previewFamily },
                      children: summaryLabel,
                    }),
                  ],
                }),
                /* @__PURE__ */ jsx("span", {
                  className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-accent)] shrink-0",
                  children: "Fonts",
                }),
              ],
            }),
            /* @__PURE__ */ jsx("div", {
              className: "border-t border-[color:var(--color-border)] max-h-56 overflow-y-auto custom-scrollbar p-2 space-y-1",
              children: COMPANY_FONT_OPTIONS.map((opt) =>
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    disabled: !canEdit,
                    onClick: () => {
                      if (!canEdit) return;
                      handleUpdateAttr(attr.key, opt.value);
                      if (companyFontDetailsRef.current) companyFontDetailsRef.current.open = false;
                    },
                    className: `w-full text-left px-3 py-2.5 rounded-xl text-[14px] transition-all border ${trimmed === opt.value || (!trimmed && opt.value === "inherit") ? "border-[color:var(--color-accent)] bg-[color:color-mix(in_srgb,var(--color-accent-tint)_80%,transparent)] ring-1 ring-[color:var(--color-info-line)]" : "border-transparent hover:bg-[color:var(--color-bg-alt)] hover:border-[color:var(--color-border)]"} ${!canEdit ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`,
                    style: {
                      fontFamily: opt.value === "inherit" ? "ui-sans-serif, system-ui, sans-serif" : opt.value,
                    },
                    children: opt.label,
                  },
                  opt.value
                )
              ),
            }),
          ],
        }),
        /* @__PURE__ */ jsxs("div", {
          className: "space-y-1",
          children: [
            /* @__PURE__ */ jsx("label", {
              className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]",
              children: "Custom CSS font-family (optional)",
            }),
            /* @__PURE__ */ jsx("input", {
              type: "text",
              value: raw,
              disabled: !canEdit,
              onChange: (e) => handleUpdateAttr(attr.key, e.target.value),
              placeholder: '"Inter", system-ui, sans-serif',
              spellCheck: false,
              className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold outline-none transition-all placeholder:text-[color:var(--color-text-muted)] ${canEdit ? "bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)] cursor-text" : "bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed"}`,
            }),
          ],
        }),
      ],
    });
  };
  
  const renderLocationSelectField = (attr) => {
    const isModified = attr.isPendingReview;
    return /* @__PURE__ */ jsxs("div", {
      className: `space-y-2 group relative bg-[color:var(--color-bg-white)] p-5 rounded-[1.5rem] border-2 shadow-sm hover:shadow-xl transition-all duration-500 animate-in fade-in slide-in-from-bottom-2 ${isModified ? "border-[color:var(--color-warn-fg)] ring-4 ring-[color:color-mix(in_srgb,var(--color-warn-line)_60%,transparent)] bg-[color:color-mix(in_srgb,var(--color-warn-bg)_10%,transparent)]" : "border-[color:var(--color-border)]"}`, children: [
      /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-[0.2em] text-[color:var(--color-text-muted)]", children: attr.label }),
          isModified && /* @__PURE__ */ jsx("span", { className: "px-2 py-0.5 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border border-[color:var(--color-warn-line)] text-[7px] font-black uppercase tracking-[0.3em] rounded-md shadow-sm", children: "CHANGED" })
        ] }),
        canEdit && /* @__PURE__ */ jsx("button", { onClick: () => handleDeleteAttr(attr.key), className: "p-1.5 bg-[color:var(--color-bg-alt)] hover:brightness-95 hover:text-white text-[color:var(--color-danger-fg)] border border-[color:var(--color-border)] rounded-lg transition-all shadow-sm opacity-0 group-hover:opacity-100", children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" }) }) })
      ] }),
      /* @__PURE__ */ jsxs("select", {
        value: attr.value || "",
        disabled: !canEdit,
        onChange: (e) => { const next = (profile.attributes || []).map((a) => a.key === attr.key ? { ...a, value: e.target.value, isPendingReview: true, lastModifiedBy: user?.name || "Authorized Member" } : a); markAttributeModified(attr.key, next); },
        className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] disabled:opacity-50",
        children: [
          /* @__PURE__ */ jsx("option", { value: "", disabled: true, children: "Select country\u2026" }),
          ...COUNTRIES.map((c) => /* @__PURE__ */ jsx("option", { value: c, children: c }, c))
        ]
      })
      ]
    });
  };
  const parseCsvText = (text) => {
    const lines = (text || "").split("\n").filter((l) => l.trim());
    if (lines.length === 0) return { headers: [], rows: [] };
    const split = (line) => {
      const result = []; let cur = ""; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') { inQ = !inQ; continue; }
        if (c === ',' && !inQ) { result.push(cur.trim()); cur = ""; continue; }
        cur += c;
      }
      result.push(cur.trim());
      return result;
    };
    const headers = split(lines[0]);
    const rows = lines.slice(1).map((l) => split(l));
    return { headers, rows };
  };
  const renderCsvUploadField = (attr) => {
    const isModified = attr.isPendingReview;
    const hasFile = !!attr.value;
    let parsedInfo = null;
    if (hasFile) { try { parsedInfo = JSON.parse(attr.value); } catch {} }
    const csvData = parsedInfo?.csvContent ? parseCsvText(parsedInfo.csvContent) : null;
    const dataRows = csvData?.rows?.length || parsedInfo?.rowCount || 0;
    return /* @__PURE__ */ jsxs("div", {
      className: `col-span-full space-y-4 bg-[color:var(--color-bg-white)] p-6 md:p-8 rounded-[2rem] border shadow-xl relative overflow-hidden animate-in fade-in zoom-in-95 duration-500 transition-all ${isModified ? "border-[color:var(--color-warn-line)] ring-4 ring-[color:color-mix(in_srgb,var(--color-warn-line)_50%,transparent)]" : "border-[color:var(--color-border)]"}`, children: [
      /* @__PURE__ */ jsx("div", { className: "absolute top-0 right-0 w-[300px] h-[300px] bg-[color:color-mix(in_srgb,var(--color-ok-fg)_5%,transparent)] blur-[100px] rounded-full pointer-events-none" }),
      /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center relative z-10", children: [
        /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-[11px] font-black uppercase tracking-[0.4em] text-[color:var(--color-ok-fg)]", children: attr.label }),
          /* @__PURE__ */ jsx("div", { className: "h-1 w-12 bg-[color:color-mix(in_srgb,var(--color-ok-fg)_20%,transparent)] rounded-full" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Upload a CSV file containing your asset inventory" })
        ] }),
        canEdit && /* @__PURE__ */ jsx("button", { onClick: () => handleDeleteAttr(attr.key), className: "p-1.5 bg-[color:var(--color-bg-alt)] hover:brightness-95 hover:text-white text-[color:var(--color-danger-fg)] border border-[color:var(--color-border)] rounded-lg transition-all shadow-sm", children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" }) }) })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "relative z-10", children:
        csvUploading ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center py-10 border-2 border-dashed border-[color:var(--color-ok-line)] rounded-2xl bg-[color:color-mix(in_srgb,var(--color-ok-bg)_40%,transparent)]", children: [
          /* @__PURE__ */ jsx("div", { className: "relative w-12 h-12 mb-4", children: /* @__PURE__ */ jsxs("svg", { className: "w-12 h-12 animate-spin", viewBox: "0 0 50 50", children: [
            /* @__PURE__ */ jsx("circle", { cx: "25", cy: "25", r: "20", fill: "none", stroke: "#d1fae5", strokeWidth: "4" }),
            /* @__PURE__ */ jsx("circle", { cx: "25", cy: "25", r: "20", fill: "none", stroke: "#10b981", strokeWidth: "4", strokeLinecap: "round", strokeDasharray: "80, 200", strokeDashoffset: "0" })
          ] }) }),
          /* @__PURE__ */ jsx("span", { className: "text-sm font-bold text-[color:var(--color-ok-fg)]", children: "Processing your file..." }),
          /* @__PURE__ */ jsx("span", { className: "text-[10px] text-[color:var(--color-ok-fg)] mt-1 font-medium", children: "Parsing and saving asset register" }),
          /* @__PURE__ */ jsx("div", { className: "mt-3 flex gap-1", children: [0, 1, 2].map((i) => /* @__PURE__ */ jsx("div", { className: "w-1.5 h-1.5 rounded-full bg-[color:var(--color-ok-fg)] animate-bounce", style: { animationDelay: `${i * 150}ms` } }, i)) })
        ] })
      : !hasFile ? /* @__PURE__ */ jsxs("label", { className: `flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${canEdit ? "border-[color:var(--color-ok-line)] hover:border-[color:var(--color-ok-fg)] hover:bg-[color:color-mix(in_srgb,var(--color-ok-bg)_30%,transparent)]" : "border-[color:var(--color-border)] cursor-not-allowed opacity-50"}`, children: [
          /* @__PURE__ */ jsx("svg", { className: "w-10 h-10 text-emerald-400 mb-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "1.5", d: "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" }) }),
          /* @__PURE__ */ jsx("span", { className: "text-sm font-bold text-[color:var(--color-text-muted)]", children: "Drop CSV file here or click to upload" }),
          /* @__PURE__ */ jsx("span", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1", children: "Accepts .csv files" }),
          canEdit && /* @__PURE__ */ jsx("input", { type: "file", accept: ".csv", className: "hidden", onChange: (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setCsvUploading(true);
            const reader = new FileReader();
            reader.onload = async (ev) => {
              try {
                const text = (ev.target?.result || "").toString();
                const lines = text.split("\n").filter((l) => l.trim());
                const info = JSON.stringify({ fileName: file.name, rowCount: Math.max(0, lines.length - 1), uploadedAt: new Date().toISOString(), csvContent: text });
                const next = (profile.attributes || []).map((a) => a.key === attr.key ? { ...a, value: info, isPendingReview: true, lastModifiedBy: user?.name || "Authorized Member" } : a);
                const updated = next.map((a) => a.key === attr.key ? { ...a, isPendingReview: true, lastModifiedBy: user?.role || "UNKNOWN" } : a);
                onUpdateProfile(project.id, { ...profile, attributes: updated });
                if (saveProjectProfile && project?.id) {
                  const toSave = updated.map((a) => {
                    if (a.format === 'table' && a.tableData) {
                      return { key: a.key, value: JSON.stringify(a.tableData.map(row => { const clean = { ...row }; delete clean.id; return clean; })) };
                    }
                    return { key: a.key, value: a.value };
                  });
                  await saveProjectProfile(project.id, toSave);
                  if (fetchProjectProfile) await fetchProjectProfile(project.id);
                }
              } finally {
                setCsvUploading(false);
              }
            };
            reader.readAsText(file);
          } })
        ] }) : /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "bg-[color:var(--color-ok-bg)] border border-[color:var(--color-ok-line)] rounded-2xl p-4 flex items-center justify-between", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
              /* @__PURE__ */ jsx("div", { className: "w-10 h-10 rounded-xl bg-[color:var(--color-ok-bg)] flex items-center justify-center shrink-0", children: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5 text-[color:var(--color-ok-fg)]", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" }) }) }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-[color:var(--color-ok-fg)]", children: parsedInfo?.fileName || "asset-register.csv" }),
                /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-[color:var(--color-ok-fg)]", children: [dataRows, " rows \u2022 Uploaded ", parsedInfo?.uploadedAt ? formatDdMmYyyy(parsedInfo.uploadedAt) : ""] })
              ] })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 shrink-0", children: [
              csvData && /* @__PURE__ */ jsx("button", { onClick: () => setCsvTableVisible((v) => !v), className: `px-3 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg border transition-all ${csvTableVisible ? "bg-[color:var(--color-accent-fill)] text-white border-[color:var(--color-accent-fill)]" : "text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-bg-alt)] border-[color:var(--color-border)]"}`, children: csvTableVisible ? "Hide Table" : "Show Table" }),
              canEdit && /* @__PURE__ */ jsx("button", { onClick: () => { setCsvTableVisible(false); const next = (profile.attributes || []).map((a) => a.key === attr.key ? { ...a, value: "", isPendingReview: true, lastModifiedBy: user?.name || "Authorized Member" } : a); markAttributeModified(attr.key, next); }, className: "px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] hover:bg-[color:var(--color-danger-bg)] rounded-lg border border-[color:var(--color-danger-line)] transition-all", children: "Remove" })
            ] })
          ] }),
          csvTableVisible && csvData && csvData.headers.length > 0 && /* @__PURE__ */ jsx("div", { className: "overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] animate-in fade-in slide-in-from-top-2 duration-300", children:
            /* @__PURE__ */ jsx("div", { className: "overflow-x-auto max-h-[400px] overflow-y-auto", children:
              /* @__PURE__ */ jsxs("table", { className: "w-full text-left", children: [
                /* @__PURE__ */ jsx("thead", { className: "sticky top-0 z-10", children:
                  /* @__PURE__ */ jsx("tr", { className: "bg-[color:var(--color-surface-sunken)] text-[8px] font-black uppercase tracking-[0.15em] text-[color:var(--color-accent)]", children:
                    csvData.headers.map((h, hi) => /* @__PURE__ */ jsx("th", { className: "px-4 py-3 whitespace-nowrap", children: h }, hi))
                  })
                }),
                /* @__PURE__ */ jsx("tbody", { className: "divide-y divide-[color:var(--color-border)]", children:
                  csvData.rows.map((row, ri) => /* @__PURE__ */ jsx("tr", { className: "hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)] transition-all", children:
                    csvData.headers.map((_, ci) => /* @__PURE__ */ jsx("td", { className: "px-4 py-2.5 text-[11px] text-[color:var(--color-text-hero)] whitespace-nowrap", children: row[ci] || "" }, ci))
                  }, ri))
                })
              ] })
            })
          })
        ] })
      }),
      ]
    });
  };
  const renderField = (attr) => {
    if (attr.format === "locations") return renderLocationAccordion();
    if (attr.format === "table") return renderTableField(attr);
    if (attr.format === "locationSelect") return renderLocationSelectField(attr);
    if (attr.format === "csvUpload") return renderCsvUploadField(attr);
    const isModified = attr.isPendingReview;
    const isChangedByOther = !!attr.lastModifiedBy && attr.lastModifiedBy !== user?.role;
    const modifierLabel = attr.lastModifiedBy ? attr.lastModifiedBy.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
    const isCompanyLogo = attr.key === "companyLogo";
    const attachmentDropHeightClass = isCompanyLogo
      ? "min-h-[220px] sm:min-h-[280px] md:min-h-[300px]"
      : "h-14";
    return /* @__PURE__ */ jsxs("div", {
      className: `space-y-2 group relative bg-[color:var(--color-bg-white)] p-5 rounded-[1.5rem] border-2 shadow-sm hover:shadow-xl transition-all duration-500 animate-in fade-in slide-in-from-bottom-2 ${isModified ? "border-[color:var(--color-warn-fg)] ring-4 ring-[color:color-mix(in_srgb,var(--color-warn-line)_60%,transparent)] bg-[color:color-mix(in_srgb,var(--color-warn-bg)_10%,transparent)]" : "border-[color:var(--color-border)]"} ${isChangedByOther ? "border-[color:var(--color-warn-fg)] ring-4 ring-[color:color-mix(in_srgb,var(--color-warn-line)_60%,transparent)] bg-[color:color-mix(in_srgb,var(--color-warn-bg)_10%,transparent)]" : ""} ${isCompanyLogo && attr.format === "attachment" ? "flex flex-col min-h-[360px] sm:min-h-[400px]" : ""}`, children: [
      /* @__PURE__ */ jsxs("div", {
        className: "flex justify-between items-center relative z-10", children: [
        /* @__PURE__ */ jsxs("div", {
          className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("label", { className: "text-[9px] font-black uppercase tracking-[0.2em] text-[color:var(--color-text-muted)]", children: attr.label }),
            isModified && /* @__PURE__ */ jsx("span", { className: "px-2 py-0.5 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border border-[color:var(--color-warn-line)] text-[7px] font-black uppercase tracking-widest rounded-md shadow-sm", children: "CHANGED" }),
            isChangedByOther && modifierLabel && /* @__PURE__ */ jsxs("span", { className: "text-[7px] font-bold text-[color:var(--color-warn-fg)] italic", children: ["by ", modifierLabel] })
          ]
        }),
          canEdit && /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handleDeleteAttr(attr.key),
              className: "opacity-0 group-hover:opacity-100 p-1 text-[color:var(--color-danger-fg)] hover:bg-[color:var(--color-danger-bg)] rounded-lg transition-all scale-75 group-hover:scale-100",
              children: /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" }) })
            }
          )
        ]
      }),
        attr.format === "attachment" ? (
          uploadingField === attr.key ? (
            /* @__PURE__ */ jsxs("div", {
              className: `relative ${attachmentDropHeightClass} ${isCompanyLogo ? "flex-1" : ""} border-2 border-dashed border-[color:var(--color-info-line)] rounded-xl flex items-center justify-center bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)] animate-pulse`,
              children: [
                /* @__PURE__ */ jsx("svg", { className: `animate-spin text-[color:var(--color-accent)] mr-2 ${isCompanyLogo ? "h-8 w-8" : "h-4 w-4"}`, viewBox: "0 0 24 24", children: /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4", fill: "none" }), /* @__PURE__ */ jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" })] }) }),
                /* @__PURE__ */ jsx("span", { className: `${isCompanyLogo ? "text-[11px]" : "text-[9px]"} font-black uppercase tracking-widest text-[color:var(--color-accent)]`, children: "Uploading..." }),
              ]
            })
          ) : attr.value && attr.value.startsWith('http') ? (
            /* @__PURE__ */ jsxs("div", {
              className: isCompanyLogo ? "flex flex-1 min-h-[220px] sm:min-h-[260px] flex-col items-center justify-center gap-4 rounded-xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)] p-6" : "flex items-center gap-2",
              children: [
                attr.key === 'companyLogo' ? (
                  companyLogoDisplayLoading && !companyLogoDisplayUrl
                    ? /* @__PURE__ */ jsx("div", { className: "flex h-32 w-full max-w-md items-center justify-center rounded-xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-bg-alt)_80%,transparent)] text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Loading preview…" })
                    : /* @__PURE__ */ jsx("img", { src: companyLogoDisplayUrl || attr.value, alt: "Logo", className: "max-h-40 w-auto max-w-full rounded-xl object-contain border border-[color:var(--color-border)] shadow-sm" })
                ) : (
                  /* @__PURE__ */ jsx("svg", { className: "w-5 h-5 text-[color:var(--color-ok-fg)] flex-shrink-0", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" }) })
                ),
                isCompanyLogo ? /* @__PURE__ */ jsxs("div", {
                  className: "flex flex-wrap items-center justify-center gap-3",
                  children: [
                    /* @__PURE__ */ jsx("a", { href: "#", onClick: (e) => { e.preventDefault(); if (openBlobUrl) { void Promise.resolve(openBlobUrl(attr.value)).then((ok) => { if (ok === false) message.error("A secure link could not be generated. Please retry."); }); } else { window.open(attr.value, "_blank", "noopener"); } }, className: "text-[10px] font-bold text-[color:var(--color-accent)] hover:underline", children: "View file" }),
                    canEdit && /* @__PURE__ */ jsxs("label", {
                      className: "px-2 py-1 bg-[color:var(--color-bg-alt)] hover:bg-[color:var(--color-accent-tint)] text-[8px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] hover:text-[color:var(--color-accent)] rounded-lg cursor-pointer transition-all",
                      children: [
                        "Replace",
                        /* @__PURE__ */ jsx("input", { type: "file", accept: "image/*", className: "hidden", onChange: (e) => { if (e.target.files?.[0]) handleFileUpload(attr.key, e.target.files[0]); } }),
                      ]
                    }),
                  ],
                }) : /* @__PURE__ */ jsxs(Fragment, {
                  children: [
                    /* @__PURE__ */ jsx("a", { href: attr.value, target: "_blank", rel: "noreferrer", className: "text-[10px] font-bold text-[color:var(--color-accent)] hover:underline truncate flex-1", children: "View file" }),
                    canEdit && /* @__PURE__ */ jsxs("label", {
                      className: "px-2 py-1 bg-[color:var(--color-bg-alt)] hover:bg-[color:var(--color-accent-tint)] text-[8px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] hover:text-[color:var(--color-accent)] rounded-lg cursor-pointer transition-all",
                      children: [
                        "Replace",
                        /* @__PURE__ */ jsx("input", { type: "file", className: "hidden", onChange: (e) => { if (e.target.files?.[0]) handleFileUpload(attr.key, e.target.files[0]); } }),
                      ]
                    }),
                  ],
                }),
              ]
            })
          ) : (
            /* @__PURE__ */ jsxs("div", {
              className: `relative ${attachmentDropHeightClass} border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 px-4 group/upload transition-all ${isCompanyLogo ? "border-[color:var(--color-info-line)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_40%,transparent)] flex-1" : "border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)]"} ${!canEdit ? "cursor-default" : "hover:border-[color:var(--color-accent)] cursor-pointer"}`,
              children: [
                /* @__PURE__ */ jsx("input", {
                  type: "file",
                  className: "absolute inset-0 opacity-0 cursor-pointer disabled:cursor-default",
                  disabled: !canEdit,
                  accept: isCompanyLogo ? "image/*" : undefined,
                  onChange: (e) => { if (e.target.files?.[0]) handleFileUpload(attr.key, e.target.files[0]); },
                }),
                /* @__PURE__ */ jsx("svg", { className: `${isCompanyLogo ? "w-12 h-12 mb-1" : "w-4 h-4 mb-0.5"} text-[color:var(--color-text-muted)] transition-all ${canEdit ? "group-hover/upload:text-[color:var(--color-accent)]" : ""}`, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" }) }),
                /* @__PURE__ */ jsx("span", { className: `${isCompanyLogo ? "text-[10px] sm:text-[11px] text-[color:var(--color-text-muted)]" : "text-[8px] text-[color:var(--color-text-muted)]"} font-black uppercase tracking-widest truncate px-2 w-full text-center`, children: "Select file to upload" }),
              ]
            })
          )
        ) : attr.format === "dropdown" ? /* @__PURE__ */ jsxs(
          "select",
          {
            value: attr.value,
            disabled: !canEdit,
            onChange: (e) => handleUpdateAttr(attr.key, e.target.value),
            className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold outline-none transition-all ${canEdit ? "bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] cursor-pointer" : "bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed"}`,
            children: [
            /* @__PURE__ */ jsx("option", { value: "", children: "Select..." }),
              attr.options?.map((o) => /* @__PURE__ */ jsx("option", { value: o, children: o }, o))
            ]
          }
        ) : attr.format === "textarea" ? /* @__PURE__ */ jsx(
          "textarea",
          {
            rows: 3,
            value: attr.value,
            disabled: !canEdit,
            onChange: (e) => handleUpdateAttr(attr.key, e.target.value),
            placeholder: `Describe...`,
            className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold outline-none transition-all placeholder:text-[color:var(--color-text-muted)] resize-y ${canEdit ? "bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)] cursor-text" : "bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed"}`
          }
        ) : attr.format === "colorPalette" ? renderColorPaletteField(attr) : attr.format === "fontPicker" ? renderFontPickerField(attr) : /* @__PURE__ */ jsx(
          "input",
          {
            type: attr.format === "number" ? "number" : "text",
            ...(attr.format === "number" ? { min: 0, step: 1 } : {}),
            value:
              attr.format === "number"
                ? attr.value === undefined || attr.value === null
                  ? ""
                  : String(attr.value)
                : attr.value,
            disabled: !canEdit,
            onChange: (e) => handleUpdateAttr(attr.key, e.target.value),
            ...(attr.format === "number"
              ? {
                  onInput: (e) => handleUpdateAttr(attr.key, e.target.value),
                  onBlur: (e) => handleUpdateAttr(attr.key, e.target.value),
                }
              : {}),
            placeholder: `Define...`,
            className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold outline-none transition-all placeholder:text-[color:var(--color-text-muted)] ${canEdit ? "bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)] cursor-text" : "bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed"}`
          }
        )
      ]
    }, attr.key);
  };
  const renderLocationAccordion = () => {
    const attrs = profile.attributes || [];
    const locCountAttr = attrs.find(a => a.key === 'additionalLocations');
    const rawLoc = locCountAttr?.value;
    const locCount =
      rawLoc === undefined || rawLoc === null || rawLoc === ""
        ? 0
        : Math.max(0, Math.floor(Number.parseInt(String(rawLoc).trim(), 10) || 0));
    if (locCount === 0) {
      return /* @__PURE__ */ jsx("div", {
        className: "col-span-full text-center py-12 text-[color:var(--color-text-muted)]",
        children: /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx("div", { className: "text-[10px] font-black uppercase tracking-widest", children: "No additional locations" }),
          /* @__PURE__ */ jsx("p", { className: "text-xs", children: "Set the \"Additional # Locations\" field in Basic Intelligence to add location details." }),
        ]})
      });
    }
    const locDetailsAttr = attrs.find(a => a.key === 'locationDetails');
    let locations = [];
    try { locations = JSON.parse(locDetailsAttr?.value || '[]'); } catch { locations = []; }
    if (!Array.isArray(locations)) locations = [];
    while (locations.length < locCount) {
      locations.push({ index: locations.length + 1, country: '', city: '', zipCode: '', legalStatus: '', statutoryObligations: '', regulatoryObligations: '', headcount: '', cloudServices: '', applications: '', itAssets: '' });
    }
    if (locations.length > locCount) locations = locations.slice(0, locCount);

    const updateLocation = (idx, field, value) => {
      if (!canEdit) return;
      const updated = [...locations];
      updated[idx] = { ...updated[idx], [field]: value };
      const newAttrs = (profile.attributes || []).map(a =>
        a.key === 'locationDetails' ? { ...a, value: JSON.stringify(updated) } : a
      );
      markAttributeModified('locationDetails', newAttrs);
    };

    const LOCATION_FIELDS = [
      { key: 'country', label: 'Location Country' },
      { key: 'city', label: 'Location City' },
      { key: 'zipCode', label: 'Location Zip Code' },
      { key: 'legalStatus', label: 'Legal Status of Entity' },
      { key: 'statutoryObligations', label: 'Statutory Obligations' },
      { key: 'regulatoryObligations', label: 'Regulatory Obligations' },
      { key: 'headcount', label: 'Location Headcount', type: 'number' },
      { key: 'cloudServices', label: 'Cloud Services' },
      { key: 'applications', label: 'Applications' },
      { key: 'itAssets', label: 'IT Assets' },
    ];

    return locations.map((loc, idx) => {
      const isOpen = expandedLocations[idx] !== false;
      const summary = loc.city && loc.country ? `${loc.city}, ${loc.country}` : 'Not configured';
      return /* @__PURE__ */ jsx("div", {
        className: "col-span-full",
        children: /* @__PURE__ */ jsxs("div", {
          className: `bg-[color:var(--color-bg-white)] rounded-[2rem] border-2 shadow-sm overflow-hidden transition-all duration-500 ${isOpen ? 'border-[color:var(--color-info-line)] ring-4 ring-[color:color-mix(in_srgb,var(--color-info-line)_50%,transparent)]' : 'border-[color:var(--color-border)] hover:border-[color:var(--color-border)]'}`,
          children: [
            /* @__PURE__ */ jsx("button", {
              onClick: () => setExpandedLocations(prev => ({ ...prev, [idx]: !isOpen })),
              className: "w-full flex items-center justify-between px-6 py-4 text-left transition-all hover:bg-[color:color-mix(in_srgb,var(--color-bg-alt)_50%,transparent)]",
              children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
                /* @__PURE__ */ jsx("div", { className: `w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs transition-all ${isOpen ? 'bg-[color:var(--color-accent-fill)] text-white shadow-lg shadow-blue-200' : 'bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]'}`, children: idx + 1 }),
                /* @__PURE__ */ jsxs("div", { children: [
                  /* @__PURE__ */ jsxs("div", { className: "text-sm font-black text-[color:var(--color-text-hero)]", children: [`Location ${idx + 1}`] }),
                  /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-text-muted)] font-medium", children: summary }),
                ]}),
                /* @__PURE__ */ jsx("svg", { className: `w-4 h-4 text-[color:var(--color-text-muted)] transition-transform duration-300 ml-auto ${isOpen ? 'rotate-180' : ''}`, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 9l-7 7-7-7" }) }),
              ]}),
            }),
            isOpen && /* @__PURE__ */ jsx("div", {
              className: "px-6 pb-6 pt-2 border-t border-[color:var(--color-border)]",
              children: /* @__PURE__ */ jsx("div", {
                className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
                children: LOCATION_FIELDS.map(f => (
                  /* @__PURE__ */ jsxs("div", {
                    className: "space-y-1.5",
                    children: [
                      /* @__PURE__ */ jsx("label", { className: "text-[9px] font-black uppercase tracking-[0.2em] text-[color:var(--color-text-muted)]", children: f.label }),
                      f.key === 'country' ? /* @__PURE__ */ jsxs("select", {
                        value: loc[f.key] || '',
                        disabled: !canEdit,
                        onChange: (e) => {
                          if (!canEdit) return;
                          const updated = [...locations];
                          updated[idx] = { ...updated[idx], country: e.target.value, city: '' };
                          const newAttrs = (profile.attributes || []).map(a =>
                            a.key === 'locationDetails' ? { ...a, value: JSON.stringify(updated) } : a
                          );
                          markAttributeModified('locationDetails', newAttrs);
                        },
                        className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold outline-none transition-all ${canEdit ? 'bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)]' : 'bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed'}`,
                        children: [
                          /* @__PURE__ */ jsx("option", { value: "", disabled: true, className: "text-[color:var(--color-text-muted)]", children: "Select..." }),
                          ...COUNTRIES.map((country) => /* @__PURE__ */ jsx("option", { value: country, children: country }, country))
                        ]
                      }) : f.key === 'city' ? /* @__PURE__ */ jsxs("select", {
                        value: loc[f.key] || '',
                        disabled: !canEdit || !loc.country,
                        onChange: (e) => updateLocation(idx, f.key, e.target.value),
                        className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold outline-none transition-all ${canEdit && loc.country ? 'bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)]' : 'bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed'}`,
                        children: [
                          /* @__PURE__ */ jsx("option", { value: "", disabled: true, className: "text-[color:var(--color-text-muted)]", children: loc.country ? "Select..." : "Select Country First" }),
                          ...getCitiesForCountry(loc.country).map((city) => /* @__PURE__ */ jsx("option", { value: city, children: city }, city))
                        ]
                      }) : /* @__PURE__ */ jsx("input", {
                        type: f.type || 'text',
                        value: loc[f.key] || '',
                        disabled: !canEdit,
                        onChange: (e) => updateLocation(idx, f.key, e.target.value),
                        placeholder: 'Define...',
                        className: `w-full px-3 py-2 rounded-xl text-[13px] font-semibold outline-none transition-all placeholder:text-[color:var(--color-text-muted)] ${canEdit ? 'bg-[color:var(--color-bg-white)] border-2 border-[color:var(--color-info-line)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] focus:border-[color:var(--color-accent)] hover:border-[color:var(--color-info-line)]' : 'bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] opacity-60 cursor-not-allowed'}`,
                      }),
                    ],
                  }, f.key)
                )),
              }),
            }),
          ],
        }),
      }, `loc-${idx}`);
    });
  };
  const renderOnboardingForm = () => {
    const categories = [
      { key: "Basic", label: "Basic Intelligence" },
      { key: "Contractual", label: "Contractual Oversight" },
      { key: "IT", label: "IT Infrastructure Matrix" },
      { key: "Legal", label: "Legal Constitution Registry" },
      { key: "Assets", label: "Asset Register" },
      { key: "Branding", label: "Brand Identity" },
      { key: "Location", label: "Location Intelligence" },
      { key: "Optional", label: "Optional governance" }
    ];
    const PROFILING_PHASES = [
      { key: "Draft", label: "DRAFT", sub: "Risk identification", icon: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" }) }) },
      { key: "Under Review", label: "UNDER REVIEW", sub: "SPOC reviewing", icon: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" }) }) },
      { key: "Approved by SPOC", label: "APPROVED", sub: "Risks approved", icon: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" }) }) },
      { key: "Finalized", label: "PUBLISHED", sub: "Live dashboard", icon: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }) }) },
    ];
    const phaseOrder = PROFILING_PHASES.map(p => p.key);
    const activePhaseIdx = (() => {
      if (profilingStatus === "Changes Requested") return 0;
      const idx = phaseOrder.indexOf(profilingStatus);
      return idx >= 0 ? idx : 0;
    })();
    const pAttrs = profile.attributes || [];
    const docGetProfiling = (key) => (pAttrs.find((a) => a.key === key) || { value: "" }).value || "";
    const showProfilingAppendEditor =
      isLeadOrAdmin &&
      !isAdvisorOnly &&
      isDocEditMode &&
      profilingDocHasBody &&
      profilingStatus !== "Finalized";
    const onboardingTabCompleted =
      profilingStatus === "Finalized" || profilingStatus === "Approved by SPOC";
    const profileTabCompleted =
      profilingStatus === "Finalized" || hasProfilingDocDraft || profilingDocHasBody;
    const profilingCompanyOnboardingAttention =
      isSPOC &&
      !onboardingTabCompleted &&
      (profilingStatus === "Under Review" || profilingStatus === "Changes Requested")
        ? "Your turn - review company onboarding"
        : null;

    return /* @__PURE__ */ jsxs("div", {
      className: "space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-1000 pb-20", children: [
      /* @__PURE__ */ jsx("div", {
        className: "bg-[color:var(--color-bg-white)] rounded-2xl border border-[color:var(--color-border)] p-6 shadow-sm", children:
        /* @__PURE__ */ jsx("div", {
          className: "flex items-center justify-between gap-0", children:
          PROFILING_PHASES.map((phase, idx) => {
            const isCompleted = idx < activePhaseIdx;
            const isActive = idx === activePhaseIdx;
            const isFuture = idx > activePhaseIdx;
            return /* @__PURE__ */ jsxs(React.Fragment, { children: [
              /* @__PURE__ */ jsxs("div", {
                className: `flex items-center gap-3 transition-all duration-500 ${isFuture ? "opacity-30 blur-[0.5px]" : "opacity-100"}`,
                children: [
                  /* @__PURE__ */ jsx("div", {
                    className: `w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 ${isCompleted ? "bg-[color:var(--color-accent-fill)] text-white shadow-lg shadow-blue-200" : isActive ? "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] ring-2 ring-[color:var(--color-info-line)]" : "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]"}`,
                    children: phase.icon,
                  }),
                  /* @__PURE__ */ jsxs("div", { children: [
                    /* @__PURE__ */ jsx("div", { className: `text-[10px] font-black uppercase tracking-[0.15em] ${isCompleted ? "text-[color:var(--color-accent)]" : isActive ? "text-[color:var(--color-text-hero)]" : "text-[color:var(--color-text-muted)]"}`, children: phase.label }),
                    /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-text-muted)] font-medium", children: phase.sub }),
                  ]}),
                ],
              }),
              idx < PROFILING_PHASES.length - 1 && /* @__PURE__ */ jsx("div", {
                className: `flex-1 h-[2px] mx-4 rounded-full transition-all duration-500 ${idx < activePhaseIdx ? "bg-[color:var(--color-accent)]" : "bg-[color:var(--color-bg-alt)]"}`,
              }),
            ]}, phase.key);
          })
        })
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "flex gap-2 -mt-4 mb-2 flex-wrap items-center",
        children: [
          isAdvisorOnly && /* @__PURE__ */ jsx("span", {
            className: "px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border border-[color:var(--color-info-line)]",
            children: "Advisor: company profile document (read-only)",
          }),
          /* @__PURE__ */ jsx(SubStepNav, {
            ariaLabel: "Company onboarding sections",
            activeKey: profilingSubTab,
            onSelect: (k) => void persistProfilingSubTab(k),
            steps: [
              !isAdvisorOnly && { key: "onboarding", label: "Company Onboarding", num: 1, completed: onboardingTabCompleted, attentionTitle: profilingCompanyOnboardingAttention },
              { key: "profile", label: "Company profile", num: 2, completed: profileTabCompleted },
            ].filter(Boolean),
          }),
        ],
      }),
      profilingSubTab === "onboarding" && !isAdvisorOnly && /* @__PURE__ */ jsxs("header", {
        className: "flex flex-col md:flex-row md:items-center justify-between gap-6 sticky top-0 bg-[color:color-mix(in_srgb,var(--color-bg-white)_80%,transparent)] backdrop-blur-3xl z-30 py-4 border-b border-[color:var(--color-border)] -mx-10 px-10", children: [
        /* @__PURE__ */ jsxs("div", {
          className: "space-y-0.5", children: [
          /* @__PURE__ */ jsx(StageHeader, { title: "Company Onboarding" }),
          /* @__PURE__ */ jsxs("div", {
            className: "flex items-center gap-2.5 mt-2", children: [
            /* @__PURE__ */ jsx("span", { className: `px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border transition-all ${profilingStatus === "Draft" ? "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)]" : profilingStatus === "Under Review" ? "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)] animate-pulse" : profilingStatus === "Changes Requested" ? "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)] animate-pulse" : profilingStatus === "Approved by SPOC" ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]" : "bg-[color:var(--color-pill-final-bg)] text-[color:var(--color-pill-final-fg)] border-[color:var(--color-pill-final-line)] shadow-lg"}`, children: profilingStatus }),
            /* @__PURE__ */ jsxs("p", {
              className: "text-[color:var(--color-text-muted)] font-semibold text-xs leading-none", children: [
                "Entity: ",
              /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-hero)] font-bold", children: project.companyName })
              ]
            })
            ]
          })
          ]
        }),
        profilingStatus !== "Finalized" && !hasProfilingDocDraft && !profilingDocHasBody && !isDocGenerating && /* @__PURE__ */ jsxs("div", {
          className: "flex gap-2", children: [
            isLeadOrAdmin && /* @__PURE__ */ jsxs(Fragment, {
              children: [
                (profilingStatus === "Draft" || profilingStatus === "Changes Requested") && /* @__PURE__ */ jsxs(
                  "button",
                  {
                    onClick: () => openReviewModal("Under Review"),
                    className: "px-3 py-1.5 bg-[color:var(--color-accent-fill)] text-white rounded-xl font-black text-[9px] uppercase tracking-[0.2em] hover:brightness-110 transition-all shadow-xl active:scale-95 flex items-center gap-2",
                    children: [
                  /* @__PURE__ */ jsx("span", { children: "\u26A1" }),
                      " Review Request"
                    ]
                  }
                ),
                profilingStatus === "Approved by SPOC" && /* @__PURE__ */ jsxs("div", {
                  className: "flex gap-2", children: [
              /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => openReviewModal("Under Review"),
                      className: "px-3 py-1.5 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted)] rounded-xl font-black text-[9px] uppercase tracking-[0.2em] hover:bg-[color:var(--color-bg-alt)] transition-all shadow-xl active:scale-95",
                      children: "Resend Review"
                    }
                  ),
              canFinalizeProfiling && /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => {
                        void (async () => {
                          await persistProfilingSubTab("profile");
                          openDocModal();
                        })();
                      },
                      className: "px-3 py-1.5 bg-[color:var(--color-accent-fill)] text-white rounded-xl font-black text-[9px] uppercase tracking-[0.2em] hover:brightness-110 transition-all shadow-xl active:scale-95 flex items-center gap-2",
                      children: "Finalize Profile"
                    }
                  )
                  ]
                }),
                profilingStatus !== "Finalized" && profilingStatus !== "Under Review" && /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: () => setIsAddAttrOpen(true),
                    className: "px-3 py-1.5 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-hero)] rounded-xl font-black text-[9px] uppercase tracking-[0.2em] hover:bg-[color:var(--color-bg-alt)] transition-all shadow-xl active:scale-95 flex items-center gap-2",
                    children: "+ Attributes"
                  }
                )
              ]
            }),
            isSPOC && profilingStatus === "Under Review" && /* @__PURE__ */ jsxs("div", {
              className: "flex gap-2", children: [
            /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: () => openReviewModal("Changes Requested"),
                  className: "px-3 py-1.5 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border border-[color:var(--color-danger-line)] rounded-xl font-black text-[9px] uppercase tracking-[0.2em] hover:bg-[color:var(--color-danger-bg)] transition-all shadow-xl active:scale-95",
                  children: "Send for Review"
                }
              ),
            /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: () => openReviewModal("Approved by SPOC"),
                  className: "px-3 py-1.5 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] rounded-xl font-black text-[9px] uppercase tracking-[0.2em] hover:brightness-95 transition-all shadow-xl active:scale-95",
                  children: "Approve Profile"
                }
              )
              ]
            })
          ]
        })
        ]
      }),
      /* @__PURE__ */ jsx(
        Modal,
        {
          isOpen: isReviewModalOpen,
          onClose: () => setIsReviewModalOpen(false),
          title: reviewTargetStatus === "Under Review" ? "Send for Review" : reviewTargetStatus === "Changes Requested" ? "Request Changes" : reviewTargetStatus === "Approved by SPOC" ? "Approve Profile" : "Send Review",
          footer: /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => {
                if (!reviewTargetStatus) return;
                submitReviewWithComment(reviewTargetStatus);
              },
              className: "w-full py-3 bg-[color:var(--color-accent-fill)] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-lg",
              children: "Send"
            }
          ),
          children: /* @__PURE__ */ jsxs("div", {
            className: "space-y-4", children: [
            /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-[0.2em] text-[color:var(--color-text-muted)]", children: "Comment (optional)" }),
            /* @__PURE__ */ jsx(
              "textarea",
              {
                rows: 4,
                value: reviewComment,
                onChange: (e) => setReviewComment(e.target.value),
                className: "w-full px-4 py-3 bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-2xl text-sm font-medium outline-none resize-none",
                placeholder: "Add a note for the reviewer..."
              }
            )
            ]
          })
        }
      ),
      profilingSubTab === "profile" &&
        /* @__PURE__ */ jsxs("div", {
          className: "rounded-[2rem] border border-[color:var(--color-border)] shadow-lg overflow-hidden bg-[color:var(--color-bg-white)] mb-4",
          children: [
            /* @__PURE__ */ jsxs("div", {
              className:
                "bg-[color:var(--color-accent-fill)] text-white px-8 py-6 flex flex-wrap justify-between gap-6 items-start",
              children: [
                /* @__PURE__ */ jsxs("div", {
                  className: "space-y-2 min-w-0 flex-1",
                  children: [
                    /* @__PURE__ */ jsx("p", {
                      className: "text-[10px] font-black uppercase tracking-widest text-blue-300",
                      children: "Company profile",
                    }),
                    /* @__PURE__ */ jsx("h2", {
                      className: "text-2xl font-black mt-1 tracking-tight",
                      children: docGetProfiling("legalName") || project.companyName,
                    }),
                    profilingAdvisorName && /* @__PURE__ */ jsx("p", {
                      className: "text-sm text-white/70",
                      children: profilingAdvisorName,
                    }),
                    canEdit &&
                      isLeadOrAdmin &&
                      isDocEditMode &&
                      /* @__PURE__ */ jsx("button", {
                        type: "button",
                        onClick: () => setIsDocEditMode(false),
                        className:
                          "mt-3 px-4 py-2.5 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border border-[color:var(--color-warn-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-95 transition-all",
                        children: "Done editing",
                      }),
                  ],
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "text-right space-y-2 shrink-0",
                  children: [
                    /* @__PURE__ */ jsx("p", {
                      className: "text-[10px] font-black uppercase tracking-widest text-white/70",
                      children: "Report date",
                    }),
                    /* @__PURE__ */ jsx("p", {
                      className: "text-lg font-bold",
                      children: formatDdMmYyyy(Date.now()),
                    }),
                    /* @__PURE__ */ jsx("span", {
                      className:
                        "inline-block text-[9px] font-black uppercase tracking-widest border border-[color:color-mix(in_srgb,var(--color-warn-fg)_80%,transparent)] text-amber-200 px-2.5 py-1 rounded-lg",
                      children: "Confidential",
                    }),
                  ],
                }),
              ],
            }),
            /* @__PURE__ */ jsx("div", {
              className: "p-8",
              children: isDocGenerating
                ? /* @__PURE__ */ jsxs("div", {
                    className: "flex flex-col items-center justify-center py-16 gap-4",
                    children: [
                      /* @__PURE__ */ jsx("div", {
                        className:
                          "w-8 h-8 border-3 border-[color:var(--color-info-line)] border-t-[color:var(--color-accent)] rounded-full animate-spin",
                      }),
                      /* @__PURE__ */ jsx("p", {
                        className: "text-sm font-semibold text-[color:var(--color-text-muted)] animate-pulse",
                        children: "Generating document with AI...",
                      }),
                    ],
                  })
                : !profilingDocHasBody
                  ? profilingStatus === "Finalized"
                    ? /* @__PURE__ */ jsx("p", {
                        className: "text-center text-[color:var(--color-text-muted)] py-12 text-sm font-medium",
                        children: "No company profile document was found for this project.",
                      })
                    : /* @__PURE__ */ jsxs("div", {
                        className: "text-center py-16 space-y-6 max-w-lg mx-auto",
                        children: [
                          /* @__PURE__ */ jsx("p", {
                            className: "text-[color:var(--color-text-muted)] text-sm font-medium leading-relaxed",
                            children:
                              "Generate the company profile from onboarding data, then save a draft here to unlock custom sections (paragraphs and tables).",
                          }),
                          profilingStatus === "Approved by SPOC" &&
                            isLeadOrAdmin &&
                            /* @__PURE__ */ jsx("button", {
                              type: "button",
                              onClick: () => openDocModal(),
                              className:
                                "px-6 py-3 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg",
                              children: "Generate document",
                            }),
                          profilingStatus !== "Approved by SPOC" &&
                            /* @__PURE__ */ jsx("p", {
                              className: "text-xs text-[color:var(--color-text-muted)]",
                              children: "Available once the profile is approved by SPOC.",
                            }),
                        ],
                      })
                  : /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
                    /* @__PURE__ */ jsx(AiGenerationNotice, { title: "Applicable requirements", testId: "doc-applicable-missing", messages: docApplicableMissing ? ["The AI did not identify any applicable requirements for this company. The table below is not AI-derived; add the requirements manually."] : [] }),
                    /* @__PURE__ */ jsx(CompanyProfileDocument, {
                      project,
                      getAttr: docGetProfiling,
                      docSections,
                      setDocSections,
                      isDocEditMode: isAdvisorOnly ? false : profilingStatus === "Finalized" ? false : isDocEditMode,
                      isDocGenerating,
                      appendSectionsEditable: showProfilingAppendEditor,
                      advisorName: profilingAdvisorName,
                      omitOuterBranding: true,
                    })] }),
            }),
            profilingStatus !== "Finalized" &&
              /* @__PURE__ */ jsxs("div", {
                className:
                  "px-8 py-4 border-t border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_80%,transparent)] flex flex-wrap items-center gap-2",
                children: [
                  isLeadOrAdmin &&
                    profilingStatus === "Approved by SPOC" &&
                    /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => openDocModal(),
                      disabled: isDocGenerating,
                      className:
                        "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] rounded-xl text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] hover:bg-[color:var(--color-bg-alt)] disabled:opacity-50",
                      children: isDocGenerating ? "Generating…" : "Regenerate",
                    }),
                  isLeadOrAdmin &&
                    profilingDocHasBody &&
                    /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () =>
                        isDocEditMode ? setIsDocEditMode(false) : setIsDocEditMode(true),
                      className:
                        "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] rounded-xl text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] hover:bg-[color:var(--color-bg-alt)]",
                      children: isDocEditMode ? "Preview" : "Edit document",
                    }),
                  isLeadOrAdmin &&
                    canEdit &&
                    (profilingDocHasBody || hasProfilingDocDraft) &&
                    /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => persistProfilingDocumentDraft(),
                      className:
                        "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110",
                      children: "Save document draft",
                    }),
                  // User ruling: Advisor keeps onboarding Finalize (backend statusPatchRoles allows advisor + lead_advisor).
                  // The Advisor lands on this read-only document tab, so the footer is where they finalize.
                  canFinalizeProfiling &&
                    profilingStatus === "Approved by SPOC" &&
                    /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => saveDocAndFinalize(),
                      className:
                        "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 sm:ml-auto",
                      children: "Finalize Profile",
                    }),
                ],
              }),
          ],
        }),
      profilingSubTab === "onboarding" && !isAdvisorOnly && isLeadOrAdmin && /* @__PURE__ */ jsx(CompanyEnrichmentPanel, {
        initialName: project.companyName || "",
        initialDomain: (profile.attributes || []).find((a) => a.key === "website")?.value || "",
        fieldLabels: ENRICHMENT_FIELD_LABELS,
        currentValue: (key) => (profile.attributes || []).find((a) => a.key === key)?.value || "",
        canApply: canEdit,
        onApply: (key, value) => handleUpdateAttr(key, value),
      }),
      profilingSubTab === "onboarding" && !isAdvisorOnly &&
        (() => {
          const displayAttrs = (() => {
            const existing = profile.attributes || [];
            if (existing.length >= MASTER_REGISTRY.length) return existing;
            const existingKeys = new Set(existing.map((a) => a.key));
            const toAdd = [];
            for (const def of MASTER_REGISTRY) {
              if (existingKeys.has(def.key)) continue;
              const headers = def.tableHeaders || ["Title", "Location"];
              const defaultRow = headers.reduce((acc, h) => ({ ...acc, [h]: "" }), { id: Date.now().toString() });
              toAdd.push({
                ...def,
                value: "",
                tableData: def.format === "table" ? [defaultRow] : void 0,
                isPendingReview: false
              });
            }
            return [...existing, ...toAdd];
          })();
          return categories.map((cat, idx) => {
            const catAttrs = displayAttrs.filter((a) => a.category === cat.key);
            if (catAttrs.length === 0) return null;
            return /* @__PURE__ */ jsxs("section", {
              className: "space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700", children: [
            /* @__PURE__ */ jsxs("div", {
                className: "flex items-center gap-3", children: [
              /* @__PURE__ */ jsx("div", { className: "w-8 h-8 rounded-xl bg-[color:var(--color-accent-fill)] text-white flex items-center justify-center font-black text-xs shadow-md", children: idx + 1 < 10 ? `0${idx + 1}` : idx + 1 }),
              /* @__PURE__ */ jsxs("div", {
                  className: "space-y-0.5", children: [
                /* @__PURE__ */ jsx("h3", { className: "text-[11px] font-black uppercase tracking-[0.2em] text-[color:var(--color-text-hero)] leading-none", children: cat.label }),
                /* @__PURE__ */ jsx("div", { className: "h-0.5 w-12 bg-[color:color-mix(in_srgb,var(--color-accent-fill)_30%,transparent)] rounded-full" })
                  ]
                })
                ]
              }),
            /* @__PURE__ */ jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)] p-8 rounded-[2rem] border border-[color:var(--color-border)] relative overflow-hidden", children: [
              /* @__PURE__ */ jsx("div", { className: "absolute top-0 right-0 w-48 h-48 bg-[color:color-mix(in_srgb,var(--color-accent-fill)_5%,transparent)] blur-[60px] rounded-full pointer-events-none" }),
                  catAttrs.map((attr) => renderField(attr))
                ]
              })
              ]
            }, cat.key);
          });
        })(),
      profilingSubTab === "onboarding" && !isAdvisorOnly &&
        /* @__PURE__ */ jsxs("section", {
          className: "space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700",
          children: [
            /* @__PURE__ */ jsxs("div", {
              className: "flex items-center gap-3",
              children: [
                /* @__PURE__ */ jsx("div", { className: "w-8 h-8 rounded-xl bg-[color:var(--color-accent-fill)] text-white flex items-center justify-center font-black text-xs shadow-md", children: "\u2022" }),
                /* @__PURE__ */ jsxs("div", { className: "space-y-0.5", children: [
                  /* @__PURE__ */ jsx("h3", { className: "text-[11px] font-black uppercase tracking-[0.2em] text-[color:var(--color-text-hero)] leading-none", children: "Additional onboarding files" }),
                  /* @__PURE__ */ jsx("div", { className: "h-0.5 w-12 bg-[color:color-mix(in_srgb,var(--color-accent-fill)_30%,transparent)] rounded-full" }),
                  /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] font-medium pt-1 max-w-2xl", children: "Upload supporting brand or legal files (images, PDF, Word). Files are stored in your workspace blob area and listed here for preview and download." })
                ] })
              ]
            }),
            /* @__PURE__ */ jsxs("div", {
              className: "rounded-[2rem] border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)] p-6 space-y-4",
              children: [
                /* @__PURE__ */ jsxs("a", {
                  href: getBulkUploadTemplateHref(),
                  download: BULK_UPLOAD_TEMPLATE_FILE_NAME,
                  className: "inline-flex items-center gap-2 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-[color:var(--color-accent)] border border-[color:var(--color-info-line)] rounded-lg hover:bg-[color:var(--color-accent-tint)] w-fit",
                  title: "Download a generic policy-document starter template (Word, editable headings)",
                  children: [
                    /* @__PURE__ */ jsx("span", { children: "⬇" }),
                    /* @__PURE__ */ jsx("span", { children: "Download policy document template" })
                  ]
                }),
                canEdit && /* @__PURE__ */ jsxs("div", {
                  className: "relative min-h-[72px] border-2 border-dashed border-[color:var(--color-border)] rounded-xl flex items-center justify-center",
                  children: [
                    onboardingExtraUploading && /* @__PURE__ */ jsx("p", { className: "text-xs font-bold text-[color:var(--color-accent)]", children: "Uploading\u2026" }),
                    !onboardingExtraUploading && /* @__PURE__ */ jsxs(Fragment, { children: [
                      /* @__PURE__ */ jsx("input", {
                        type: "file",
                        multiple: true,
                        className: "absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed",
                        disabled: onboardingExtraUploading,
                        accept: "image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                        onChange: (e) => {
                          const selected = Array.from(e.target.files || []);
                          if (selected.length) void handleOnboardingExtraUpload(selected);
                          e.target.value = "";
                        }
                      }),
                      /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] pointer-events-none", children: "Drop or click to add files" })
                    ] })
                  ]
                }),
                (Array.isArray(project.profile?.onboardingAssetManifest) ? project.profile.onboardingAssetManifest : []).length === 0 && /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)] text-center", children: "No additional files yet." }),
                (Array.isArray(project.profile?.onboardingAssetManifest) ? project.profile.onboardingAssetManifest : []).map((asset) => {
                  // Task #129: the tenant-aware SAS signer can legitimately return a null
                  // readUrl (cross-tenant denial or Azure credential-parse failure). Blob
                  // containers are private (no createIfNotExists() in this repo sets
                  // publicAccess) so asset.blobUrl is an unsigned, inert 403/404 URL — never
                  // fall back to it (see lesson audit-log-unconditional-success-before-persisted-check
                  // and 5388ad9's guard on the sibling kickoff-transcript link).
                  const url = asset.readUrl;
                  const isImg = (asset.contentType || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(asset.fileName || "");
                  return /* @__PURE__ */ jsxs("div", {
                    className: "flex flex-wrap items-center gap-4 p-4 bg-[color:var(--color-bg-white)] rounded-xl border border-[color:var(--color-border)]",
                    key: asset.id,
                    children: [
                      isImg && url ? /* @__PURE__ */ jsx("img", { src: url, alt: "", className: "h-16 w-16 object-contain rounded-lg border border-[color:var(--color-border)] bg-white" }) : /* @__PURE__ */ jsx("div", { className: "h-16 w-16 rounded-lg bg-[color:var(--color-bg-alt)] flex items-center justify-center text-[9px] font-black text-[color:var(--color-text-muted)]", children: "FILE" }),
                      /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
                        /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-[color:var(--color-text-hero)] truncate", children: asset.fileName || "File" }),
                        /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: [
                          asset.category || "file",
                          asset.createdAt ? ` \u00b7 ${formatDdMmYyyyTime(asset.createdAt)}` : ""
                        ] })
                      ] }),
                      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 shrink-0", children: [
                        url
                          ? /* @__PURE__ */ jsx("button", {
                              type: "button",
                              onClick: () => {
                                if (openBlobUrl) {
                                  void Promise.resolve(openBlobUrl(url)).then((ok) => {
                                    if (ok === false) message.error("A secure link could not be generated. Please retry.");
                                  });
                                } else {
                                  window.open(url, "_blank", "noopener");
                                }
                              },
                              className: "px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-[color:var(--color-accent)] border border-[color:var(--color-info-line)] rounded-lg hover:bg-[color:var(--color-accent-tint)]",
                              children: "Open"
                            })
                          : /* @__PURE__ */ jsx("span", {
                              className: "px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] border border-[color:var(--color-border)] rounded-lg cursor-not-allowed",
                              title: "A secure link could not be generated. Please retry.",
                              children: "Link unavailable"
                            }),
                        canEdit && /* @__PURE__ */ jsx("button", {
                          type: "button",
                          onClick: () => void handleOnboardingAssetDelete(asset.id),
                          className: "px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] border border-[color:var(--color-danger-line)] rounded-lg hover:bg-[color:var(--color-danger-bg)]",
                          children: "Remove"
                        })
                      ] })
                    ]
                  });
                })
              ]
            })
          ]
        }),
      /* @__PURE__ */ jsx(
          Modal,
          {
            isOpen: isAddAttrOpen,
            onClose: () => setIsAddAttrOpen(false),
            title: "Entity Master Attributes",
            children: /* @__PURE__ */ jsxs("div", {
              className: "space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar pb-6", children: [
            /* @__PURE__ */ jsx("div", {
                className: "sticky top-0 z-20 bg-[color:var(--color-bg-white)] pb-2", children: /* @__PURE__ */ jsxs("div", {
                  className: "relative group", children: [
              /* @__PURE__ */ jsx(
                    "input",
                    {
                      type: "text",
                      value: attrSearch,
                      onChange: (e) => setAttrSearch(e.target.value),
                      placeholder: "Filter catalog...",
                      className: "w-full pl-10 pr-6 py-3 bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-2xl text-xs font-bold outline-none focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_5%,transparent)] focus:bg-[color:var(--color-bg-white)] transition-all shadow-inner"
                    }
                  ),
              /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--color-text-muted)] group-hover:text-[color:var(--color-accent)] transition-all", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "3", d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" }) })
                  ]
                })
              }),
            /* @__PURE__ */ jsx("div", {
                className: "grid grid-cols-1 gap-2", children: filteredMasterRegistry.map((attr) => {
                  const exists = (profile.attributes || []).find((a) => a.key === attr.key);
                  return /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => handleAddAttr(attr),
                      disabled: !!exists,
                      className: `w-full text-left p-4 rounded-xl border transition-all flex justify-between items-center group/btn active:scale-[0.98] ${exists ? "bg-[color:var(--color-surface-sunken)] border-[color:var(--color-border)] opacity-20 cursor-not-allowed" : "bg-[color:var(--color-bg-white)] border-[color:var(--color-border)] hover:border-[color:var(--color-accent)] hover:shadow-lg"}`,
                      children: /* @__PURE__ */ jsxs("div", {
                        className: "flex items-center gap-4 relative z-10", children: [
                    /* @__PURE__ */ jsx("div", { className: `w-8 h-8 rounded-lg flex items-center justify-center font-black text-[9px] ${exists ? "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]" : "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] group-hover/btn:bg-[color:var(--color-accent-fill)] group-hover/btn:text-white transition-all shadow-sm"}`, children: attr.format.substring(0, 2).toUpperCase() }),
                    /* @__PURE__ */ jsxs("div", {
                          className: "space-y-0.5", children: [
                      /* @__PURE__ */ jsx("div", { className: "text-sm font-black text-[color:var(--color-text-hero)] group-hover/btn:text-[color:var(--color-accent)] transition-colors leading-tight", children: attr.label }),
                      /* @__PURE__ */ jsxs("div", {
                            className: "text-[8px] font-black text-[color:var(--color-text-muted)] uppercase tracking-[0.2em] mt-0.5", children: [
                              attr.category,
                              " \u2022 ",
                              attr.format
                            ]
                          })
                          ]
                        })
                        ]
                      })
                    },
                    attr.key
                  );
                })
              })
              ]
            })
          }
        )
      ]
    });
  };
  const renderScoping = () => {
    const scopingReadOnly = !canEditScoping;
    const attrs = project.profile?.attributes || [];
    const getAttr = (k) => (attrs.find((a) => a.key === k) || { value: "" }).value;
    let locationDetails = [];
    try { locationDetails = JSON.parse(getAttr("locationDetails") || '[]'); } catch { locationDetails = []; }
    if (!Array.isArray(locationDetails)) locationDetails = [];

    const totalHeadcount = getAttr("companyHeadcount") || "-";
    
    // Compute total IT assets from location details
    let totalItAssets = 0;
    let hasItAssets = false;
    locationDetails.forEach(loc => {
      if (loc.itAssets) {
        hasItAssets = true;
        totalItAssets += parseInt(loc.itAssets) || 0;
      }
    });
    const displayItAssets = hasItAssets ? totalItAssets : "-";
    
    // Process locations directly from locationDetails (Cloud, Applications, IT Assets are per-location)
    const locations = locationDetails.map(loc => {
      const name = loc.city && loc.country ? `${loc.city}, ${loc.country}` : (loc.country || "Unnamed Location");
      return {
        name,
        hc: loc.headcount || "-",
        itAssets: loc.itAssets || "-",
        cloud: loc.cloudServices || "-",
        apps: loc.applications || "-"
      };
    }).filter(l => l.name !== "Unnamed Location");

    const _scopingBase = scopingRows && scopingRows.length > 0 ? scopingRows[0] : {};
    
    // Get unique countries for Applicable regions
    const uniqueCountries = [...new Set(locationDetails.map(loc => loc.country).filter(Boolean))];
    const applicableRegions = uniqueCountries.length > 0 ? uniqueCountries.join(", ") : "-";

    const _framework = _scopingBase.framework || selectedFramework;
    // Control total: the analysis result, else the live ISO 27001 registry count (only when the
    // framework is ISO 27001); otherwise unknown and shown as an em dash. Requirements have no
    // live source yet, so they are never displayed as a number.
    const _liveControlTotal =
      typeof controlAnalysis?.totalControls === "number" && controlAnalysis.totalControls > 0
        ? controlAnalysis.totalControls
        : /27001/.test(_framework) && isoControlStats.totalControls
          ? isoControlStats.totalControls
          : null;
    const assessment = {
      framework: _framework,
      totalRequirements: "-",
      totalControls: _liveControlTotal ?? "-",
      applicableRegions: _scopingBase.applicableRegions ?? applicableRegions,
      reasoning: controlAnalysis?.reasoning || null,
    };
    // Milestones are now loaded from the API via useEffect
    const finalMilestones = milestoneItems;
    const SCOPING_PHASES = [
      { key: "Draft", label: "DRAFT", sub: "Lead Advisor - build document", icon: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" }) }) },
      { key: "Under Review", label: "UNDER REVIEW", sub: "Company SPOC review", icon: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" }) }) },
      { key: "Approved", label: "APPROVED", sub: "Scoping approved", icon: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" }) }) },
      { key: "Finalized", label: "PUBLISHED", sub: "MSA finalized", icon: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }) }) },
    ];
    const scopingPhaseOrder = SCOPING_PHASES.map(p => p.key);
    const activeScopingPhaseIdx = (() => {
      const idx = scopingPhaseOrder.indexOf(scopingStatus);
      return idx >= 0 ? idx : 0;
    })();

    const scopingWizard = project.profile?.scopingWizard || defaultScopingWizard();
    const summaryLocked = scopingReadOnly || !!scopingWizard.summary;
    const raciLocked = scopingReadOnly || !!scopingWizard.raci;
    const milestonesLocked = scopingReadOnly || !!scopingWizard.milestones;
    const resourcesLocked = scopingReadOnly || !!scopingWizard.resources;
    const pricingLocked = scopingReadOnly || !!scopingWizard.pricing;
    const allScopingWizardComplete = SCOPING_WIZARD_ORDER.every((k) => scopingWizard[k]);
    const canOpenScopingTab = (tab) => {
      if (isSPOC) {
        return (
          tab === "contract" &&
          (scopingStatus === "Under Review" || scopingStatus === "Approved" || scopingStatus === "Finalized")
        );
      }
      if (isAdvisorOnly) return tab === "contract";
      if (!canEditScoping) return true;
      if (!isLeadAdvisor) return true;
      if (tab === "contract") return true;
      const idx = SCOPING_WIZARD_ORDER.indexOf(tab);
      if (idx < 0) return true;
      if (allScopingWizardComplete) return true;
      if (scopingWizard[tab]) return true;
      if (idx <= 0) return true;
      for (let i = 0; i < idx; i++) {
        if (!scopingWizard[SCOPING_WIZARD_ORDER[i]]) return false;
      }
      return true;
    };

    let scopingLeadAttentionKey = null;
    if (!isSPOC && scopingStatus !== "Under Review") {
      for (const spec of SCOPING_SUBSTEP_NAV_SPECS) {
        const open = canOpenScopingTab(spec.key);
        const done = spec.key === "contract" ? allScopingWizardComplete : !!scopingWizard[spec.key];
        if (open && !done) {
          scopingLeadAttentionKey = spec.key;
          break;
        }
      }
    }

    const contractExhibitEditable = isContractEditMode && canEditScoping && scopingStatus !== "Under Review";

    const hasContractDraftSaved =
      project.profile?.scopingContractDraft && typeof project.profile.scopingContractDraft === "object";
    const canReviewScopingDocument = allScopingWizardComplete;
    const contractDocumentSurface = scopingTab === "contract";
    const showContractAppendEditor =
      isLeadOrAdmin &&
      hasContractDraftSaved &&
      contractDocumentSurface &&
      isContractEditMode &&
      canEditScoping;

    const scopingReviewModal = isScopingReviewOpen && /* @__PURE__ */ jsx("div", {
      className: "fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md p-4", children: /* @__PURE__ */ jsxs("div", {
        className: "bg-[color:var(--color-bg-white)] rounded-[2rem] shadow-2xl border border-[color:var(--color-border)] w-full max-w-md overflow-hidden", children: [
    /* @__PURE__ */ jsx("div", { className: "px-8 py-5 border-b border-[color:var(--color-border)] bg-[color:var(--color-accent-fill)] text-white rounded-t-[2rem]", children: /* @__PURE__ */ jsx("h3", { className: "text-sm font-black uppercase tracking-widest", children: scopingReviewTarget === "Under Review" ? "Send for Review" : scopingReviewTarget === "Draft" ? "Send Back" : scopingReviewTarget === "Approved" ? "Approve Scoping" : "Review" }) }),
    /* @__PURE__ */ jsxs("div", {
          className: "p-8 space-y-4", children: [
      scopingReviewTarget === "Under Review" && contractPartyGaps.length > 0 && /* @__PURE__ */ jsx("div", { role: "alert", "data-testid": "scoping-review-missing-parties", className: "rounded-xl border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] p-3 text-xs text-[color:var(--color-danger-fg)] font-semibold", children: `Cannot send for review yet. Missing contract details: ${contractPartyGaps.join(", ")}. Complete the company profile and your advisory firm profile, then try again.` }),
      /* @__PURE__ */ jsx("label", { className: "text-xs font-bold text-[color:var(--color-text-muted)] uppercase tracking-wide", children: "Add a comment (optional)" }),
      /* @__PURE__ */ jsx(
            "textarea",
            {
              value: scopingReviewComment,
              onChange: (e) => setScopingReviewComment(e.target.value),
              className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl p-4 text-sm text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_30%,transparent)] focus:bg-[color:var(--color-bg-white)] transition-all min-h-[100px]",
              placeholder: "Add notes for the reviewer..."
            }
          ),
      /* @__PURE__ */ jsxs("div", {
            className: "flex items-center justify-end gap-2 mt-4", children: [
        /* @__PURE__ */ jsx("button", { type: "button", onClick: () => setIsScopingReviewOpen(false), className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all", children: "Cancel" }),
        /* @__PURE__ */ jsx("button", { type: "button", onClick: submitScopingReview, disabled: scopingReviewTarget === "Under Review" && contractPartyGaps.length > 0, className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed", children: "Submit" })
            ]
          })
        ]
      })
    ]
  })
    });

    if (isSPOC) {
      const profile = project.profile || {};
      const onboardingReviewStatusStored = getStoredOnboardingReviewStatusFromProfile(profile);
      const onboardingReviewStatus = getOnboardingReviewStatusForUi(profile);
      const computedContract = buildContractPreviewFromProfile(project, advisoryFirm);
      const draft = profile.scopingContractDraft;
      const {
        raciSnapshot: draftRaci,
        resourcesSnapshot: draftResources,
        milestonesSnapshot: _milestoneSnap,
        scopingDocumentUpdatedAt,
        ...draftRest
      } = draft && typeof draft === "object" ? draft : {};
      const merged = { ...computedContract, ...stripLegacyFabricatedContractValues(draftRest, { dropBlankParties: true }) };
      const raciForSpoc = Array.isArray(draftRaci) && draftRaci.length ? draftRaci : raciRows;
      const resourceForSpoc = draftResources && typeof draftResources === "object" && (draftResources.rows || draftResources.header)
        ? draftResources
        : resourceTable;
      const hasSavedDraft = !!(draft && typeof draft === "object");
      const spocOnboardingBadge = activeStepId === 3 ? onboardingReviewStatus : scopingStatus;
      const spocBadgeClass =
        spocOnboardingBadge === "Draft"
          ? "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)]"
          : spocOnboardingBadge === "Under Review"
            ? "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)] animate-pulse"
            : spocOnboardingBadge === "Approved" || spocOnboardingBadge === "Approved by SPOC"
              ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]"
              : "bg-[color:var(--color-accent-fill)] text-white border-[color:var(--color-accent-fill)]";
      const patchOnboardingReviewStatus = async (status) => {
        if (!project?.id || !saveScopingProfile || !onUpdateProfile) return;
        const base = project.profile || {};
        const newProfile = mergeProjectOnboardingWizard(base, { onboardingReviewStatus: status });
        onUpdateProfile(project.id, newProfile);
        const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
        if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
      };
      const spocContractSelectionEnabled =
        (activeStepId === 3 && onboardingReviewStatusStored === "Under Review") ||
        (activeStepId !== 3 && scopingStatus === "Under Review");
      const spocContractShowSidebar =
        contractAnnotations.length > 0 || spocContractSelectionEnabled;
      const spocContractCanReply =
        (activeStepId === 3 &&
          (onboardingReviewStatusStored === "Under Review" || onboardingReviewStatusStored === "Draft")) ||
        (activeStepId !== 3 && (scopingStatus === "Under Review" || scopingStatus === "Draft"));
      const spocContractCanResolve =
        (activeStepId === 3 && onboardingReviewStatusStored === "Draft") ||
        (activeStepId !== 3 && scopingStatus === "Draft");
      return /* @__PURE__ */ jsxs(Fragment, {
        children: [
      /* @__PURE__ */ jsxs("section", {
        className: "space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700",
        children: [
          activeStepId === 3
            ? renderOnboardingStatusBar(onboardingReviewStatus)
            : /* @__PURE__ */ jsx("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl border border-[color:var(--color-border)] p-6 shadow-sm",
                children: /* @__PURE__ */ jsx("div", {
                  className: "flex items-center justify-between gap-0",
                  children: SCOPING_PHASES.map((phase, idx) => {
                    const isCompleted = idx < activeScopingPhaseIdx;
                    const isActive = idx === activeScopingPhaseIdx;
                    const isFuture = idx > activeScopingPhaseIdx;
                    return /* @__PURE__ */ jsxs(React.Fragment, { children: [
                      /* @__PURE__ */ jsxs("div", {
                        className: `flex items-center gap-3 transition-all duration-500 ${isFuture ? "opacity-30 blur-[0.5px]" : "opacity-100"}`,
                        children: [
                          /* @__PURE__ */ jsx("div", {
                            className: `w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 ${isCompleted ? "bg-[color:var(--color-accent-fill)] text-white shadow-lg shadow-blue-200" : isActive ? "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] ring-2 ring-[color:var(--color-info-line)]" : "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]"}`,
                            children: phase.icon,
                          }),
                          /* @__PURE__ */ jsxs("div", { children: [
                            /* @__PURE__ */ jsx("div", { className: `text-[10px] font-black uppercase tracking-[0.15em] ${isCompleted ? "text-[color:var(--color-accent)]" : isActive ? "text-[color:var(--color-text-hero)]" : "text-[color:var(--color-text-muted)]"}`, children: phase.label }),
                            /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-text-muted)] font-medium", children: phase.sub }),
                          ]}),
                        ],
                      }),
                      idx < SCOPING_PHASES.length - 1 && /* @__PURE__ */ jsx("div", {
                        className: `flex-1 h-[2px] mx-4 rounded-full transition-all duration-500 ${idx < activeScopingPhaseIdx ? "bg-[color:var(--color-accent)]" : "bg-[color:var(--color-bg-alt)]"}`,
                      }),
                    ]}, phase.key);
                  }),
                }),
              }),
          /* @__PURE__ */ jsxs("div", {
            className: "flex items-center justify-between flex-wrap gap-3",
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
                /* @__PURE__ */ jsx("h3", { className: "text-2xl font-extrabold", children: activeStepId === 3 ? "Project Onboarding - Contract (MSA\u2013NDA)" : "Scoping agreement (MSA\u2013NDA)" }),
                /* @__PURE__ */ jsx("span", { className: `px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${spocBadgeClass}`, children: spocOnboardingBadge }),
              ]}),
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
                activeStepId === 3 && onboardingReviewStatusStored === "Under Review" && /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx("button", { type: "button", onClick: () => patchOnboardingReviewStatus("Draft"), className: "px-4 py-2 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border border-[color:var(--color-danger-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-danger-bg)] transition-all shadow-sm", children: "Send for Re-review" }),
                  /* @__PURE__ */ jsx("button", { type: "button", onClick: () => patchOnboardingReviewStatus("Approved by SPOC"), className: "px-4 py-2 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-95 transition-all shadow-lg", children: "Approve" }),
                ]}),
                activeStepId !== 3 && scopingStatus === "Under Review" && /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx("button", { onClick: () => openScopingReview("Draft"), className: "px-4 py-2 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border border-[color:var(--color-danger-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-danger-bg)] transition-all shadow-sm", children: "Send for Re-review" }),
                  /* @__PURE__ */ jsx("button", { onClick: () => openScopingReview("Approved"), className: "px-4 py-2 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-95 transition-all shadow-lg", children: "Approve Scoping" }),
                ]}),
              ]}),
            ],
          }),
          activeStepId === 3 && !hasSavedDraft && /* @__PURE__ */ jsx("div", { className: "bg-[color:color-mix(in_srgb,var(--color-accent-tint)_90%,transparent)] border border-[color:var(--color-info-line)] rounded-2xl p-6 text-sm text-[color:var(--color-accent)] font-medium", children: "Live MSA preview from the project profile (profiling + scoping data). It updates as the Lead Advisor saves scoping; a saved contract draft in the workspace pins additional text." }),
          activeStepId !== 3 && !draft && /* @__PURE__ */ jsx("div", { className: "bg-[color:var(--color-warn-bg)] border border-[color:var(--color-warn-line)] rounded-2xl p-6 text-sm text-[color:var(--color-warn-fg)] font-medium", children: "The Lead Advisor has not submitted the first section (Summary) yet. After each submit, the MSA\u2013NDA draft below will update for you here." }),
          /* @__PURE__ */ jsxs("div", {
            className:
              "flex max-h-[min(calc(100vh-180px),1280px)] min-h-0 flex-col overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm",
            children: [
              /* @__PURE__ */ jsxs("div", { className: "shrink-0 px-6 py-4 border-b border-[color:var(--color-border)] bg-[color:var(--color-accent-fill)] text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2", children: [
                /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-blue-300", children: "Master Services - Confidentiality & Non-Disclosure Agreement (read-only)" }),
                scopingDocumentUpdatedAt && /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-white/70 font-medium", children: [
                  "Draft saved: ",
                  formatDdMmYyyyTime(scopingDocumentUpdatedAt),
                ]}),
              ]}),
              /* @__PURE__ */ jsx("div", { className: "shrink-0 px-6 py-3 bg-[color:color-mix(in_srgb,var(--color-accent-tint)_80%,transparent)] border-b border-[color:var(--color-info-line)] text-xs text-[color:var(--color-accent)]", children: activeStepId === 3 ? "Project onboarding contract preview. When the Lead Advisor saves a draft in the workspace, pinned sections override the live preview." : "This document refreshes whenever the Lead Advisor submits each section (Summary, RACI, Milestones, Resources, Pricing). You always see the latest saved draft." }),
              /* @__PURE__ */ jsx("div", {
                className: "flex min-h-0 flex-1 flex-col overflow-hidden p-4 sm:p-6",
                children: /* @__PURE__ */ jsx(ContractGoogleDocsShell, {
                  enableSelectionToolbar: spocContractSelectionEnabled,
                  annotations: contractAnnotations,
                  selectedAnchorKey: contractAnnotationFocusKey,
                  onSelectAnchor: setContractAnnotationFocusKey,
                  onPostAnnotation: handleContractAnnotationPost,
                  showSidebar: spocContractShowSidebar,
                  canReplySidebar: spocContractCanReply,
                  canResolveSidebar: spocContractCanResolve,
                  onReply: handleContractAnnotationReply,
                  onResolve: handleContractAnnotationResolve,
                  userDisplayName: user?.name || user?.email || "SPOC",
                  userInitial: String(user?.name || user?.email || "S").trim().slice(0, 1).toUpperCase() || "S",
                  emptySidebarHint: "No contract comments yet.",
                  shellOuterClassName: "w-full min-w-0 items-stretch",
                  deskClassName:
                    "max-h-[min(72vh,calc(100vh-280px))] overflow-y-auto py-4 sm:py-6 !bg-white",
                  children: /* @__PURE__ */ jsx(MsaContractBody, {
                    isContractEditMode: false,
                    contractSections: merged,
                    setContractSections: () => {},
                    raciRows: raciForSpoc,
                    updateRaciCell: () => {},
                    resourceTable: resourceForSpoc,
                    updateResourceCell: () => {},
                    project,
                    contractExhibitEditable: false,
                    annotateAnchors: true,
                    appendSections: contractAppendSections,
                    appendSectionsEditable: false,
                    scrollClassName: "paper-surface space-y-8 bg-white min-w-0",
                    scrollStyle: { fontFamily: "'Times New Roman', Georgia, serif", lineHeight: "1.7" },
                  }),
                }),
              }),
            ],
          }),
        ],
      }),
      scopingReviewModal
    ]
  });
    }

    return /* @__PURE__ */ jsxs(Fragment, {
      children: [
    /* @__PURE__ */ jsxs("section", {
      className: "space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700", children: [
      /* @__PURE__ */ jsx("div", {
        className: "bg-[color:var(--color-bg-white)] rounded-2xl border border-[color:var(--color-border)] p-6 shadow-sm", children:
        /* @__PURE__ */ jsx("div", {
          className: "flex items-center justify-between gap-0", children:
          SCOPING_PHASES.map((phase, idx) => {
            const isCompleted = idx < activeScopingPhaseIdx;
            const isActive = idx === activeScopingPhaseIdx;
            const isFuture = idx > activeScopingPhaseIdx;
            return /* @__PURE__ */ jsxs(React.Fragment, { children: [
              /* @__PURE__ */ jsxs("div", {
                className: `flex items-center gap-3 transition-all duration-500 ${isFuture ? "opacity-30 blur-[0.5px]" : "opacity-100"}`,
                children: [
                  /* @__PURE__ */ jsx("div", {
                    className: `w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 ${isCompleted ? "bg-[color:var(--color-accent-fill)] text-white shadow-lg shadow-blue-200" : isActive ? "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] ring-2 ring-[color:var(--color-info-line)]" : "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]"}`,
                    children: phase.icon,
                  }),
                  /* @__PURE__ */ jsxs("div", { children: [
                    /* @__PURE__ */ jsx("div", { className: `text-[10px] font-black uppercase tracking-[0.15em] ${isCompleted ? "text-[color:var(--color-accent)]" : isActive ? "text-[color:var(--color-text-hero)]" : "text-[color:var(--color-text-muted)]"}`, children: phase.label }),
                    /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-text-muted)] font-medium", children: phase.sub }),
                  ]}),
                ],
              }),
              idx < SCOPING_PHASES.length - 1 && /* @__PURE__ */ jsx("div", {
                className: `flex-1 h-[2px] mx-4 rounded-full transition-all duration-500 ${idx < activeScopingPhaseIdx ? "bg-[color:var(--color-accent)]" : "bg-[color:var(--color-bg-alt)]"}`,
              }),
            ]}, phase.key);
          })
        })
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "flex items-center justify-between flex-wrap gap-3", children: [
        /* @__PURE__ */ jsxs("div", {
          className: "flex items-center gap-4", children: [
          /* @__PURE__ */ jsx(StageHeader, { title: "Scoping & Strategy", status: /* @__PURE__ */ jsx("span", { className: `px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${scopingStatus === "Draft" ? "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)]" : scopingStatus === "Under Review" ? "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)] animate-pulse" : scopingStatus === "Approved" ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]" : "bg-[color:var(--color-accent-fill)] text-white border-[color:var(--color-accent-fill)]"}`, children: scopingStatus }) })
          ]
        }),
        /* @__PURE__ */ jsxs("div", {
          className: "flex items-center gap-2 flex-wrap", children: [
            isSPOC && scopingStatus === "Under Review" && /* @__PURE__ */ jsxs(Fragment, {
              children: [
            /* @__PURE__ */ jsx("button", { onClick: () => openScopingReview("Draft"), className: "px-4 py-2 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border border-[color:var(--color-danger-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-danger-bg)] transition-all shadow-sm", children: "Send for Re-review" }),
            /* @__PURE__ */ jsx("button", { onClick: () => openScopingReview("Approved"), className: "px-4 py-2 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-95 transition-all shadow-lg", children: "Approve Scoping" })
              ]
            }),
            isLeadOrAdmin && scopingStatus === "Under Review" && /* @__PURE__ */ jsx("button", {
              type: "button",
              onClick: () => openScopingReview("Draft"),
              className: "px-4 py-2 bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border border-[color:var(--color-warn-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-warn-bg)] transition-all shadow-sm",
              children: "Return to Draft",
            }),
            isLeadOrAdmin && scopingStatus === "Draft" && canReviewScopingDocument && /* @__PURE__ */ jsx("button", { type: "button", onClick: () => openScopingReview("Under Review"), className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-info-line)] text-[color:var(--color-accent)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm", children: "Send to Company SPOC for review" }),
            isLeadOrAdmin && scopingStatus === "Approved" && /* @__PURE__ */ jsxs(Fragment, {
              children: [
            /* @__PURE__ */ jsx("button", { onClick: () => openScopingReview("Under Review"), className: "px-4 py-2 bg-[color:var(--color-bg-white)] border text-[color:var(--color-text-muted)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-bg-alt)] transition-all shadow-sm", children: "Resend Review" }),
            canFinalizeScoping && /* @__PURE__ */ jsxs("button", {
                onClick: async () => {
                  try {
                    if (!updateScopingStatusAPI || !project?.id) throw new Error("missing status API");
                    await updateScopingStatusAPI(project.id, "Finalized", "Scoping finalized");
                  } catch (err) {
                    message.error(userMessageForError(err, "Could not finalize scoping. Please try again."));
                    return;
                  }
                  setScopingStatus("Finalized");
                  setActiveStepId(3);
                  // Bug #5 fix (RACI refresh-to-Profiling) — see saveDocAndFinalize's
                  // matching comment. Persist the step-3 cursor so leaving Scoping doesn't
                  // leave the server pointing at a stale/absent workspaceActiveStep.
                  void persistWorkspaceActiveStep(3);
                }, className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg flex items-center gap-2", children: [
              /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M5 13l4 4L19 7" }) }),
                  "Finalize Contract"
                ]
              })
              ]
            })
          ]
        })
        ]
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "sticky top-2 z-30 w-full max-w-full flex flex-nowrap sm:flex-wrap items-center gap-2 overflow-x-auto overflow-y-visible overscroll-x-contain bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_95%,transparent)] backdrop-blur-sm py-3 mb-2 border-b border-[color:color-mix(in_srgb,var(--color-border)_90%,transparent)] shadow-sm [scrollbar-width:thin]", children: isAdvisorOnly ? [
        /* @__PURE__ */ jsx("span", { className: "px-4 py-2 rounded-full text-sm font-semibold bg-[color:var(--color-accent-fill)] text-white", children: "MSA \u2013 NDA contract (read-only)" }),
      ] : [/* @__PURE__ */ jsx(SubStepNav, {
        ariaLabel: "Scoping sections",
        activeKey: scopingTab,
        onSelect: (k) => {
          setScopingTab(k);
          void persistScopingUiTab(k);
        },
        steps: SCOPING_SUBSTEP_NAV_SPECS.filter((spec) =>
          visibleSubtabsForRole({ isLeadOrAdmin, isSPOC, isAdvisorOnly, isDelegate }).includes(spec.key)
        ).map((spec) => {
          const done =
            spec.key === "contract"
              ? allScopingWizardComplete
              : !!scopingWizard[spec.key];
          const scopingAttentionTitle =
            isSPOC && scopingStatus === "Under Review" && spec.key === "contract"
              ? "Your turn - review and approve the contract"
              : !isSPOC && scopingStatus !== "Under Review" && spec.key === scopingLeadAttentionKey
                ? "Continue here next"
                : null;
          return { key: spec.key, label: spec.label, num: spec.num, locked: !canOpenScopingTab(spec.key), completed: done, attentionTitle: scopingAttentionTitle };
        }),
      }, "scoping-nav")]
      }),
        scopingTab === "summary" && /* @__PURE__ */ jsxs("div", {
          className: "grid grid-cols-1 sm:grid-cols-3 gap-4", children: [
        /* @__PURE__ */ jsxs("div", {
            className: "bg-[color:var(--color-bg-white)] rounded-2xl p-6 border shadow-sm", children: [
          /* @__PURE__ */ jsx("div", { className: "text-xs font-black uppercase text-[color:var(--color-text-muted)]", children: "Locations" }),
          /* @__PURE__ */ jsx("div", { className: "text-3xl font-extrabold mt-2", children: locations.length || "-" }),
          /* @__PURE__ */ jsx("div", { className: "text-sm text-[color:var(--color-text-muted)] mt-1", children: "Distinct office / delivery locations" })
            ]
          }),
        /* @__PURE__ */ jsxs("div", {
            className: "bg-[color:var(--color-bg-white)] rounded-2xl p-6 border shadow-sm", children: [
          /* @__PURE__ */ jsx("div", { className: "text-xs font-black uppercase text-[color:var(--color-text-muted)]", children: "IT Assets" }),
          /* @__PURE__ */ jsx("div", { className: "text-3xl font-extrabold mt-2", children: displayItAssets }),
          /* @__PURE__ */ jsx("div", { className: "text-sm text-[color:var(--color-text-muted)] mt-1", children: "Estimated infrastructure inventory (servers, endpoints)" })
            ]
          }),
        /* @__PURE__ */ jsxs("div", {
            className: "bg-[color:var(--color-bg-white)] rounded-2xl p-6 border shadow-sm", children: [
          /* @__PURE__ */ jsx("div", { className: "text-xs font-black uppercase text-[color:var(--color-text-muted)]", children: "Headcount" }),
          /* @__PURE__ */ jsx("div", { className: "text-3xl font-extrabold mt-2", children: totalHeadcount }),
          /* @__PURE__ */ jsx("div", { className: "text-sm text-[color:var(--color-text-muted)] mt-1", children: "Total employees across locations" })
            ]
          })
          ]
        }),
        scopingTab === "summary" && /* @__PURE__ */ jsxs("div", {
          className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-4 relative overflow-hidden", children: [
        isAnalyzingControls && /* @__PURE__ */ jsx("div", {
          className: "absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-[color:color-mix(in_srgb,var(--color-bg-white)_80%,transparent)] backdrop-blur-[2px] rounded-2xl",
          children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 text-[color:var(--color-accent)]", children: [
            /* @__PURE__ */ jsx("svg", { className: "animate-spin h-8 w-8 text-[color:var(--color-accent)]", viewBox: "0 0 24 24", fill: "none", children: [
              /* @__PURE__ */ jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }),
              /* @__PURE__ */ jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" }),
            ]}),
            /* @__PURE__ */ jsx("span", { className: "text-sm font-bold tracking-wide", children: "Analyzing controls\u2026" }),
          ]}),
        }),
        /* @__PURE__ */ jsxs("div", {
            className: "flex items-center justify-between mb-3", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-md font-bold", children: "Applicable requirements of legal, statutory and standards" }),
          isLeadOrAdmin && !summaryLocked && /* @__PURE__ */ jsx("button", {
              onClick: analyzeControls,
              disabled: isAnalyzingControls,
              className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm flex items-center gap-2 ${isAnalyzingControls ? "bg-[color:var(--color-accent-fill)] text-white cursor-wait" : controlAnalysis ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] hover:brightness-95" : "bg-[color:var(--color-accent-fill)] text-white hover:brightness-110"}`,
              children: isAnalyzingControls ? [
                /* @__PURE__ */ jsx("svg", { className: "animate-spin w-3.5 h-3.5", viewBox: "0 0 24 24", fill: "none", children: [
                  /* @__PURE__ */ jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }),
                  /* @__PURE__ */ jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" }),
                ]}),
                "Analyzing..."
              ] : controlAnalysis ? ["\u2713 Re-analyze with AI"] : ["\u2728 Analyze with AI"]
            })
          ]
        }),
        /* @__PURE__ */ jsx("div", {
            className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
              className: "w-full text-left min-w-[720px]", children: [
          /* @__PURE__ */ jsx("thead", {
                children: /* @__PURE__ */ jsxs("tr", {
                  className: "text-sm font-black uppercase text-[color:var(--color-text-muted)]", children: [
            /* @__PURE__ */ jsx("th", { className: "px-4 py-3", children: "Title" }),
            /* @__PURE__ */ jsx("th", { className: "px-4 py-3", children: "Count of requirements" }),
            /* @__PURE__ */ jsx("th", { className: "px-4 py-3", children: "Count of controls" }),
            /* @__PURE__ */ jsx("th", { className: "px-4 py-3", children: "Applicable locations" })
                  ]
                })
              }),
          /* @__PURE__ */ jsx("tbody", {
                className: "divide-y", children: /* @__PURE__ */ jsxs("tr", {
                  className: "hover:bg-[color:var(--color-bg-alt)]", children: [
            /* @__PURE__ */ jsx("td", { className: "px-4 py-3 font-semibold", children: assessment.framework }),
            /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: assessment.totalRequirements }),
            /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: assessment.totalControls }),
            /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: assessment.applicableRegions })
                  ]
                })
              })
              ]
            })
          }),
        (() => {
          const a = describeControlAnalysis(controlAnalysis);
          if (!a || (a.count === null && a.controls.length === 0)) return null;
          return /* @__PURE__ */ jsxs("div", {
            "data-testid": "control-analysis-result", "data-basis": a.basis,
            className: "mt-3 px-4 py-3 bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl text-xs text-[color:var(--color-text-hero)] space-y-1",
            children: [
              a.basis === "ai_cited_validated"
                ? /* @__PURE__ */ jsxs("div", { children: [
                    /* @__PURE__ */ jsx("span", { className: "font-bold", children: `AI cited ${a.controls.length} applicable control${a.controls.length === 1 ? "" : "s"} (checked against the control registry)` }),
                    /* @__PURE__ */ jsx("ul", { "data-testid": "control-analysis-list", className: "list-disc pl-4 mt-1 max-h-40 overflow-y-auto", children: a.controls.map((c) => /* @__PURE__ */ jsx("li", { children: `${c.ctrlNumber || ""} ${c.ctrlName || ""}`.trim() }, c.ctrlId || c.ctrlNumber)) }),
                  ] })
                : /* @__PURE__ */ jsx("div", { "data-testid": "control-analysis-count-only", children: a.count !== null ? `AI estimate: ${a.count} control${a.count === 1 ? "" : "s"}. The AI did not name specific controls, so no control list is shown; treat this as a count only.` : "The AI did not name specific controls." }),
              a.rejectedControlNumbers.length > 0 && /* @__PURE__ */ jsx("div", { "data-testid": "control-analysis-rejected", className: "text-[color:var(--color-warn-fg)]", children: `Discarded (not in the control registry): ${a.rejectedControlNumbers.join(", ")}` }),
            ],
          });
        })(),
        assessment.reasoning && /* @__PURE__ */ jsx("div", {
            className: "mt-3 px-4 py-3 bg-[color:var(--color-accent-tint)] border border-[color:var(--color-info-line)] rounded-xl",
            children: /* @__PURE__ */ jsxs("p", { className: "text-xs text-[color:var(--color-accent)]", children: [
              /* @__PURE__ */ jsx("span", { className: "font-bold", children: "AI Analysis: " }),
              assessment.reasoning,
            ]})
          })
          ]
        }),
        scopingTab === "summary" && /* @__PURE__ */ jsxs("div", {
          className: "space-y-4", children: [
        /* @__PURE__ */ jsx("h4", { className: "text-md font-bold", children: "Location-wise scope" }),
        /* @__PURE__ */ jsx("div", {
            className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", children: locations.length === 0 ? /* @__PURE__ */ jsx("div", { className: "col-span-full py-8 text-center text-[color:var(--color-text-muted)] text-sm", children: 'No locations configured in "Basic Intelligence"' }) : locations.map((loc, idx) => /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-4 shadow-sm", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "flex items-center justify-between mb-3", children: [
            /* @__PURE__ */ jsx("div", { className: "text-sm font-extrabold", children: loc.name }),
            /* @__PURE__ */ jsx("div", { className: "text-xs text-[color:var(--color-text-muted)]", children: "Location" })
                ]
              }),
          /* @__PURE__ */ jsxs("div", {
                className: "grid grid-cols-2 gap-3", children: [
            /* @__PURE__ */ jsxs("div", {
                  className: "p-3 bg-[color:var(--color-surface-sunken)] rounded", children: [
              /* @__PURE__ */ jsx("div", { className: "text-[11px] text-[color:var(--color-text-muted)]", children: "HC" }),
              /* @__PURE__ */ jsx("div", { className: "font-bold text-lg", children: loc.hc })
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "p-3 bg-[color:var(--color-surface-sunken)] rounded", children: [
              /* @__PURE__ */ jsx("div", { className: "text-[11px] text-[color:var(--color-text-muted)]", children: "IT assets" }),
              /* @__PURE__ */ jsx("div", { className: "font-bold text-lg", children: loc.itAssets })
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "col-span-2 p-3 bg-[color:var(--color-surface-sunken)] rounded", children: [
              /* @__PURE__ */ jsx("div", { className: "text-[11px] text-[color:var(--color-text-muted)]", children: "Cloud" }),
              /* @__PURE__ */ jsx("div", { className: "font-medium text-sm", children: loc.cloud })
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "col-span-2 p-3 bg-[color:var(--color-surface-sunken)] rounded", children: [
              /* @__PURE__ */ jsx("div", { className: "text-[11px] text-[color:var(--color-text-muted)]", children: "Applications" }),
              /* @__PURE__ */ jsx("div", { className: "font-medium text-sm", children: loc.apps })
                  ]
                })
                ]
              })
              ]
            }, idx))
          })
          ]
        }),
        scopingTab === "summary" && isLeadOrAdmin && !summaryLocked && !isAnalyzingControls && /* @__PURE__ */ jsx("div", {
          className: "flex justify-end pt-2",
          children: /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => persistScopingWizardStep("summary", "raci"),
            disabled: isAnalyzingControls,
            className: "px-6 py-3 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed",
            children: "Submit & continue to RACI",
          }),
        }),
        scopingTab === "raci" && /* @__PURE__ */ jsxs("div", {
          className: "space-y-4", children: [
        /* @__PURE__ */ jsx("div", {
          className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-4", children: /* @__PURE__ */ jsxs("div", {
            className: "overflow-x-auto", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "flex items-center justify-between mb-3", children: [
          /* @__PURE__ */ jsx("div", {}),
          /* @__PURE__ */ jsxs("div", {
                className: "flex items-center gap-2", children: [
                  !raciLocked && /* @__PURE__ */ jsx("button", {
                    onClick: generateRaciWithAI,
                    disabled: isGeneratingRaci || aiDisabled.raci || scopingWizard.raci,
                    className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${isGeneratingRaci ? "bg-[color:var(--color-accent-fill)] text-white cursor-wait" : aiDisabled.raci || scopingWizard.raci ? "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-gradient-to-r from-[color:var(--color-accent-fill)] to-[color:var(--color-accent-fill)] text-white hover:from-[color:var(--color-accent-fill)] hover:to-[color:var(--color-accent-fill)]"}`,
                    children: isGeneratingRaci
                      ? /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
                          /* @__PURE__ */ jsx("svg", { className: "animate-spin h-3.5 w-3.5", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsxs("g", { children: [/* @__PURE__ */ jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4", fill: "none" }), /* @__PURE__ */ jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" })] }) }),
                          "Assigning..."
                        ] })
                      : "\u2728 Auto-assign with AI"
                  }),
                  !raciLocked && /* @__PURE__ */ jsx("button", { onClick: addRaciRow, className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-accent)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm", children: "+ Add Row" }),
                  !raciLocked && /* @__PURE__ */ jsx("button", { onClick: () => persistScopingMilestoneResource("raci"), disabled: savedFlash === "raci", className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${savedFlash === "raci" ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] cursor-default" : "bg-[color:var(--color-accent-fill)] text-white hover:brightness-110"}`, children: savedFlash === "raci" ? "\u2713 Saved" : "Save" })
                ]
              })
              ]
            }),
              /* @__PURE__ */ jsx(AiGenerationNotice, { title: "RACI draft", messages: raciNotice, testId: "raci-generation-notice" }),
              renderRaciFromTest()
            ]
          })
        }),
        scopingTab === "raci" && isLeadOrAdmin && !raciLocked && /* @__PURE__ */ jsx("div", {
          className: "flex justify-end",
          children: /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => persistScopingWizardStep("raci", "milestones"),
            className: "px-6 py-3 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg",
            children: "Submit & continue to Milestones",
          }),
        }),
          ]
        }),
        scopingTab === "milestones" && (() => {
          const ganttColors = [
            "bg-[color:var(--color-accent-fill)]",
            "bg-[color:var(--color-accent-fill)]",
            "bg-[color:var(--color-accent-fill)]",
            "bg-[color:var(--color-accent-fill)]",
            "bg-[color:var(--color-accent-fill)]",
            "bg-[color:var(--color-accent-fill)]",
            "bg-[color:var(--color-ok-fg)]",
            "bg-[color:var(--color-ok-fg)]"
          ];
          const allStarts = finalMilestones.map((m) => new Date(m.start).getTime()).filter((t) => !isNaN(t));
          const allEnds = finalMilestones.map((m) => new Date(m.due).getTime()).filter((t) => !isNaN(t));
          const ganttMin = allStarts.length ? Math.min(...allStarts) : Date.now();
          const ganttMax = allEnds.length ? Math.max(...allEnds) : ganttMin + 12 * 7 * 864e5;
          const ganttRange = Math.max(ganttMax - ganttMin, 1);
          const totalWeeks = Math.ceil(ganttRange / (7 * 864e5)) + 1;
          const weekLabels = [];
          for (let w = 0; w < totalWeeks; w++) {
            const d = new Date(ganttMin + w * 7 * 864e5);
            weekLabels.push(`W${w + 1}`);
          }
          return /* @__PURE__ */ jsxs("div", {
            className: "space-y-6", children: [
          /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-4", children: [
            /* @__PURE__ */ jsxs("div", {
                className: "flex items-center justify-between mb-3", children: [
              /* @__PURE__ */ jsx("h4", { className: "text-md font-bold", children: "Milestone Map" }),
              /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center gap-2", children: [
                    !milestonesLocked && /* @__PURE__ */ jsx("button", { onClick: handleGenerateMilestones, disabled: milestoneGenerating || aiDisabled.milestones || scopingWizard.milestones, className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${milestoneGenerating ? "bg-[color:var(--color-accent-fill)] text-white cursor-wait" : aiDisabled.milestones || scopingWizard.milestones ? "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[color:var(--color-accent-fill)] text-white hover:brightness-110"}`, children: milestoneGenerating ? "✨ Generating..." : "✨ Generate with AI" }),
                    !milestonesLocked && /* @__PURE__ */ jsx("button", { onClick: handleSaveMilestones, disabled: savedFlash === "milestones", className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${savedFlash === "milestones" ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] cursor-default" : "bg-[color:var(--color-accent-fill)] text-white hover:brightness-110"}`, children: savedFlash === "milestones" ? "\u2713 Saved" : "Save" })
                  ]
                })
                ]
              }),
            /* @__PURE__ */ jsx("div", {
                className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
                  className: "w-full text-left", children: [
              /* @__PURE__ */ jsx("thead", {
                    children: /* @__PURE__ */ jsxs("tr", {
                      className: "bg-[color:var(--color-surface-sunken)] text-[10px] font-black uppercase tracking-[0.15em] text-[color:var(--color-accent)]", children: [
                /* @__PURE__ */ jsx("th", { className: "px-4 py-4 w-[200px]", children: "Task Title" }),
                /* @__PURE__ */ jsx("th", { className: "px-3 py-4 w-[120px]", children: "Start Date" }),
                /* @__PURE__ */ jsx("th", { className: "px-3 py-4 w-[120px]", children: "Due Date" }),
                /* @__PURE__ */ jsx("th", { className: "px-3 py-4 w-[90px]", children: "Duration" }),
                /* @__PURE__ */ jsx("th", { className: "px-3 py-4 w-[160px]", children: "Dependencies" }),
                /* @__PURE__ */ jsx("th", { className: "px-3 py-4 min-w-[200px]", children: "Key Deliverable(s)" }),
                        !milestonesLocked && /* @__PURE__ */ jsx("th", { className: "px-3 py-4 w-[70px] text-right", children: "Actions" })
                      ]
                    })
                  }),
              /* @__PURE__ */ jsx("tbody", {
                    className: "divide-y divide-[color:var(--color-border)]", children: finalMilestones.map((m, idx) => /* @__PURE__ */ jsxs("tr", {
                      className: "group hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)] transition-all", children: [
                /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("input", { value: m.title, readOnly: true, className: "w-full bg-transparent border-b border-[color:var(--color-border)] py-1 text-sm font-semibold text-[color:var(--color-text-hero)] outline-none cursor-default" }) }),
                /* @__PURE__ */ jsx("td", { className: "px-3 py-3", children: /* @__PURE__ */ jsx("input", { type: "date", value: m.start, readOnly: milestonesLocked, onChange: (e) => updateMilestoneField(m.id, "start", e.target.value), className: `w-full bg-transparent border-b border-[color:var(--color-border)] py-1 text-sm text-[color:var(--color-text-hero)] outline-none transition-all ${milestonesLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}` }) }),
                /* @__PURE__ */ jsx("td", { className: "px-3 py-3", children: /* @__PURE__ */ jsx("input", { type: "date", value: m.due, readOnly: milestonesLocked, onChange: (e) => updateMilestoneField(m.id, "due", e.target.value), className: `w-full bg-transparent border-b border-[color:var(--color-border)] py-1 text-sm text-[color:var(--color-text-hero)] outline-none transition-all ${milestonesLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}` }) }),
                /* @__PURE__ */ jsx("td", { className: "px-3 py-3", children: /* @__PURE__ */ jsx("input", { type: "number", value: String(m.duration || 7), readOnly: milestonesLocked, onChange: (e) => updateMilestoneField(m.id, "duration", Number(e.target.value)), className: `w-full bg-transparent border-b border-[color:var(--color-border)] py-1 text-sm text-[color:var(--color-text-hero)] outline-none transition-all text-center ${milestonesLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}` }) }),
                /* @__PURE__ */ jsx("td", { className: "px-3 py-3", children: /* @__PURE__ */ jsx("input", { value: (m.dependencies || "").toString(), readOnly: milestonesLocked, onChange: (e) => updateMilestoneField(m.id, "dependencies", e.target.value), className: `w-full bg-transparent border-b border-[color:var(--color-border)] py-1 text-sm text-[color:var(--color-text-hero)] outline-none transition-all ${milestonesLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}` }) }),
                /* @__PURE__ */ jsx("td", { className: "px-3 py-3", children: /* @__PURE__ */ jsx("input", { value: (m.keyDeliverable || "").toString(), readOnly: milestonesLocked, onChange: (e) => updateMilestoneField(m.id, "keyDeliverable", e.target.value), className: `w-full bg-transparent border-b border-[color:var(--color-border)] py-1 text-sm text-[color:var(--color-text-hero)] outline-none transition-all ${milestonesLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}` }) }),
                        !milestonesLocked && /* @__PURE__ */ jsx("td", { className: "px-3 py-3 text-right", children: /* @__PURE__ */ jsx("button", { onClick: () => deleteMilestone(m.id), className: "p-2 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-colors opacity-0 group-hover:opacity-100", children: /* @__PURE__ */ jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" }) }) }) })
                      ]
                    }, m.id))
                  })
                  ]
                })
              })
              ]
            }),
          /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-2xl border p-6 shadow-sm", children: [
            /* @__PURE__ */ jsx("h4", { className: "text-md font-bold mb-4", children: "Timeline View" }),
            /* @__PURE__ */ jsx("div", {
                className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("div", {
                  style: { minWidth: Math.max(800, totalWeeks * 60) }, children: [
              /* @__PURE__ */ jsxs("div", {
                    className: "flex", children: [
                /* @__PURE__ */ jsx("div", { className: "w-[200px] flex-shrink-0 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Phase" }),
                /* @__PURE__ */ jsx("div", { className: "flex-1 flex", children: weekLabels.map((w, wi) => /* @__PURE__ */ jsx("div", { className: "flex-1 text-center text-[9px] font-bold text-[color:var(--color-text-muted)] py-2 border-l border-[color:var(--color-border)]", children: w }, wi)) })
                    ]
                  }),
                    finalMilestones.map((m, idx) => {
                      const startMs = new Date(m.start).getTime();
                      const endMs = new Date(m.due).getTime();
                      const leftPct = isNaN(startMs) ? 0 : (startMs - ganttMin) / ganttRange * 100;
                      const widthPct = isNaN(endMs) || isNaN(startMs) ? 10 : Math.max(2, (endMs - startMs) / ganttRange * 100);
                      const color = ganttColors[idx % ganttColors.length];
                      return /* @__PURE__ */ jsxs("div", {
                        className: "flex items-center group/row hover:bg-[color:color-mix(in_srgb,var(--color-bg-alt)_50%,transparent)] transition-all", style: { minHeight: 44 }, children: [
                  /* @__PURE__ */ jsx("div", { className: "w-[200px] flex-shrink-0 px-3 py-2 text-xs font-semibold text-[color:var(--color-text-hero)] truncate border-r border-[color:var(--color-border)]", children: m.title }),
                  /* @__PURE__ */ jsxs("div", {
                          className: "flex-1 relative", style: { height: 36 }, children: [
                    /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex pointer-events-none", children: weekLabels.map((_, wi) => /* @__PURE__ */ jsx("div", { className: "flex-1 border-l border-[color:var(--color-border)]" }, wi)) }),
                    /* @__PURE__ */ jsx(
                            "div",
                            {
                              className: `absolute top-1 h-7 ${color} rounded-lg shadow-md flex items-center px-2 transition-all group-hover/row:shadow-lg group-hover/row:brightness-110`,
                              style: { left: `${leftPct}%`, width: `${widthPct}%`, minWidth: 24 },
                              children: /* @__PURE__ */ jsxs("span", {
                                className: "text-[9px] font-bold text-white truncate", children: [
                                  m.duration,
                                  "d"
                                ]
                              })
                            }
                          )
                          ]
                        })
                        ]
                      }, m.id);
                    }),
                    (() => {
                      const todayPct = (Date.now() - ganttMin) / ganttRange * 100;
                      if (todayPct < 0 || todayPct > 100) return null;
                      return /* @__PURE__ */ jsx("div", { className: "relative", style: { minWidth: Math.max(800, totalWeeks * 60) }, children: /* @__PURE__ */ jsx("div", { className: "absolute top-0 bottom-0 w-0.5 bg-[color:var(--color-danger-fg)] z-10", style: { left: `calc(200px + ${todayPct}% * (100% - 200px) / 100%)` } }) });
                    })()
                  ]
                })
              }),
            /* @__PURE__ */ jsxs("div", {
                className: "flex items-center gap-4 mt-4 pt-3 border-t border-[color:var(--color-border)]", children: [
                  finalMilestones.slice(0, 4).map((m, idx) => /* @__PURE__ */ jsxs("div", {
                    className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx("div", { className: `w-3 h-3 rounded ${ganttColors[idx % ganttColors.length]}` }),
                /* @__PURE__ */ jsx("span", { className: "text-[10px] font-semibold text-[color:var(--color-text-muted)] truncate max-w-[120px]", children: m.title })
                    ]
                  }, m.id)),
                  finalMilestones.length > 4 && /* @__PURE__ */ jsxs("span", {
                    className: "text-[10px] text-[color:var(--color-text-muted)]", children: [
                      "+",
                      finalMilestones.length - 4,
                      " more"
                    ]
                  })
                ]
              })
              ]
            })
            ]
          });
        })(),
        scopingTab === "milestones" && isLeadOrAdmin && !milestonesLocked && /* @__PURE__ */ jsx("div", {
          className: "flex justify-end",
          children: /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => persistScopingWizardStep("milestones", "resources"),
            className: "px-6 py-3 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg",
            children: "Submit & continue to Resources",
          }),
        }),
        scopingTab === "resources" && (() => {
          const rHeader = resourceTable && resourceTable.header && resourceTable.header.length ? resourceTable.header : ["Phase ID", "Phase title", "Task title", "Lead Advisor", "Advisor", "Associate Advisor", "Technical Advisor"];
          const rRows = resourceTable && resourceTable.rows && resourceTable.rows.length ? resourceTable.rows : [];
          const phaseTitleColIdx = rHeader.findIndex((h) => (h || "").toLowerCase().includes("phase title"));
          const phaseIdColIdx = rHeader.findIndex((h) => (h || "").toLowerCase().includes("phase id"));
          const taskColIdx = rHeader.findIndex((h) => (h || "").toLowerCase().includes("task"));
          const effortCols = rHeader.map((h, ci) => ({ h, ci })).filter(({ h }) => isResourceEffortRoleColumn(h));
          const phaseGroups = [];
          const phaseMap = {};
          rRows.forEach((row, idx) => {
            const phaseTitle = phaseTitleColIdx >= 0 ? (row[phaseTitleColIdx] || "").toString().trim() : "";
            const phaseId = phaseIdColIdx >= 0 ? (row[phaseIdColIdx] || "").toString().trim() : "";
            const key = phaseTitle || phaseId || "Unspecified";
            if (!phaseMap[key]) {
              phaseMap[key] = { phase: phaseTitle || "Unspecified", phaseId: phaseId || "?", rows: [] };
              phaseGroups.push(phaseMap[key]);
            }
            phaseMap[key].rows.push({ globalIdx: idx, row });
          });
          const totalEffortParallel = totalEffortParallelAdjusted(rRows, effortCols);
          const totalRoleDaysRaw = totalEffortRawRoleDays(rRows, effortCols);
          const hasAnyEffort = rRows.some((row) => effortCols.some(({ ci }) => String(row[ci] ?? "").trim() !== ""));
          return /* @__PURE__ */ jsxs("div", {
            className: "space-y-6", children: [
          /* @__PURE__ */ jsx(AiGenerationNotice, { title: "Effort draft", messages: resourcesNotice, testId: "resources-generation-notice" }),
          /* @__PURE__ */ jsxs("div", {
              className: "grid grid-cols-1 sm:grid-cols-3 gap-4", children: [
            /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl p-6 border shadow-sm", children: [
              /* @__PURE__ */ jsx("div", { className: "text-[10px] font-black uppercase text-[color:var(--color-text-muted)] tracking-widest", children: "Total Phases" }),
              /* @__PURE__ */ jsx("div", { className: "text-3xl font-extrabold mt-2", children: phaseGroups.length })
                ]
              }),
            /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl p-6 border shadow-sm", children: [
              /* @__PURE__ */ jsx("div", { className: "text-[10px] font-black uppercase text-[color:var(--color-text-muted)] tracking-widest", children: "Total Tasks" }),
              /* @__PURE__ */ jsx("div", { className: "text-3xl font-extrabold mt-2", children: rRows.length })
                ]
              }),
            /* @__PURE__ */ jsxs("div", {
                className: "bg-[color:var(--color-bg-white)] rounded-2xl p-6 border shadow-sm", children: [
              /* @__PURE__ */ jsx("div", { className: "text-[10px] font-black uppercase text-[color:var(--color-text-muted)] tracking-widest", children: "Total effort (number of days)" }),
              /* @__PURE__ */ jsx("div", { className: "text-3xl font-extrabold mt-2", "data-testid": "total-effort", children: hasAnyEffort ? totalEffortParallel : "-" }),
              /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-2 leading-snug", children: [
                "Parallel-safe: ",
                /* @__PURE__ */ jsx("span", { className: "font-semibold text-[color:var(--color-text-muted)]", children: "sum of max per task row" }),
                " (roles on the same task are not double-counted). Raw role-days (sum of all cells): ",
                /* @__PURE__ */ jsx("span", { className: "font-semibold text-[color:var(--color-text-hero)]", children: hasAnyEffort ? totalRoleDaysRaw : "-" }),
                "."
              ] })
                ]
              })
              ]
            }),
          /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
            /* @__PURE__ */ jsxs("div", {
                className: "flex items-center justify-between px-8 py-5 border-b border-[color:var(--color-border)]", children: [
              /* @__PURE__ */ jsxs("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: [
                "Effort Registry ",
                /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-muted)] font-semibold normal-case tracking-normal", children: "(number of days)" })
              ] }),
              /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center gap-2 flex-wrap justify-end", children: [
                    !resourcesLocked && /* @__PURE__ */ jsx("button", { type: "button", onClick: generateResourcesWithAI, disabled: isGeneratingResources || aiDisabled.resources || scopingWizard.resources, className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${isGeneratingResources ? "bg-[color:var(--color-accent-fill)] text-white cursor-wait" : aiDisabled.resources || scopingWizard.resources ? "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-gradient-to-r from-[color:var(--color-accent-fill)] to-[color:var(--color-accent-fill)] text-white hover:from-[color:var(--color-accent-fill)] hover:to-[color:var(--color-accent-fill)]"}`, children: isGeneratingResources ? "Generating\u2026" : "\u2728 Auto-assign with AI" }),
                    !resourcesLocked && /* @__PURE__ */ jsx("button", { onClick: addResourceRow, className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-accent)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm", children: "+ Add Row" }),
                    !resourcesLocked && /* @__PURE__ */ jsx("button", { onClick: () => persistScopingMilestoneResource("resources"), disabled: savedFlash === "resources", className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${savedFlash === "resources" ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] cursor-default" : "bg-[color:var(--color-accent-fill)] text-white hover:brightness-110"}`, children: savedFlash === "resources" ? "\u2713 Saved" : "Save" })
                  ]
                })
                ]
              }),
            /* @__PURE__ */ jsx("div", {
                className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
                  className: "w-full text-left", children: [
              /* @__PURE__ */ jsx("thead", {
                    children: /* @__PURE__ */ jsxs("tr", {
                      className: "bg-[color:var(--color-surface-sunken)] text-[10px] font-black uppercase tracking-[0.15em] text-[color:var(--color-accent)]", children: [
                /* @__PURE__ */ jsx("th", { className: "px-6 py-4 min-w-[240px]", children: "Task" }),
                        effortCols.map(({ h, ci }) => /* @__PURE__ */ jsx("th", { className: "px-4 py-4 text-center min-w-[110px]", children: /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-col items-center gap-0.5", children: [
                          /* @__PURE__ */ jsx("span", { children: h }),
                          /* @__PURE__ */ jsx("span", { className: "text-[9px] font-semibold normal-case tracking-normal text-blue-300/90", children: "(days)" })
                        ] }) }, ci)),
                        !resourcesLocked && /* @__PURE__ */ jsx("th", { className: "px-6 py-4 text-right", children: "Actions" })
                      ]
                    })
                  }),
              /* @__PURE__ */ jsx("tbody", {
                    className: "divide-y divide-[color:var(--color-border)]", children: phaseGroups.map((pg, pgIdx) => /* @__PURE__ */ jsxs(React.Fragment, {
                      children: [
                /* @__PURE__ */ jsx("tr", {
                        className: "bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_50%,transparent)]", children: /* @__PURE__ */ jsx("td", {
                          colSpan: effortCols.length + 1 + (resourcesLocked ? 0 : 1), className: "px-6 py-3", children: /* @__PURE__ */ jsxs("div", {
                            className: "flex items-center justify-between", children: [
                  /* @__PURE__ */ jsxs("div", {
                                className: "flex items-center gap-3", children: [
                  /* @__PURE__ */ jsx("span", { className: "w-7 h-7 rounded-lg bg-[color:var(--color-accent-fill)] text-white flex items-center justify-center text-[10px] font-black", children: pg.phaseId }),
                  /* @__PURE__ */ jsx("span", { className: "text-[11px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: pg.phase }),
                  /* @__PURE__ */ jsxs("span", {
                              className: "text-[10px] text-[color:var(--color-text-muted)] font-semibold ml-2", children: [
                                pg.rows.length,
                                " tasks"
                              ]
                            })
                                ]
                              }),
                              !resourcesLocked && /* @__PURE__ */ jsx(
                                "button",
                                {
                                  onClick: () => addResourceRowInPhase(pg.phase),
                                  className: "px-3 py-1 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-accent)] rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm",
                                  children: "+ Add Task"
                                }
                              )
                            ]
                          })
                        })
                      }),
                        pg.rows.map(({ globalIdx, row }) => {
                          const taskVal = taskColIdx >= 0 ? (row[taskColIdx] || "").toString().trim() : "";
                          return /* @__PURE__ */ jsxs("tr", {
                            className: "group hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)] transition-all", children: [
                    /* @__PURE__ */ jsx("td", {
                              className: "px-8 py-3", children: /* @__PURE__ */ jsx(
                                "input",
                                {
                                  value: taskVal,
                                  readOnly: resourcesLocked,
                                  onChange: (e) => updateResourceCell(globalIdx, taskColIdx, e.target.value),
                                  className: `w-full bg-transparent text-sm font-semibold text-[color:var(--color-text-hero)] outline-none border-b border-transparent transition-all py-1 ${resourcesLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}`
                                }
                              )
                            }),
                              effortCols.map(({ h, ci }) => {
                                const val = (row[ci] || "").toString().trim();
                                // Unset effort stays visibly unset (blank), not a fabricated "0" day count.
                                const displayVal = val;
                                return /* @__PURE__ */ jsx("td", {
                                  className: "px-4 py-3 text-center", children: /* @__PURE__ */ jsx(
                                    "input",
                                    {
                                      type: "number",
                                      placeholder: "-",
                                      value: displayVal,
                                      readOnly: resourcesLocked,
                                      onChange: (e) => updateResourceCell(globalIdx, ci, e.target.value),
                                      className: `w-full max-w-[80px] mx-auto bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none transition-all text-center ${resourcesLocked ? "cursor-default" : "focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] focus:bg-[color:var(--color-bg-white)]"}`
                                    }
                                  )
                                }, ci);
                              }),
                              !resourcesLocked && /* @__PURE__ */ jsx("td", {
                                className: "px-6 py-3 text-right", children: /* @__PURE__ */ jsx(
                                  "button",
                                  {
                                    onClick: () => deleteResourceRow(globalIdx),
                                    className: "p-2 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-colors opacity-0 group-hover:opacity-100",
                                    children: /* @__PURE__ */ jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" }) })
                                  }
                                )
                              })
                            ]
                          }, globalIdx);
                        })
                      ]
                    }, pg.phase))
                  })
                  ]
                })
              })
              ]
            })
            ]
          });
        })(),
        scopingTab === "resources" && isLeadOrAdmin && !resourcesLocked && /* @__PURE__ */ jsx("div", {
          className: "flex justify-end",
          children: /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => persistScopingWizardStep("resources", "pricing"),
            className: "px-6 py-3 bg-[color:var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg",
            children: "Submit & continue to Pricing",
          }),
        }),
        scopingTab === "pricing" && /* @__PURE__ */ jsxs("div", {
          className: "space-y-6", children: [
            /* @__PURE__ */ jsx(AiGenerationNotice, { title: "Pricing draft", messages: pricingNotice, testId: "pricing-generation-notice" }),
            listUnpricedLines(priceEstimate).length > 0 && /* @__PURE__ */ jsxs("div", { "data-testid": "unpriced-lines", role: "status", className: "rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-xs text-[color:var(--color-warn-fg)]", children: [
              /* @__PURE__ */ jsx("div", { className: "font-black uppercase tracking-widest text-[10px] mb-1", children: "Rate needed" }),
              /* @__PURE__ */ jsx("ul", { className: "list-disc pl-4 space-y-0.5", children: listUnpricedLines(priceEstimate).map((name, i) => /* @__PURE__ */ jsx("li", { children: `${name}: Rate needed` }, `${name}-${i}`)) }),
              /* @__PURE__ */ jsx("p", { className: "mt-1", children: "The total is shown only when every line is priced." }),
            ] }),
            !pricingLocked && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-end gap-3", children: [
              /* @__PURE__ */ jsx("button", { type: "button", onClick: generatePricingWithAI, disabled: isGeneratingPricing || aiDisabled.pricing || scopingWizard.pricing, className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${isGeneratingPricing ? "bg-[color:var(--color-accent-fill)] text-white cursor-wait" : aiDisabled.pricing || scopingWizard.pricing ? "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-gradient-to-r from-[color:var(--color-accent-fill)] to-[color:var(--color-accent-fill)] text-white hover:from-[color:var(--color-accent-fill)] hover:to-[color:var(--color-accent-fill)]"}`, children: isGeneratingPricing ? "Generating\u2026" : "\u2728 AI recommend pricing" }),
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-0.5", children: [
                /* @__PURE__ */ jsx("button", { type: "button", onClick: () => setPricingDisplayCurrency("INR"), className: `px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${pricingDisplayCurrency === "INR" ? "bg-[color:var(--color-bg-white)] text-[color:var(--color-text-hero)] shadow-sm" : "text-[color:var(--color-text-muted)]"}`, children: "\u20B9 INR" }),
                /* @__PURE__ */ jsx("button", { type: "button", onClick: () => setPricingDisplayCurrency("USD"), disabled: !inrPerUsd, title: inrPerUsd ? void 0 : "Exchange rate unavailable", className: `px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${pricingDisplayCurrency === "USD" ? "bg-[color:var(--color-bg-white)] text-[color:var(--color-text-hero)] shadow-sm" : "text-[color:var(--color-text-muted)]"}`, children: "$ USD" }),
              ] }),
              /* @__PURE__ */ jsxs("span", { className: "text-[10px] text-[color:var(--color-text-muted)] font-medium", children: [
                inrPerUsd
                  ? `1 USD \u2248 ${Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(inrPerUsd)} INR`
                  : "USD rate unavailable - amounts shown in INR",
              ] }),
              /* @__PURE__ */ jsx("button", { onClick: () => persistScopingMilestoneResource("pricing"), disabled: savedFlash === "pricing", className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${savedFlash === "pricing" ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] cursor-default" : "bg-[color:var(--color-accent-fill)] text-white hover:brightness-110"}`, children: savedFlash === "pricing" ? "\u2713 Saved" : "Save" }),
            ] }),
        /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "px-8 py-5 border-b border-[color:var(--color-border)] flex items-center justify-between", children: [
            /* @__PURE__ */ jsxs("div", {
                  children: [
              /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Price Estimate" }),
              /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Engagement cost breakdown by expense category. Values are stored in INR; use the toggle to view or edit in USD (live FX)." })
                  ]
                }),
            /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: !pricingLocked && /* @__PURE__ */ jsx("button", { onClick: addPriceCategory, className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-accent)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm", children: "+ Category" }) })
                ]
              }),
          /* @__PURE__ */ jsx("div", {
                className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
                  className: "w-full text-left", children: [
            /* @__PURE__ */ jsx("thead", {
                    children: /* @__PURE__ */ jsxs("tr", {
                      className: "bg-[color:var(--color-surface-sunken)] text-[10px] font-black uppercase tracking-[0.15em] text-[color:var(--color-accent)]", children: [
              /* @__PURE__ */ jsx("th", { className: "px-6 py-4 w-[60px]", children: "Sr." }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[200px]", children: "Expense Head" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[130px]", children: "Unit" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[110px] text-center", children: "Total Units" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[110px] text-center", children: pricingDisplayCurrency === "INR" ? "Unit Price (\u20B9)" : "Unit Price ($)" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[120px] text-right", children: pricingDisplayCurrency === "INR" ? "Total (\u20B9)" : "Total ($)" })
                      ]
                    })
                  }),
            /* @__PURE__ */ jsxs("tbody", {
                    className: "divide-y divide-[color:var(--color-border)]", children: [
                      priceEstimate.length === 0 && /* @__PURE__ */ jsx("tr", { "data-testid": "price-estimate-empty", children: /* @__PURE__ */ jsx("td", { colSpan: 6, className: "px-6 py-8 text-center text-sm text-[color:var(--color-text-muted)]", children: pricingLocked ? "No price estimate has been entered." : "No price estimate yet. Add a category, or use AI recommend pricing and review the draft." }) }),
                      priceEstimate.map((pe) => /* @__PURE__ */ jsxs(React.Fragment, {
                        children: [
                /* @__PURE__ */ jsxs("tr", {
                          className: "bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_70%,transparent)]", children: [
                  /* @__PURE__ */ jsx("td", { className: "px-6 py-3", children: /* @__PURE__ */ jsx("span", { className: "w-6 h-6 rounded-lg bg-[color:var(--color-accent-fill)] text-white flex items-center justify-center text-[10px] font-black inline-flex", children: pe.srNo }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsx("input", { value: pe.expenseHead, readOnly: pricingLocked, onChange: (e) => updatePriceRow(pe.id, "expenseHead", e.target.value), className: `flex-1 bg-transparent text-sm font-black text-[color:var(--color-text-hero)] outline-none min-w-0 ${pricingLocked ? "cursor-default" : ""}` }),
                    !pricingLocked && /* @__PURE__ */ jsx("button", { type: "button", onClick: () => addPriceChildRow(pe.id), className: "shrink-0 px-2 py-1 text-[10px] font-black uppercase text-[color:var(--color-accent)] border border-[color:var(--color-info-line)] rounded-lg hover:bg-[color:var(--color-accent-tint)]", title: "Add line item", children: "+ item" }),
                  ] }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3" }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3" }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3" }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3" })
                          ]
                        }),
                          (pe.children || []).map((child) => /* @__PURE__ */ jsxs("tr", {
                            className: "group hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)] transition-all", children: [
                  /* @__PURE__ */ jsx("td", { className: "px-6 py-3" }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("input", { value: child.expenseHead, readOnly: pricingLocked, onChange: (e) => updatePriceRow(child.id, "expenseHead", e.target.value), className: `w-full bg-transparent text-sm font-medium text-[color:var(--color-text-muted)] italic outline-none border-b border-transparent transition-all py-1 ${pricingLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}` }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("input", { value: child.unitOfMeasurement, readOnly: pricingLocked, onChange: (e) => updatePriceRow(child.id, "unitOfMeasurement", e.target.value), className: `w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm text-[color:var(--color-text-hero)] outline-none transition-all text-center max-w-[110px] ${pricingLocked ? "cursor-default" : "focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] focus:bg-[color:var(--color-bg-white)]"}` }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: /* @__PURE__ */ jsx("input", { value: child.totalUnits, readOnly: pricingLocked, onChange: (e) => updatePriceRow(child.id, "totalUnits", e.target.value), className: `w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none transition-all text-center max-w-[90px] mx-auto ${pricingLocked ? "cursor-default" : "focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] focus:bg-[color:var(--color-bg-white)]"}` }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: /* @__PURE__ */ jsx("input", { value: displayMoneyFromInr(child.unitPrice, pricingDisplayCurrency, inrPerUsd), placeholder: "Rate needed", readOnly: pricingLocked, onChange: (e) => updatePriceRow(child.id, "unitPrice", inputToInrStorage(e.target.value, pricingDisplayCurrency, inrPerUsd)), className: `w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none transition-all text-center max-w-[90px] mx-auto ${pricingLocked ? "cursor-default" : "focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] focus:bg-[color:var(--color-bg-white)]"}` }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-right", title: "Total = Unit Price \xD7 Total Units (computed)", children: /* @__PURE__ */ jsx("input", { value: displayMoneyFromInr(child.totalPrice, pricingDisplayCurrency, inrPerUsd), placeholder: "-", readOnly: true, disabled: true, className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none text-right max-w-[110px] ml-auto cursor-default" }) })
                            ]
                          }, child.id)),
                          pe.isHeader && (!pe.children || pe.children.length === 0) && /* @__PURE__ */ jsxs("tr", {
                            className: "group hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)] transition-all", children: [
                  /* @__PURE__ */ jsx("td", { className: "px-6 py-3" }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3" }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("input", { value: pe.unitOfMeasurement, readOnly: pricingLocked, onChange: (e) => updatePriceRow(pe.id, "unitOfMeasurement", e.target.value), className: `w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm text-[color:var(--color-text-hero)] outline-none text-center max-w-[110px] ${pricingLocked ? "cursor-default" : "focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)]"}` }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: /* @__PURE__ */ jsx("input", { value: pe.totalUnits, readOnly: pricingLocked, onChange: (e) => updatePriceRow(pe.id, "totalUnits", e.target.value), className: `w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none text-center max-w-[90px] mx-auto ${pricingLocked ? "cursor-default" : "focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)]"}` }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: /* @__PURE__ */ jsx("input", { value: displayMoneyFromInr(pe.unitPrice, pricingDisplayCurrency, inrPerUsd), placeholder: "Rate needed", readOnly: pricingLocked, onChange: (e) => updatePriceRow(pe.id, "unitPrice", inputToInrStorage(e.target.value, pricingDisplayCurrency, inrPerUsd)), className: `w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none text-center max-w-[90px] mx-auto ${pricingLocked ? "cursor-default" : "focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)]"}` }) }),
                  /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-right", title: "Total = Unit Price \xD7 Total Units (computed)", children: /* @__PURE__ */ jsx("input", { value: displayMoneyFromInr(pe.totalPrice, pricingDisplayCurrency, inrPerUsd), placeholder: "-", readOnly: true, disabled: true, className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none text-right max-w-[110px] ml-auto cursor-default" }) })
                            ]
                          })
                        ]
                      }, pe.id)),
              /* @__PURE__ */ jsxs("tr", {
                        className: "bg-[color:var(--color-accent-fill)] text-white font-black", children: [
                /* @__PURE__ */ jsx("td", { className: "px-6 py-4" }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-4 text-sm uppercase tracking-widest", children: "Total" }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-4" }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-4" }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-4" }),
                /* @__PURE__ */ jsx("td", {
                          className: "px-4 py-4 text-right text-sm font-black", children: (() => {
                            // A total only when every line is priced: a partial sum is not "the total".
                            const total = computePriceEstimateGrandTotalOrNull(priceEstimate);
                            if (total === null) return /* @__PURE__ */ jsx("span", { "data-testid": "price-estimate-total-pending", title: "Shown once every line has a rate", children: "-" });
                            const sym = pricingDisplayCurrency === "INR" ? "\u20B9" : "$";
                            return sym + " " + displayMoneyFromInr(formatInrForStorage(total), pricingDisplayCurrency, inrPerUsd);
                          })()
                        })
                        ]
                      })
                    ]
                  })
                  ]
                })
              })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "bg-[color:var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "px-8 py-5 border-b border-[color:var(--color-border)] flex items-center justify-between", children: [
            /* @__PURE__ */ jsxs("div", {
                  children: [
              /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]", children: "Payment Tranches" }),
              /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Milestone-linked payment schedule" })
                  ]
                }),
            /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: !pricingLocked && /* @__PURE__ */ jsx("button", { onClick: addTrancheRow, className: "px-4 py-2 bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-accent)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm", children: "+ Tranche" }) })
                ]
              }),
          /* @__PURE__ */ jsx("div", {
                className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
                  className: "w-full text-left", children: [
            /* @__PURE__ */ jsx("thead", {
                    children: /* @__PURE__ */ jsxs("tr", {
                      className: "bg-[color:var(--color-surface-sunken)] text-[10px] font-black uppercase tracking-[0.15em] text-[color:var(--color-accent)]", children: [
              /* @__PURE__ */ jsx("th", { className: "px-6 py-4 w-[60px]", children: "Sr." }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[140px]", children: "Particulars" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[240px]", children: "Description" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[110px] text-center", children: "% of Total" }),
              /* @__PURE__ */ jsx("th", { className: "px-4 py-4 min-w-[140px] text-right", children: pricingDisplayCurrency === "INR" ? "Payable (\u20B9)" : "Payable ($)" })
                      ]
                    })
                  }),
            /* @__PURE__ */ jsxs("tbody", {
                    className: "divide-y divide-[color:var(--color-border)]", children: [
                      paymentTranches.length === 0 && /* @__PURE__ */ jsx("tr", { "data-testid": "payment-tranches-empty", children: /* @__PURE__ */ jsx("td", { colSpan: 5, className: "px-6 py-8 text-center text-sm text-[color:var(--color-text-muted)]", children: pricingLocked ? "No payment tranches have been entered." : "No payment tranches yet. Add a tranche once the price estimate is set." }) }),
                      paymentTranches.map((t, idx) => /* @__PURE__ */ jsxs("tr", {
                        className: "group hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)] transition-all", children: [
                /* @__PURE__ */ jsx("td", { className: "px-6 py-3 text-sm text-[color:var(--color-text-muted)] font-bold", children: idx + 1 }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("input", { value: t.particulars, readOnly: pricingLocked, onChange: (e) => updateTrancheRow(t.id, "particulars", e.target.value), className: `w-full bg-transparent text-sm font-semibold text-[color:var(--color-text-hero)] outline-none border-b border-transparent transition-all py-1 ${pricingLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}` }) }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx("input", { value: t.description, readOnly: pricingLocked, onChange: (e) => updateTrancheRow(t.id, "description", e.target.value), className: `w-full bg-transparent text-sm text-[color:var(--color-text-muted)] outline-none border-b border-transparent transition-all py-1 ${pricingLocked ? "cursor-default" : "focus:border-[color:var(--color-accent)]"}` }) }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: /* @__PURE__ */ jsx("input", { value: t.pctOfTotal, placeholder: "-", readOnly: pricingLocked, onChange: (e) => updateTrancheRow(t.id, "pctOfTotal", e.target.value), className: `w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none transition-all text-center max-w-[80px] mx-auto ${pricingLocked ? "cursor-default" : "focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] focus:bg-[color:var(--color-bg-white)]"}` }) }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-right", title: "Payable = % of Total \xD7 Price Estimate grand total (computed)", children: /* @__PURE__ */ jsx("input", { value: displayMoneyFromInr(t.payableAmount, pricingDisplayCurrency, inrPerUsd), placeholder: "-", readOnly: true, disabled: true, className: "w-full bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 text-sm font-bold text-[color:var(--color-text-hero)] outline-none text-right max-w-[120px] ml-auto cursor-default" }) })
                        ]
                      }, t.id)),
              /* @__PURE__ */ jsxs("tr", {
                        className: "bg-[color:var(--color-accent-fill)] text-white font-black", children: [
                /* @__PURE__ */ jsx("td", { className: "px-6 py-4" }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-4 text-sm uppercase tracking-widest", children: "Total" }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-4" }),
                /* @__PURE__ */ jsx("td", { className: "px-4 py-4 text-center text-sm", "data-testid": "tranche-pct-total", children: (() => {
                  const pcts = paymentTranches.map((t) => (isBlankMoney(t.pctOfTotal) ? null : parseFloat(String(t.pctOfTotal).replace(/[^0-9.]/g, ""))));
                  if (pcts.length === 0 || pcts.some((x) => x === null || !Number.isFinite(x))) return "-";
                  return `${Math.round(pcts.reduce((a, b) => a + b, 0) * 100) / 100}%`;
                })() }),
                /* @__PURE__ */ jsx("td", {
                          className: "px-4 py-4 text-right text-sm font-black", children: (() => {
                            if (paymentTranches.length === 0 || paymentTranches.some((t) => isBlankMoney(t.payableAmount))) return "-";
                            let total = 0;
                            paymentTranches.forEach((t) => {
                              total += Number((t.payableAmount || "").replace(/,/g, "")) || 0;
                            });
                            const sym = pricingDisplayCurrency === "INR" ? "\u20B9" : "$";
                            return sym + " " + displayMoneyFromInr(formatInrForStorage(total), pricingDisplayCurrency, inrPerUsd);
                          })()
                        })
                        ]
                      })
                    ]
                  })
                  ]
                })
              })
              ]
            }),
            isLeadOrAdmin && !pricingLocked && /* @__PURE__ */ jsx("div", {
              className: "flex justify-end pt-2",
              children: /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => persistScopingWizardStep("pricing", "contract"),
                className: "px-6 py-3 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-95 transition-all shadow-lg",
                children: "Submit & complete scoping sections",
              }),
            }),
          ]
        }),
        scopingTab === "contract" && contractAccessDenied && /* @__PURE__ */ jsx("div", {
          className: "flex flex-col items-center justify-center rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm p-16 text-center",
          children: /* @__PURE__ */ jsxs("div", {
            className: "max-w-xl flex flex-col items-center gap-4",
            children: [
              /* @__PURE__ */ jsx("div", {
                className: "w-14 h-14 rounded-full bg-[color:var(--color-warn-bg)] flex items-center justify-center",
                children: /* @__PURE__ */ jsx("svg", {
                  className: "w-7 h-7 text-[color:var(--color-warn-fg)]",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth: "2",
                  children: /* @__PURE__ */ jsx("path", {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    d: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
                  }),
                }),
              }),
              /* @__PURE__ */ jsx("h3", {
                className: "text-lg font-black uppercase tracking-widest text-[color:var(--color-text-hero)]",
                children: "Contract details are restricted",
              }),
              /* @__PURE__ */ jsx("p", {
                className: "text-sm text-[color:var(--color-text-muted)]",
                children: "Commercial terms, pricing, and payment tranches are visible to the Lead Advisor and Company SPOC only. Ask your Lead Advisor if you need to know the commercial scope of this engagement.",
              }),
              /* @__PURE__ */ jsx("p", {
                className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]",
                children: "Access restricted by engagement policy.",
              }),
            ],
          }),
        }),
        scopingTab === "contract" && !contractAccessDenied && /* @__PURE__ */ jsxs("div", {
          className: "flex min-h-0 flex-col overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm",
          style: { maxHeight: "min(92vh, 1280px)" },
          children: [
            /* @__PURE__ */ jsxs("div", {
              className: "shrink-0 px-6 py-5 border-b border-[color:var(--color-border)] bg-[color:var(--color-banner-bg)] text-white flex flex-col gap-4",
              children: [
                /* @__PURE__ */ jsxs("div", {
                  className: "flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4",
                  children: [
                    /* @__PURE__ */ jsxs("div", {
                      children: [
                        /* @__PURE__ */ jsx("h3", { className: "text-lg font-black uppercase tracking-widest", children: "MSA \u2013 NDA Agreement" }),
                        /* @__PURE__ */ jsx("p", { className: "text-[10px] text-blue-400 font-medium mt-1", children: "Master Services - Confidentiality & Non-Disclosure Agreement" }),
                      ],
                    }),
                    /* @__PURE__ */ jsxs("div", {
                      className: "flex flex-wrap items-center gap-2",
                      children: [
                        canEditScoping && scopingStatus !== "Under Review" && isLeadOrAdmin && /* @__PURE__ */ jsx("button", {
                          type: "button",
                          disabled: contractDraftSaving,
                          onClick: () => {
                            populateContractData();
                          },
                          className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white border border-[color:var(--color-border-strong)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed",
                          children: "Refresh from sections",
                        }),
                        canEditScoping && scopingStatus !== "Under Review" && (!isContractEditMode ? /* @__PURE__ */ jsx("button", {
                          type: "button",
                          disabled: contractDraftSaving,
                          onClick: () => setIsContractEditMode(true),
                          className: "px-4 py-2 bg-[color:var(--color-bg-white)] text-[color:var(--color-text-hero)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed",
                          children: "Edit template",
                        }) : /* @__PURE__ */ jsx("button", {
                          type: "button",
                          disabled: contractDraftSaving,
                          onClick: () => setIsContractEditMode(false),
                          className: "px-4 py-2 bg-[color:var(--color-bg-white)] text-[color:var(--color-text-hero)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[color:var(--color-accent-tint)] transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed",
                          children: "Preview",
                        })),
                        canEditScoping && scopingStatus !== "Under Review" && isLeadOrAdmin && /* @__PURE__ */ jsx("button", {
                          type: "button",
                          disabled: contractDraftSaving,
                          onClick: async () => {
                            const ok = await persistScopingContractDraft();
                            if (ok) {
                              setSavedFlash("contractDraft");
                              setTimeout(() => setSavedFlash(null), 2e3);
                            }
                          },
                          className: "px-4 py-2 bg-[color:var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2",
                          children: contractDraftSaving ? [
                            /* @__PURE__ */ jsx("svg", { className: "animate-spin h-3.5 w-3.5", viewBox: "0 0 24 24", fill: "none", children: [
                              /* @__PURE__ */ jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }),
                              /* @__PURE__ */ jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" }),
                            ]}),
                            "Saving\u2026",
                          ] : "Save draft",
                        }),
                      ],
                    }),
                  ],
                }),
                !isAdvisorOnly && /* @__PURE__ */ jsx("div", {
                  className: "flex flex-wrap gap-2 items-center",
                  children: SCOPING_WIZARD_ORDER.map((wk) => {
                    const done = !!scopingWizard[wk];
                    const label = wk === "summary" ? "Summary" : wk === "raci" ? "RACI" : wk === "milestones" ? "Milestones" : wk === "resources" ? "Resources" : "Pricing";
                    return /* @__PURE__ */ jsx(
                      "button",
                      {
                        type: "button",
                        onClick: () => {
                          if (!canOpenScopingTab(wk)) return;
                          setScopingTab(wk);
                          void persistScopingUiTab(wk);
                        },
                        disabled: !canOpenScopingTab(wk),
                        className: `px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${!canOpenScopingTab(wk) ? "opacity-40 cursor-not-allowed border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)]" : done ? "bg-[color:color-mix(in_srgb,var(--color-ok-fg)_20%,transparent)] border-[color:color-mix(in_srgb,var(--color-ok-fg)_50%,transparent)] text-emerald-100" : "bg-[color:var(--color-accent-fill)] border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)] hover:border-[color:var(--color-accent)]"}`,
                        children: label,
                      },
                      wk
                    );
                  }).concat([
                    /* @__PURE__ */ jsx("span", {
                      key: "contract-mini",
                      className: "px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-[color:color-mix(in_srgb,var(--color-accent)_70%,transparent)] bg-[color:color-mix(in_srgb,var(--color-accent-fill)_25%,transparent)] text-sky-100",
                      children: "Contract (this tab)",
                    }),
                  ]),
                }),
                isAdvisorOnly && /* @__PURE__ */ jsx("p", {
                  className: "text-[10px] font-medium text-blue-200/90",
                  children: "You can review the latest MSA\u2013NDA text below. Editing is limited to the Lead Advisor.",
                }),
              ],
            }),
            savedFlash === "contractDraft" && /* @__PURE__ */ jsx("div", { className: "px-6 py-2 bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] text-xs font-semibold border-b border-[color:var(--color-ok-line)]", children: "Contract draft saved." }),
            /* @__PURE__ */ jsxs("div", {
              className: "flex flex-col lg:flex-row flex-1 min-h-0 min-w-0 overflow-hidden",
              children: [
                /* @__PURE__ */ jsx("div", {
                  className: "flex-1 min-w-0 flex flex-col min-h-[52vh] overflow-hidden",
                  children: /* @__PURE__ */ jsx(MsaContractBody, {
                    isContractEditMode: isAdvisorOnly ? false : isContractEditMode,
                    contractSections,
                    setContractSections,
                    raciRows,
                    updateRaciCell,
                    resourceTable,
                    updateResourceCell,
                    project,
                    contractExhibitEditable,
                    annotateAnchors: isLeadOrAdmin && (contractAnnotations.length > 0 || scopingStatus === "Under Review"),
                    appendSections: contractAppendSections,
                    onAppendSectionsChange: showContractAppendEditor ? setContractAppendSections : void 0,
                    appendSectionsEditable: showContractAppendEditor,
                    scrollClassName: "paper-surface overflow-y-auto p-8 sm:p-10 space-y-8 flex-1 min-h-[52vh]",
                  }),
                }),
                (isLeadOrAdmin && (contractAnnotations.length > 0 || scopingStatus === "Under Review")) &&
                  /* @__PURE__ */ jsx("div", {
                    className:
                      "flex min-h-0 flex-1 flex-col self-stretch overflow-hidden border-t border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_80%,transparent)] p-4 lg:flex-none lg:w-[340px] lg:max-w-[340px] lg:shrink-0 lg:self-stretch lg:border-l lg:border-t-0",
                    children: /* @__PURE__ */ jsx(ContractAnnotationsSidebar, {
                      fillAvailableHeight: true,
                      annotations: contractAnnotations,
                      selectedAnchorKey: contractAnnotationFocusKey,
                      onSelectAnchor: setContractAnnotationFocusKey,
                      onReply: handleContractAnnotationReply,
                      onResolve: handleContractAnnotationResolve,
                      canReply: scopingStatus === "Under Review" || scopingStatus === "Draft",
                      canResolve: scopingStatus === "Draft",
                      title: "Contract comments",
                      emptyHint: "No contract comments yet.",
                    }),
                  }),
              ],
            }),
          ],
        }),
        scopingTab === "contract" && !contractAccessDenied && (isLeadOrAdmin || isSPOC) && /* @__PURE__ */ jsx(ContractSigningPanel, {
          key: "contract-signing-panel",
          projectId: project.id,
          canWrite: isLeadOrAdmin,
        }),
      ]
    }),
    scopingReviewModal
  ]
});
  };
  const steps = useMemo(() => {
    const onboardingDone = isOnboardingWorkflowGateSatisfied(project?.profile || {});
    const gw = project?.profile?.gapAnalysisWizard && typeof project.profile.gapAnalysisWizard === "object"
      ? project.profile.gapAnalysisWizard
      : {};
    const gapStatus = typeof gw.gapAnalysisStatus === "string" ? gw.gapAnalysisStatus : "Draft";
    const gapPushed = gw.pushedToDashboard === true;
    const gapAnalysisCompleted = gapPushed || gapStatus === "Finalized";
    const orgWiz = project?.profile?.organizationalSetupWizard;
    const ow = orgWiz && typeof orgWiz === "object" ? orgWiz : {};
    let orgStepDescription = "Structure & ownership";
    let orgStepStatus = "pending";
    if (ow.orgChartBuiltAt) {
      const eff = getOrgChartSpocEffectiveStatus(ow);
      if (eff === "pending") {
        orgStepDescription = "Org chart with company SPOC for review";
        orgStepStatus = "in-progress";
      } else if (eff === "approved") {
        orgStepDescription = "Org chart approved";
        orgStepStatus = "completed";
      } else {
        orgStepDescription = "Org chart drafted";
        orgStepStatus = "completed";
      }
    } else if (ow.delegateListWorkflowStatus === "sent_to_advisor") {
      orgStepDescription = "SPOC shared file - advisor building org chart";
      orgStepStatus = "in-progress";
    } else if (ow.brandingImportedAt) {
      orgStepDescription = "Awaiting SPOC: organizational details (CSV)";
      orgStepStatus = "in-progress";
    } else if (project?.profile) {
      orgStepDescription = "Awaiting advisor: import branding";
      orgStepStatus = "in-progress";
    }
    const crwPkg = project?.profile?.complianceRegisterPackage?.workflow;
    const crSent = crwPkg === "sent_to_spoc" || crwPkg === "spoc_acknowledged";
    const rrPkg = project?.profile?.riskRegisterPackage;
    const rrRows = Array.isArray(rrPkg?.rows) ? rrPkg.rows : [];
    const riskRowGranularStatus =
      rrRows.some((r) => r.lifecycle === "accepted" || r.lifecycle === "manual") ? "completed"
      : rrRows.filter((r) => r.lifecycle !== "rejected").length ? "in-progress"
      : "pending";
    const gate6 = getStepNavigationGate(6, project, user?.role);
    const orgStepDoneForBar = crSent || orgStepStatus === "completed";
    const step5DisplayStatus = orgStepDoneForBar ? "completed" : orgStepStatus;
    const rrWorkflowBar = typeof rrPkg?.workflow === "string" ? rrPkg.workflow : "";
    const treatmentPublishedForBar = Boolean(project?.profile?.riskTreatmentPackage?.publishedAt);
    const soaPkgForBar = project?.profile?.statementOfApplicabilityPackage;
    const soaPhaseCompleteForBar = Boolean(
      soaPkgForBar && typeof soaPkgForBar === "object" && soaPkgForBar.lastAiDetailsAt,
    );
    const registerFinalizedForBar = rrWorkflowBar === "finalized";
    const riskWorkbenchPhaseComplete =
      registerFinalizedForBar && treatmentPublishedForBar && soaPhaseCompleteForBar;
    const step6DisplayStatus = riskWorkbenchPhaseComplete
      ? "completed"
      : gate6.allowed
        ? "in-progress"
        : riskRowGranularStatus;
    const gate7 = getStepNavigationGate(7, project, user?.role);
    const ciFinalized = isControlImplementationFinalized(project?.profile);
    const ciPkgForBar = project?.profile?.controlImplementationPackage;
    const ciHasDocs =
      ciPkgForBar &&
      typeof ciPkgForBar === "object" &&
      ciPkgForBar.docs &&
      Object.keys(ciPkgForBar.docs).length > 0;
    const step7DisplayStatus = ciFinalized
      ? "completed"
      : gate7.allowed
        ? ciHasDocs
          ? "in-progress"
          : "in-progress"
        : "pending";
    const incidentWorkflow = project?.profile?.incidentMonitoringPackage?.workflow || "not_started";
    let riskStepDescription = "Risk identification & treatment";
    if (!crSent) riskStepDescription = "Opens after compliance register is sent to SPOC";
    else if (riskRowGranularStatus === "in-progress") riskStepDescription = "Risk register in progress";
    else if (riskRowGranularStatus === "completed") riskStepDescription = "Accepted risks in register";
    return [
      { id: 1, title: "Profiling", description: "Onboarding data collection", status: profilingStatus === "Finalized" ? "completed" : "in-progress" },
      { id: 2, title: "Scoping", description: "Applicability mapping", status: scopingStatus === "Finalized" ? "completed" : "pending" },
      { id: 3, title: "Project Onboarding", description: "Contract & engagement kickoff", status: onboardingDone ? "completed" : "pending" },
      {
        id: 4,
        title: "Gap Analysis",
        description: "Maturity assessment",
        status: gapAnalysisCompleted ? "completed" : "pending",
      },
      { id: 5, title: "Organizational Setup", description: orgStepDescription, status: step5DisplayStatus },
      { id: 6, title: "Risk Analysis", description: riskStepDescription, status: step6DisplayStatus },
      { id: 7, title: "Control Implementation", description: "Implement policies & procedures", status: step7DisplayStatus },
      { id: 8, title: "Evidencing & Monitoring", description: "Evidence & continuous monitoring", status: incidentWorkflow === "published" ? "completed" : incidentWorkflow !== "not_started" ? "in-progress" : "pending" },
      { id: 9, title: "Internal Audit", description: "Independent assurance", status: "pending" },
    ];
  }, [
    profilingStatus,
    scopingStatus,
    project?.profile?.projectOnboardingWizard?.onboardingReviewStatus,
    project?.profile?.gapAnalysisWizard?.gapAnalysisStatus,
    project?.profile?.gapAnalysisWizard?.pushedToDashboard,
    project?.profile?.organizationalSetupWizard,
    project?.profile?.complianceRegisterPackage?.workflow,
    project?.profile?.riskRegisterPackage,
    project?.profile?.riskTreatmentPackage,
    project?.profile?.statementOfApplicabilityPackage,
    project?.profile?.controlImplementationPackage,
    project?.profile?.incidentMonitoringPackage?.workflow,
    user?.role,
  ]);

  const renderWorkspaceMainPanel = () => {
    if (activeStepId === 1) return renderOnboardingForm();
    if (activeStepId === 2) return renderScoping();
    if (activeStepId === 3) return /* @__PURE__ */ jsx(ProjectOnboarding, { project: projectWithFirm, user });
    if (activeStepId === 4) {
      return /* @__PURE__ */ jsx(GapAnalysis, { project, user, onAfterGapDashboardPush: handleAfterGapDashboardPush });
    }
    if (activeStepId === 5) return /* @__PURE__ */ jsx(OrganizationalSetup, { project: projectWithFirm, user, onAfterComplianceRegisterSentToSpoc: handleComplianceRegisterSentToSpoc });
    if (activeStepId === 6) return /* @__PURE__ */ jsx(RiskAnalysis, { project, user });
    if (activeStepId === 7) return /* @__PURE__ */ jsx(ControlImplementation, { project, user });
    if (activeStepId === 8) return /* @__PURE__ */ jsx(EvidencingMonitoring, { project, user });
    if (activeStepId === 9) return /* @__PURE__ */ jsx(InternalAudit, { project, user });
    const meta = steps.find((s) => s.id === activeStepId);
    const stepLabel = activeStepId < 10 ? `0${activeStepId}` : String(activeStepId);
    return /* @__PURE__ */ jsxs("div", {
      className: "relative z-10 flex min-h-[560px] flex-col items-center justify-start px-6 pt-16 text-center animate-in fade-in slide-in-from-bottom-6",
      children: [
        /* @__PURE__ */ jsx("div", {
          className: "mb-8 flex h-20 w-20 items-center justify-center rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-2xl font-black text-[color:var(--color-text-muted)]",
          children: stepLabel,
        }),
        /* @__PURE__ */ jsx("h2", {
          className: "text-3xl font-black tracking-tight text-[color:var(--color-text-hero)] md:text-4xl",
          children: meta?.title ?? "Phase",
        }),
        meta?.description
          ? /* @__PURE__ */ jsx("p", {
              className: "mt-3 max-w-lg text-sm font-semibold text-[color:var(--color-accent)]",
              children: meta.description,
            })
          : null,
        /* @__PURE__ */ jsx("p", {
          className: "mt-6 max-w-lg text-base font-medium leading-relaxed text-[color:var(--color-text-muted)]",
          children:
            "Detailed tools and workflows for this phase will appear here as we build it out section by section.",
        }),
      ],
    });
  };

  return /* @__PURE__ */ jsxs("div", {
    className: "w-full max-w-screen-2xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-1000 relative", children: [
    /* @__PURE__ */ jsxs("header", {
      className: "relative p-10 md:p-14 rounded-[3rem] bg-[color:var(--color-hero-bg)] text-white overflow-hidden shadow-2xl", children: [
      /* @__PURE__ */ jsx("div", { className: "absolute top-0 right-0 w-[800px] h-[800px] bg-[color:color-mix(in_srgb,var(--color-accent-fill)_10%,transparent)] blur-[200px] rounded-full pointer-events-none" }),
      /* @__PURE__ */ jsxs("div", {
        className: "relative z-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10", children: [
        /* @__PURE__ */ jsxs("div", {
          className: "space-y-8", children: [
          /* @__PURE__ */ jsxs("div", {
            className: "flex items-center gap-6", children: [
            /* @__PURE__ */ jsx("span", { className: "px-5 py-2 bg-white/10 backdrop-blur-3xl border border-white/10 rounded-xl text-[9px] font-black uppercase tracking-[0.5em] text-blue-400", children: "Authorized Workspace" }),
            /* @__PURE__ */ jsx("div", { className: "h-8 w-px bg-white/20" }),
            /* @__PURE__ */ jsxs("div", {
              className: "flex flex-col", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black text-white/70 uppercase tracking-widest", children: "Protocol Target" }),
              /* @__PURE__ */ jsx("span", { className: "text-xl font-black text-white", children: project.certification })
              ]
            })
            ]
          }),
          /* @__PURE__ */ jsxs("div", {
            className: "space-y-2", children: [
            /* @__PURE__ */ jsx("h1", { className: "text-5xl md:text-6xl font-extrabold tracking-tighter leading-none brand", children: project.companyName }),
            /* @__PURE__ */ jsxs("p", {
              className: "text-white/70 text-xl font-medium max-w-2xl leading-snug", children: [
                "Active Plan: ",
              /* @__PURE__ */ jsx("span", { className: "text-white font-black underline decoration-blue-600 decoration-4 underline-offset-8", children: project.name })
              ]
            })
            ]
          })
          ]
        }),
        /* @__PURE__ */ jsxs("div", {
          className: "flex flex-col items-end gap-6 min-w-[320px]", children: [
          /* @__PURE__ */ jsxs("div", {
            className: "w-full flex justify-between items-end px-4", children: [
            /* @__PURE__ */ jsxs("div", {
              children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black text-white/70 uppercase tracking-widest", children: "Integrity Index" }),
              /* @__PURE__ */ jsxs("div", {
                className: "text-5xl font-black mt-2 tracking-tighter", children: [
                  project.progress,
                  "%"
                ]
              })
              ]
            }),
            /* @__PURE__ */ jsxs("div", {
              className: "flex items-center gap-3 mb-2", children: [
              /* @__PURE__ */ jsx("div", { className: "w-3 h-3 rounded-full bg-[color:var(--color-hero-sync)] animate-pulse shadow-lg" }),
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-hero-sync)]", children: "Sync Active" })
              ]
            })
            ]
          }),
          /* @__PURE__ */ jsx("div", { className: "w-full h-4 bg-white/5 rounded-full p-1 border border-white/10 overflow-hidden shadow-inner backdrop-blur-xl", children: /* @__PURE__ */ jsx("div", { className: "h-full bg-gradient-to-r from-[color:var(--color-hero-bar-from)] via-[color:var(--color-hero-bar-via)] to-[color:var(--color-hero-bar-to)] rounded-full transition-all duration-[2000ms] shadow-lg", style: { width: `${project.progress}%` } }) })
          ]
        })
        ]
      })
      ]
    }),
    /* @__PURE__ */ jsxs("div", {
      className: "grid grid-cols-12 gap-10 items-start", children: [
      /* @__PURE__ */ jsx("div", { className: "col-span-12", children: !isDelegate ? /* @__PURE__ */ jsx(HorizontalWorkflowStepper, { steps, activeStepId, onSelectStep: handleSelectStep, stepGateMap }) : null }),
      /* @__PURE__ */ jsx("div", {
        className: "col-span-12", children: /* @__PURE__ */ jsx("div", {
          className: "bg-[color:var(--color-bg-white)] rounded-[3rem] border border-[color:var(--color-border)] shadow-xl px-6 py-10 md:px-10 min-h-[800px] relative overflow-x-hidden overflow-y-visible", children: renderWorkspaceMainPanel()
        })
      })
      ]
    }),
    project?.id && getApiToken ? /* @__PURE__ */ jsx(ActivityLogDrawer, {
      projectId: project.id,
      section: activeStepId === 1 ? "profiling" : activeStepId === 2 ? "scoping" : activeStepId === 3 ? "onboarding" : activeStepId === 4 ? "gap_analysis" : activeStepId === 5 ? "org_setup" : activeStepId === 6 ? "risk_analysis" : activeStepId === 7 ? "control_impl" : activeStepId === 8 ? "evidence" : activeStepId === 9 ? "internal_audit" : undefined,
      getApiToken,
    }) : null
    ]
  });
};
export default ProjectWorkspace;
